import { z } from 'zod';
import axios from 'axios';

// src/schemas/primitives.ts
var Iso8601Schema = z.string().regex(
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/,
  "expected ISO-8601 UTC timestamp with Z suffix"
);
var IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD").refine(
  (value) => {
    const date = /* @__PURE__ */ new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  },
  "expected a valid calendar date"
);
var UuidSchema = z.string().uuid();
var SalesforceIdSchema = z.string().regex(/^[a-zA-Z0-9]{15,18}$/, "expected 15- or 18-char Salesforce ID");
var CentsSchema = z.number().int().nonnegative();
var PageInfoSchema = z.object({
  nextCursor: z.string().nullable()
});
var ALLOWED_IMAGE_CONTENT_TYPES = ["image/png", "image/jpeg"];
var ALLOWED_LOGO_CONTENT_TYPES = [...ALLOWED_IMAGE_CONTENT_TYPES, "image/svg+xml"];
var OrganizationSchema = z.object({
  salesforceId: SalesforceIdSchema,
  name: z.string().min(1),
  createdAt: Iso8601Schema,
  updatedAt: Iso8601Schema
});
var AuctionEventStatusSchema = z.enum([
  "draft",
  "published",
  "open",
  "closed"
]);
var PaymentMethodRequirementSchema = z.enum([
  "required",
  "allow_pay_later"
]);
var AutoCheckoutModeSchema = z.enum(["auto_charge", "offer_default"]);
var OnlineAccessSchema = z.enum(["open", "invite_only"]);
var SponsorInfoSchema = z.object({
  name: z.string().nullable(),
  logoUrl: z.string().url().nullable(),
  url: z.string().url().nullable()
});
var AuctionEventBrandingSchema = z.object({
  logoUrl: z.string().url().nullable(),
  bannerUrl: z.string().url().nullable(),
  brandColor: z.string().nullable()
});
var MethodCapabilitySchema = z.enum(["supported", "unsupported", "unknown"]);
var KNOWN_PROCESSORS = ["payrix", "stripe", "pay_gateway", "paypal_venmo"];
var PROCESSOR_ALIASES = {
  payrix: "payrix",
  payrix_pro: "payrix",
  stripe: "stripe",
  stripe_connect: "stripe",
  stripe_payments: "stripe",
  pay_gateway: "pay_gateway",
  paygateway: "pay_gateway",
  gateway: "pay_gateway",
  neon_pay_gateway: "pay_gateway",
  paypal: "paypal_venmo",
  venmo: "paypal_venmo",
  paypal_venmo: "paypal_venmo",
  braintree: "paypal_venmo"
};
function isKnownProcessor(processor) {
  return typeof processor === "string" && KNOWN_PROCESSORS.includes(processor);
}
function canonicalProcessor(slug) {
  if (typeof slug !== "string") return null;
  const trimmed = slug.trim().toLowerCase();
  if (trimmed.length === 0) return null;
  return PROCESSOR_ALIASES[trimmed] ?? trimmed;
}
var ProcessorCapabilitiesSchema = z.object({
  // Processor name as reported by NeonPay, canonicalised onto KNOWN_PROCESSORS
  // when recognised (e.g. 'payrix', 'stripe'). Deliberately still `string` and
  // not `z.enum(KNOWN_PROCESSORS)` — see KNOWN_PROCESSORS above. Informational —
  // gating decisions read the per-method fields below, not this string.
  processor: z.string().nullable().default(null),
  card: MethodCapabilitySchema.default("unknown"),
  ach: MethodCapabilitySchema.default("unknown"),
  googlePay: MethodCapabilitySchema.default("unknown"),
  applePay: MethodCapabilitySchema.default("unknown"),
  paypal: MethodCapabilitySchema.default("unknown"),
  venmo: MethodCapabilitySchema.default("unknown")
});
var UNKNOWN_PROCESSOR_CAPABILITIES = {
  processor: null,
  card: "unknown",
  ach: "unknown",
  googlePay: "unknown",
  applePay: "unknown",
  paypal: "unknown",
  venmo: "unknown"
};
var paymentMethodAvailabilityShape = {
  googlePayEnabled: z.boolean().default(true),
  applePayEnabled: z.boolean().default(true),
  achEnabled: z.boolean().default(false),
  paypalEnabled: z.boolean().default(false),
  venmoEnabled: z.boolean().default(false),
  processorCapabilities: ProcessorCapabilitiesSchema.default(UNKNOWN_PROCESSOR_CAPABILITIES)
};
var SlotMerchantSchema = z.object({
  merchantId: z.string().min(1),
  publicKey: z.string().min(1),
  sandbox: z.boolean().optional()
});
var PaymentConfigSchema = z.object({
  publicKey: z.string().min(1),
  merchantId: z.string().min(1),
  // Per-merchant sandbox flag — drives the admin-side SDK CDN (sbx vs app).
  sandbox: z.boolean().optional(),
  // Neon Pay operator portal base host (sandbox vs prod) — already the portal
  // root, so a deep link appends only its own path. Admin-web appends
  // /terminals and /charges/:id to reach the right merchant's portal. (GT1-327)
  portalBaseUrl: z.string().optional(),
  // GT1-488: the admin/staff checkout surface previously carried no per-method
  // flags at all, so staff walk-up/settlement drawers could not gate ACH the
  // way bidder-web does. Same semantics + defaults as
  // PublicEvent.paymentProvider.
  ...paymentMethodAvailabilityShape,
  // Staff surfaces capture ACH (walk-up + settlement drawers), so they need the
  // ach-slot merchant for the same reason bidder-web does. No
  // digitalWalletMerchant counterpart on purpose: staffRailAvailable never
  // offers a branded wallet on a staff surface.
  achMerchant: SlotMerchantSchema.nullable().default(null)
});
var MerchantLockSlotSchema = z.enum(["general", "ach", "digital_wallet", "ipp"]);
var AuctionEventMerchantLockOnDetailSchema = z.object({
  slot: MerchantLockSlotSchema,
  orgMerchantAccountPublicId: UuidSchema,
  orgMerchantAccountName: z.string(),
  differsFromCurrent: z.boolean()
});
var DEFAULT_STARTING_BID_PERCENT = 30;
var DEFAULT_BID_INCREMENT_PERCENT = 10;
var LEGACY_STARTING_BID_PERCENT = 10;
function percentOfValueCents(fairMarketValueCents, percent) {
  const dollars = Math.round(fairMarketValueCents * percent / 1e4);
  const step = dollars < 10 ? 1 : dollars < 100 ? 5 : dollars < 500 ? 25 : 50;
  return Math.max(1, Math.round(dollars / step) * step) * 100;
}
var AuctionEventSchema = z.object({
  uuid: UuidSchema,
  slug: z.string().min(1),
  status: AuctionEventStatusSchema,
  isPaused: z.boolean(),
  // Present only on closed rows in the admin auction list.
  expectedRevenueCents: z.number().int().nonnegative().optional(),
  itemsSoldCount: z.number().int().nonnegative().optional(),
  // When the current pause began; null when not paused. closeAt - pausedAt is
  // the time that was left, which resume can restore. Optional for ADR-004
  // additive-change safety during a rolling deploy.
  pausedAt: Iso8601Schema.nullable().optional(),
  name: z.string().min(1),
  descriptionHtml: z.string(),
  auctionOpenAt: Iso8601Schema,
  auctionCloseAt: Iso8601Schema,
  registrationOpenAt: Iso8601Schema.nullable(),
  registrationCloseAt: Iso8601Schema.nullable(),
  registrationCapacity: z.number().int().positive().nullable(),
  timezone: z.string().min(1),
  closingSoonMinutes: z.number().int().nonnegative(),
  defaultBidIncrementCents: z.number().int().positive(),
  /** When set, the increment default is a % of FMV, stamped onto each new
   * item at creation; defaultBidIncrementCents is the fixed-mode value. */
  defaultBidIncrementPercent: z.number().int().min(1).max(100).nullable(),
  /** % of FMV used when a new item doesn't specify a starting bid. */
  defaultStartingBidPercent: z.number().int().min(1).max(100),
  /** When set, the starting-bid default is this fixed amount instead of
   * the percent. */
  defaultStartingBidCents: z.number().int().positive().nullable(),
  proxyBidding: z.boolean(),
  onlineAccess: OnlineAccessSchema,
  paymentMethodRequirement: PaymentMethodRequirementSchema,
  autoCheckoutMode: AutoCheckoutModeSchema,
  donorCoveredFeesEnabled: z.boolean(),
  termsAndConditionsHtml: z.string().nullable(),
  termsAndConditionsEnabled: z.boolean(),
  campaignId: z.string().nullable(),
  campaignName: z.string().nullable(),
  crmAutoSyncEnabled: z.boolean().default(false),
  branding: AuctionEventBrandingSchema,
  sponsor: SponsorInfoSchema.nullable(),
  publishedAt: Iso8601Schema.nullable(),
  archivedAt: Iso8601Schema.nullable(),
  closedWithoutSettlement: z.boolean(),
  paymentConfig: PaymentConfigSchema.nullable().optional(),
  // GT1-411: true whenever an enabled general org_merchant_accounts row
  // exists for the org, regardless of testMode/sandbox/placeholder status.
  // Distinct from `paymentConfig !== null` — a testMode merchant leaves
  // paymentConfig null while merchantConfigured stays true. Admin-web uses
  // this to decide whether to offer card capture at all in checkout drawers.
  merchantConfigured: z.boolean().default(false),
  // GT1-332: only present on the admin event-detail response. One entry
  // per (event, slot) with a lock row. Omitted from list responses.
  merchantLocks: z.array(AuctionEventMerchantLockOnDetailSchema).optional(),
  hasBids: z.boolean(),
  createdAt: Iso8601Schema,
  updatedAt: Iso8601Schema
});
var AuctionItemStatusSchema = z.enum([
  "draft",
  "published",
  "open",
  "closed",
  "sold",
  "unsold",
  "withdrawn"
]);
var PhotoVariantSchema = z.object({
  primary: z.boolean(),
  thumb: z.string().url(),
  medium: z.string().url(),
  original: z.string().url()
});
var RedemptionSchema = z.object({
  retailValueCents: CentsSchema.nullable(),
  restrictions: z.string().nullable(),
  expirationDate: Iso8601Schema.nullable(),
  instructions: z.string().nullable()
});
var AuctionItemSchema = z.object({
  id: z.number().int().positive(),
  auctionEventId: z.number().int().positive(),
  // GT1-266: donor is optional on auction_items; null = "Donor Not Provided".
  donorId: z.number().int().positive().nullable(),
  categoryId: z.number().int().positive(),
  title: z.string().min(1),
  descriptionHtml: z.string(),
  photos: z.array(PhotoVariantSchema),
  startingBidCents: CentsSchema,
  bidIncrementCents: CentsSchema,
  fairMarketValueCents: CentsSchema,
  reservePriceCents: CentsSchema.nullable(),
  currentHighBidCents: CentsSchema,
  currentHighBidderId: z.number().int().positive().nullable(),
  version: z.number().int().nonnegative(),
  status: AuctionItemStatusSchema,
  catalogPosition: z.number().int().positive().nullable().optional(),
  anonymousDonor: z.boolean(),
  redemption: RedemptionSchema.nullable(),
  // Winner-facing pickup/fulfillment instructions (plain text). Optional
  // for back-compat with pre-existing contract fixtures, same rationale
  // as activeBidCount below; the server always supplies it (null when
  // unset).
  pickupInstructions: z.string().nullable().optional(),
  closedAt: Iso8601Schema.nullable(),
  createdAt: Iso8601Schema,
  updatedAt: Iso8601Schema,
  // GT1-428: count of non-voided bids on this item. Drives the
  // pricing-fields lock in the admin edit drawer (>=1 → locked).
  // Optional-for-back-compat so contract consumers that never populated
  // it (e.g. tests written before iter-1-be-001) don't fail parse.
  activeBidCount: z.number().int().nonnegative().optional()
});
var BundleComponentSchema = z.object({
  id: z.number().int().positive(),
  auctionItemId: z.number().int().positive(),
  displayOrder: z.number().int().nonnegative(),
  // GT1-446 Flow B: a withdrawn member is hidden from bidders but stays in
  // the bundle's stored member list — the admin catalog still shows it, marked
  // withdrawn, and it is restorable. Null = visible. Optional so older
  // clients (and non-bundle-lifecycle callers) are unaffected.
  withdrawnAt: Iso8601Schema.nullable().optional(),
  createdAt: Iso8601Schema
});
var BundleSchema = z.object({
  id: z.number().int().positive(),
  auctionEventId: z.number().int().positive(),
  categoryId: z.number().int().positive(),
  title: z.string().min(1),
  descriptionHtml: z.string(),
  photos: z.array(PhotoVariantSchema),
  startingBidCents: CentsSchema,
  bidIncrementCents: CentsSchema,
  fairMarketValueCents: CentsSchema,
  reservePriceCents: CentsSchema.nullable(),
  // Winner-facing pickup/fulfillment instructions for the bundle as a lot
  // (plain text). Owned by the admin, not derived from the members' own
  // instructions — bundling frequently changes how the winner collects.
  // Optional for back-compat, same rationale as isPaused below; the server
  // always supplies it (null when unset).
  pickupInstructions: z.string().nullable().optional(),
  currentHighBidCents: CentsSchema,
  currentHighBidderId: z.number().int().positive().nullable(),
  version: z.number().int().nonnegative(),
  status: AuctionItemStatusSchema,
  catalogPosition: z.number().int().positive().nullable().optional(),
  // GT1-446 Flow B: a live bundle pauses after a withdrawal-triggered bid
  // void and accepts no new bids until an admin explicitly reopens it.
  // Optional for backward compatibility — absence reads as false.
  isPaused: z.boolean().optional(),
  components: z.array(BundleComponentSchema),
  // Count of components with withdrawnAt null — what bidders can actually see.
  // Derived server-side from `components` so the admin UI never has to
  // re-filter the full (withdrawn-inclusive) list itself. Optional for
  // backward compatibility — absence means "derive from components.length".
  visibleComponentCount: z.number().int().nonnegative().optional(),
  closedAt: Iso8601Schema.nullable(),
  createdAt: Iso8601Schema,
  updatedAt: Iso8601Schema
});
var CategoryOriginSchema = z.enum(["predefined", "custom"]);
var CategorySchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
  displayOrder: z.number().int().nonnegative(),
  origin: CategoryOriginSchema,
  createdAt: Iso8601Schema,
  updatedAt: Iso8601Schema,
  // Items + bundles currently referencing this category. Optional: only
  // the admin list endpoint computes it (create/update responses omit it).
  itemCount: z.number().int().nonnegative().optional()
});
var DonorSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  anonymousDefault: z.boolean(),
  notes: z.string().nullable(),
  crmConstituentId: z.string().nullable(),
  createdAt: Iso8601Schema,
  updatedAt: Iso8601Schema
});
var BidderEntryPathSchema = z.enum(["pre_registered", "walk_up"]);
var MethodTypeSchema = z.enum([
  "card",
  "google_pay",
  "apple_pay",
  "ach",
  "paypal",
  "venmo",
  // Card-present via a Neon Pay Terminal device (GT1-327). Only valid on
  // charge paths — terminals cannot tokenize, so a stored payment method
  // never carries this value.
  "terminal"
]);
var StoredMethodTypeSchema = MethodTypeSchema.exclude(["terminal"]);
var STORED_METHOD_TYPE_LABELS = {
  card: "Card",
  ach: "Bank account",
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  paypal: "PayPal",
  venmo: "Venmo"
};
function storedMethodTypeLabel(methodType) {
  return methodType ? STORED_METHOD_TYPE_LABELS[methodType] : STORED_METHOD_TYPE_LABELS.card;
}
var OPAQUE_STORED_METHOD_TYPES = [
  "apple_pay",
  "google_pay",
  "paypal",
  "venmo"
];
function isOpaqueStoredMethodType(methodType) {
  return methodType != null && OPAQUE_STORED_METHOD_TYPES.includes(methodType);
}
var StoredCardSchema = z.object({
  brand: z.string(),
  last4: z.string().nullable(),
  // null for non-card wallets (ACH, PayPal, Venmo)
  expiration: z.string().nullable(),
  // null for non-card wallets
  methodType: StoredMethodTypeSchema
});
var BidderSchema = z.object({
  uuid: UuidSchema,
  auctionEventUuid: UuidSchema,
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().nullable(),
  entryPath: BidderEntryPathSchema,
  activated: z.boolean(),
  emailOptIn: z.boolean(),
  smsOptIn: z.boolean(),
  hasPaymentMethod: z.boolean(),
  storedCard: StoredCardSchema.nullable(),
  tcAcceptedAt: Iso8601Schema.nullable(),
  /**
   * Terms version the bidder accepted. Null when no terms were presented at
   * acceptance, or the row predates versioning (pre-cutover tcAcceptedAt cannot
   * be attributed). Stamped once; never rewritten after acceptance.
   * Default null so older fixtures/clients that omit the field still parse.
   */
  tcTermsVersionId: UuidSchema.nullable().default(null),
  registeredAt: Iso8601Schema,
  createdAt: Iso8601Schema,
  updatedAt: Iso8601Schema
});
var BidTypeSchema = z.enum(["standard", "max"]);
var BidSchema = z.object({
  id: z.number().int().positive(),
  auctionEventId: z.number().int().positive(),
  auctionItemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  bidderId: z.number().int().positive(),
  amountCents: CentsSchema,
  bidType: BidTypeSchema,
  maxAmountCents: CentsSchema.nullable(),
  voided: z.boolean(),
  voidReason: z.string().nullable(),
  voidedByAdminId: z.number().int().positive().nullable(),
  voidedAt: Iso8601Schema.nullable(),
  placedAt: Iso8601Schema,
  createdAt: Iso8601Schema
});
var MagicLinkGrantSchema = z.object({
  id: z.number().int().positive(),
  auctionEventId: z.number().int().positive(),
  bidderId: z.number().int().positive(),
  expiresAt: Iso8601Schema,
  consumedAt: Iso8601Schema.nullable(),
  createdAt: Iso8601Schema
});
var PaymentTransactionTypeSchema = z.enum(["charge", "refund"]);
var PaymentMethodSchema = z.enum([
  "card",
  "apple_pay",
  "google_pay",
  "venmo",
  "paypal",
  "ach",
  "cash",
  "check",
  "wire",
  "terminal"
]);
var PaymentTransactionStatusSchema = z.enum([
  "pending",
  "processing",
  "succeeded",
  "failed"
]);
var PaymentTransactionSchema = z.object({
  id: z.number().int().positive(),
  invoiceId: z.number().int().positive(),
  neonPayTransactionId: z.string().nullable(),
  type: PaymentTransactionTypeSchema,
  amountCents: CentsSchema,
  paymentMethod: PaymentMethodSchema,
  status: PaymentTransactionStatusSchema,
  idempotencyKey: z.string(),
  failureCode: z.string().nullable(),
  failureMessage: z.string().nullable(),
  recordedByAdminId: z.number().int().positive().nullable(),
  processedAt: Iso8601Schema.nullable(),
  createdAt: Iso8601Schema
});

// src/schemas/Invoice.zod.ts
var InvoicePaymentStatusSchema = z.enum([
  "pending",
  "processing",
  "paid",
  "failed",
  "refunded",
  "partially_refunded",
  // ADR-012 §4. Terminal: nothing collected, nothing owed, every lot left.
  // Not open, not collectible. Settled feed label "Void".
  "void"
]);
var InvoiceCheckoutModeSchema = z.enum(["auto_charge", "offer_default"]);
var InvoiceLineItemTypeSchema = z.enum([
  "auction_item",
  "donation",
  "fee_coverage"
]);
var InvoiceLineItemSchema = z.object({
  id: z.number().int().positive(),
  invoiceId: z.number().int().positive(),
  auctionItemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  type: InvoiceLineItemTypeSchema,
  description: z.string(),
  amountCents: CentsSchema,
  fmvAtCloseCents: CentsSchema.nullable(),
  taxDeductibleCents: CentsSchema,
  // Cumulative cents refunded against this line (invoice_line_items.refunded_cents).
  // Drives the per-item "Refunded" indicator on the bidder receipt. Optional for
  // wire back-compat: absent means 0 (never refunded). A line is fully refunded
  // when refundedCents === amountCents.
  refundedCents: CentsSchema.optional(),
  // Primary photo thumb URL of the won auction item, derived from
  // auction_items.photos at read time. Null for donation/fee lines and
  // for items without photos.
  photoUrl: z.string().nullable(),
  declined: z.boolean(),
  declinedAt: Iso8601Schema.nullable(),
  createdAt: Iso8601Schema
});
var BidderPaymentStateSchema = z.enum(["settled", "clearing"]);
var BidderInvoicePaymentContextSchema = z.object({
  paymentState: BidderPaymentStateSchema.optional(),
  pendingRefundCents: CentsSchema.optional(),
  owedBackCents: CentsSchema.optional(),
  oldestRefundFailedAt: Iso8601Schema.nullable().optional(),
  organizationName: z.string().min(1).optional()
});
var InvoiceSchema = z.object({
  ...BidderInvoicePaymentContextSchema.shape,
  uuid: UuidSchema,
  auctionEventId: z.number().int().positive(),
  bidderId: z.number().int().positive(),
  subtotalCents: CentsSchema,
  donationCents: CentsSchema,
  feeCoverageCents: CentsSchema,
  /**
   * Whether the BIDDER chose to cover processing fees. Tri-state, and the null
   * matters: `null` means nobody has answered yet, so a surface may still apply
   * its default-on behaviour; `false` means they explicitly declined and it must
   * NOT be re-enabled for them. Inferring this from `feeCoverageCents > 0` is
   * what let an opt-out be silently reverted on the next page mount.
   *
   * Optional for wire back-compat: absent behaves like null.
   */
  feeCoverageOptedIn: z.boolean().nullable().optional(),
  totalCents: CentsSchema,
  /**
   * Monotonic obligation version (ADR-016). Bumped by every totals recompute
   * (line append/release, donation, fee toggle). Echoed back on bidder charge
   * as `expectedRevision`. Status-only transitions do not bump it.
   *
   * Optional for wire back-compat during rollout: absent behaves like 0.
   */
  revision: z.number().int().nonnegative().optional(),
  /**
   * ADR-012 §7. Human-readable identity unique per auction event ("INV-1041").
   * Assigned at insert, never reused. Optional for wire back-compat during
   * rollout: absent means the client falls back to a shortened uuid.
   */
  invoiceNumber: z.string().min(1).optional(),
  taxDeductibleCents: CentsSchema,
  // Net cents refunded on this invoice — the sum of succeeded refund
  // transactions (matches the admin settlement view). Drives the "Refunded"
  // total row and the net "Total paid" on the bidder receipt. Optional for
  // wire back-compat: absent means 0 (nothing refunded). The amount actually
  // retained by the org is `totalCents - refundedCents`.
  refundedCents: CentsSchema.optional(),
  paymentStatus: InvoicePaymentStatusSchema,
  checkoutMode: InvoiceCheckoutModeSchema,
  // Method + reference of the most recent succeeded charge (GT1-353), or —
  // if none has succeeded yet — the most recent charge attempt of any status
  // (GT1-488), so a `processing` invoice (ACH/terminal, submitted but not yet
  // settled) still reports the rail actually in flight instead of null.
  // `paymentReference` carries the check number for offline check payments.
  paymentMethod: PaymentMethodSchema.nullable(),
  paymentReference: z.string().nullable(),
  /**
   * Neon Pay's `alert_bank_code` from the most recent charge attempt, when that
   * attempt FAILED — `insufficient_funds`, `expired_card`, and so on.
   *
   * Carried on the invoice, not only on the `invoice.failed` frame, because the
   * frame is a live event and a bidder who reloads the retry page or follows the
   * link later has no frame to read. Without it the page falls back to generic
   * "something went wrong" copy and the bidder is never told the one thing that
   * would let them fix it.
   *
   * Null on a charge that succeeded or is still in flight. Optional for wire
   * back-compat: absent behaves like null.
   */
  lastFailureCode: z.string().nullable().optional(),
  generatedAt: Iso8601Schema,
  paidAt: Iso8601Schema.nullable(),
  // URL of the rendered tax receipt PDF. Populated by the receipt-pdf
  // microservice via /internal/webhooks/receipt-generated once the PDF
  // is uploaded to S3. Null until the receipt has been generated.
  receiptPdfUrl: z.string().nullable(),
  lineItems: z.array(InvoiceLineItemSchema),
  createdAt: Iso8601Schema,
  updatedAt: Iso8601Schema
});
var JobClassSchema = z.enum([
  "open_event",
  "close_event",
  "open_registration",
  "close_registration",
  "closing_soon",
  "payment_followup",
  "settled_crm_batch",
  "email_reconcile",
  "sms_reconcile"
]);
var ScheduledJobStatusSchema = z.enum([
  "pending",
  "running",
  "done",
  "failed",
  "skipped"
]);
var ScheduledJobSchema = z.object({
  id: z.number().int().positive(),
  auctionEventId: z.number().int().positive(),
  jobClass: JobClassSchema,
  scheduledAt: Iso8601Schema,
  status: ScheduledJobStatusSchema,
  attemptCount: z.number().int().nonnegative(),
  eventbridgeScheduleArn: z.string().nullable(),
  lastError: z.string().nullable(),
  lastAttemptedAt: Iso8601Schema.nullable(),
  completedAt: Iso8601Schema.nullable(),
  createdAt: Iso8601Schema,
  updatedAt: Iso8601Schema
});
var NotificationTypeSchema = z.enum([
  // GT1-546 — the two magic-link grant notifications. Both are ALWAYS-ON: they
  // are the only way into the product, so they carry no on/off setting and
  // their email ignores email_opt_in (an opt-out must not lock a bidder out of
  // logging in). Their SMS still respects sms_opt_in.
  //
  // sign_in_link:      the requested address already has a bidder row.
  // finish_registering: it does not, so the link starts registration instead.
  "sign_in_link",
  "finish_registering",
  "registration_confirmed",
  "activation_reminder",
  "auction_opens",
  "outbid",
  "max_bid_exceeded",
  "closing_soon",
  "you_won",
  "payment_receipt",
  "payment_failed",
  "payment_follow_up_reminder",
  "next_highest_bidder_offer",
  "item_withdrawn",
  "post_event_thank_you",
  // GT1-442 — admin bid voiding (bid-void-design §5).
  // bid_voided: the voided bidder is told their bid was removed (one per
  //   void action, deduped on recipient across a cascade).
  // now_winning: the new high bidder is told they lead, fired only on a
  //   leader change to a non-null bidder.
  "bid_voided",
  "now_winning",
  // GT1-445 — item reassignment (reassign-item-design-2026-08-05 §Notifications).
  // Email-only, all three.
  // item_reassigned_original_winner: the bidder the item is moving AWAY from is
  //   told they owe nothing (also fires when an item is marked unsold).
  // item_reassigned_original_winner_refunded: same event, but the winner had
  //   already paid, so "no payment is due" would be false — this variant names
  //   the refund amount instead. Which of the two is sent is decided by what
  //   actually executed, never by the caller's preference.
  // item_reassigned_offer_superseded: a bidder holding a pending offer on the
  //   item is told the offer is dead because the item went elsewhere.
  "item_reassigned_original_winner",
  "item_reassigned_original_winner_refunded",
  "item_reassigned_offer_superseded",
  // GT1-446 — bundle composition change (withdraw/restore/dissolve) voided the
  // bundle's bid stack. Unlike bid_voided's silent-drop rule, EVERY affected
  // bidder is notified regardless of price materiality: a withdrawal changes
  // what is being sold, not just its price.
  "bundle_bid_voided",
  // ADR-015 — offer lifecycle terminals. Email-only; nothing is owed and the
  // lot is already back with the organizer (release ran at offer creation).
  // offer_cancelled: admin withdrew an outstanding offer.
  // offer_expired: heartbeat sweep lapsed a pending offer at expires_at.
  "offer_cancelled",
  "offer_expired"
]);
var TemplateEditTierSchema = z.enum(["full", "intro", "locked"]);
var NotificationChannelSchema = z.enum(["email", "sms", "push"]);
var NotificationLogStatusSchema = z.enum([
  "queued",
  "sent",
  "delivered",
  "failed",
  "bounced",
  "opted_out",
  "suppressed_archived"
]);
var LOG_ONLY_NOTIFICATION_TYPES = ["custom_broadcast"];
var NotificationLogTypeSchema = z.enum([
  ...NotificationTypeSchema.options,
  ...LOG_ONLY_NOTIFICATION_TYPES
]);
var NotificationTemplateSchema = z.object({
  id: z.number().int().positive(),
  auctionEventId: z.number().int().positive(),
  type: NotificationTypeSchema,
  emailSubject: z.string(),
  emailBodyHtml: z.string(),
  smsBody: z.string(),
  createdAt: Iso8601Schema,
  updatedAt: Iso8601Schema
});
var NotificationTemplateComposerSchema = z.object({
  id: z.number().int().positive().optional(),
  notificationType: NotificationTypeSchema,
  emailSubject: z.string(),
  // content kept loose (z.unknown) — Tiptap's node schema is recursive and
  // validated at parse time by the editor; over-constraining here would break
  // on every new extension added to the composer.
  tiptapJson: z.object({ type: z.literal("doc"), content: z.array(z.unknown()) }),
  compiledHtml: z.string().nullable(),
  htmlByteSize: z.number().int().nullable(),
  // Indicates which level of the hierarchy this template resolves from.
  tier: z.enum(["global", "org", "event"]),
  // Whether admins are permitted to edit this template. false = view-only.
  // Driven by the org-wide template-editing feature flag.
  overridable: z.boolean(),
  // Per-type editing policy, independent of `overridable`. Optional for one
  // deploy window so an older backend still validates against this schema.
  editTier: TemplateEditTierSchema.optional(),
  status: z.enum(["default", "recommended", "customized", "incomplete"]),
  compilerVersion: z.string(),
  styleVariant: z.string(),
  updatedAt: Iso8601Schema.optional()
});
var OrgAssetSchema = z.object({
  id: z.number().int().positive(),
  fileName: z.string(),
  publicUrl: z.string().url(),
  altText: z.string().nullable(),
  mimeType: z.string(),
  byteSize: z.number().int().positive(),
  assetType: z.enum(["image", "document", "logo", "banner"]),
  createdAt: Iso8601Schema
});
var NotificationLogSchema = z.object({
  id: z.number().int().positive(),
  auctionEventId: z.number().int().positive(),
  bidderId: z.number().int().positive().nullable(),
  type: NotificationLogTypeSchema,
  channel: NotificationChannelSchema,
  status: NotificationLogStatusSchema,
  providerMessageId: z.string().nullable(),
  failureReason: z.string().nullable(),
  mergeContext: z.record(z.string(), z.unknown()),
  broadcastId: z.string().uuid().nullable().optional(),
  resendOfLogId: z.number().int().positive().nullable().optional(),
  emailDeliveryId: z.number().int().positive().nullable().optional(),
  // GT1-564. Sent to a bidder who had opted out, because the type is
  // transactional. Defaulted so rows written before the column still parse.
  consentOverridden: z.boolean().default(false),
  dispatchedAt: Iso8601Schema.nullable(),
  createdAt: Iso8601Schema
});
var NotificationLogEntrySchema = z.object({
  id: z.number().int().positive(),
  type: NotificationLogTypeSchema,
  channel: NotificationChannelSchema,
  status: NotificationLogStatusSchema,
  bidderId: z.number().int().positive().nullable(),
  bidderName: z.string().nullable(),
  bidderEmail: z.string().nullable(),
  bidderPhone: z.string().nullable(),
  providerMessageId: z.string().nullable(),
  failureReason: z.string().nullable(),
  broadcastId: z.string().uuid().nullable(),
  resendOfLogId: z.number().int().positive().nullable(),
  emailDeliveryId: z.number().int().positive().nullable(),
  consentOverridden: z.boolean().default(false),
  dispatchedAt: Iso8601Schema.nullable(),
  createdAt: Iso8601Schema
});
var NotificationBroadcastSummarySchema = z.object({
  broadcastId: z.string().uuid(),
  type: NotificationLogTypeSchema,
  channels: z.array(NotificationChannelSchema),
  sent: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  optedOut: z.number().int().nonnegative(),
  suppressedArchived: z.number().int().nonnegative().default(0),
  total: z.number().int().nonnegative(),
  createdAt: Iso8601Schema
});
var NotificationChannelSettingSchema = z.object({
  notificationType: NotificationTypeSchema,
  emailEnabled: z.boolean(),
  smsEnabled: z.boolean(),
  supportedChannels: z.array(z.enum(["email", "sms"]))
});
var NotificationSettingsWireSchema = z.object({
  closingSoonMinutes: z.number().int().min(5).max(60),
  paymentFollowUpDays: z.number().int().min(1).max(7),
  postEventThankYouHours: z.number().int().min(2).max(24),
  channelSettings: z.array(NotificationChannelSettingSchema)
});
var NotificationChannelSettingUpdateSchema = z.object({
  notificationType: NotificationTypeSchema,
  emailEnabled: z.boolean(),
  smsEnabled: z.boolean()
});
var NotificationSettingsUpdateInputSchema = z.object({
  closingSoonMinutes: z.number().int().min(5).max(60).optional(),
  paymentFollowUpDays: z.number().int().min(1).max(7).optional(),
  postEventThankYouHours: z.number().int().min(2).max(24).optional(),
  channelSettings: z.array(NotificationChannelSettingUpdateSchema).optional()
});
var CrmSyncStatusSchema = z.enum([
  "pending",
  "constituent_synced",
  "synced",
  "failed",
  "permanently_failed",
  "invoice_sync_failed"
]);
var CrmSyncDisplaySchema = z.enum([
  ...CrmSyncStatusSchema.options,
  "not_ready",
  "not_applicable"
]);
var CrmSyncRecordTypeSchema = z.enum(["constituent", "paid_invoice"]);
var CrmSyncRecordSchema = z.object({
  id: z.number().int().positive(),
  auctionEventId: z.number().int().positive(),
  bidderId: z.number().int().positive(),
  recordType: CrmSyncRecordTypeSchema,
  // NULL on constituent; NOT NULL on paid_invoice.
  invoiceId: z.number().int().positive().nullable(),
  syncStatus: CrmSyncStatusSchema,
  crmAccountId: z.string().nullable(),
  lastError: z.string().nullable(),
  retryCount: z.number().int().nonnegative(),
  lastAttemptedAt: Iso8601Schema.nullable(),
  syncedAt: Iso8601Schema.nullable(),
  createdAt: Iso8601Schema,
  updatedAt: Iso8601Schema
});
var EmailDeliveryStatusSchema = z.enum([
  "queued",
  "accepted",
  "delivered",
  "deferred",
  "bounced",
  "failed"
]);
var EmailDeliveryEventTypeSchema = z.enum([
  "accepted",
  "delivered",
  "deferred",
  "bounced",
  "failed",
  "opened",
  "clicked",
  "complained",
  "unsubscribed"
]);
var EmailDeliveryWireSchema = z.object({
  id: z.number(),
  sourceType: z.string(),
  notificationType: z.string().nullable(),
  provider: z.string(),
  recipientEmail: z.string(),
  subject: z.string(),
  status: EmailDeliveryStatusSchema,
  acceptedAt: Iso8601Schema.nullable(),
  deliveredAt: Iso8601Schema.nullable(),
  bouncedAt: Iso8601Schema.nullable(),
  failedAt: Iso8601Schema.nullable(),
  openedAt: Iso8601Schema.nullable(),
  clickedAt: Iso8601Schema.nullable(),
  createdAt: Iso8601Schema
});
var PublicEventSchema = z.object({
  uuid: UuidSchema,
  slug: z.string().min(1),
  name: z.string().min(1),
  organizationName: z.string().min(1),
  status: AuctionEventStatusSchema,
  isPaused: z.boolean(),
  descriptionHtml: z.string(),
  branding: AuctionEventBrandingSchema,
  sponsor: SponsorInfoSchema.nullable(),
  openAt: Iso8601Schema,
  closeAt: Iso8601Schema,
  closingSoonMinutes: z.number().int().nonnegative(),
  paymentMethodRequirement: PaymentMethodRequirementSchema,
  donorCoveredFeesEnabled: z.boolean(),
  proxyBidding: z.boolean(),
  registrationOpenAt: Iso8601Schema.nullable(),
  registrationCloseAt: Iso8601Schema.nullable(),
  termsAndConditionsHtml: z.string().nullable(),
  // Non-secret Neon Pay credentials safe to expose client-side, sourced from
  // org_payment_configs. Null when the org hasn't configured a real provider,
  // the merchant is in test mode, or the values still match dev placeholders
  // — bidder-web falls back to its local mock tokenizer in that case.
  paymentProvider: z.object({
    merchantId: z.string().min(1),
    publicKey: z.string().min(1),
    // Per-merchant sandbox flag — drives the bidder-side SDK CDN (sbx vs app).
    sandbox: z.boolean().optional(),
    // Effective availability (platform flag AND org toggle) plus
    // processorCapabilities (GT1-488). Defaults match the org-toggle
    // defaults so older payloads parse identically: google/apple true,
    // ach/paypal/venmo false. `*Enabled: false` means "toggled off";
    // `processorCapabilities.<method> === 'unsupported'` means "this
    // merchant's processor cannot run the rail at all" — the two are
    // deliberately separate so the UI can say which one applies.
    ...paymentMethodAvailabilityShape,
    // GT1-488 follow-up: a rail must tokenize against the same merchant that
    // will charge it (charge.deriveMerchantSlot / resolveMerchantAccount).
    // The wire used to carry only `general`'s merchantId, so bidder-web asked
    // the card merchant whether it could run PayPal — and the SDK correctly
    // said no — while ACH minted tokens on `general` that a charge would then
    // put through the `ach` merchant.
    //
    // Null means the org has no dedicated merchant for that slot, which is
    // precisely when resolveMerchantAccount falls back to general, so
    // consumers fall back to the merchantId/publicKey above. For PayPal/Venmo
    // a null with paypalEnabled/venmoEnabled true means the general
    // merchant's own processor natively runs them (isPpvProcessor).
    digitalWalletMerchant: SlotMerchantSchema.nullable().default(null),
    achMerchant: SlotMerchantSchema.nullable().default(null)
  }).nullable().default(null),
  // GT1-411: true whenever an enabled general org_merchant_accounts row
  // exists for the org, regardless of testMode/sandbox/placeholder status.
  // Distinct from `paymentProvider !== null` — a testMode merchant leaves
  // paymentProvider null (intentionally, to route bidder-web to its local
  // mock tokenizer) while merchantConfigured stays true. Consumers use this
  // to decide whether to offer card entry at all: false means no merchant
  // exists anywhere (real or test) and there is nothing a tokenized card
  // could ever be charged against.
  merchantConfigured: z.boolean().default(false)
});
var LotKindSchema = z.enum(["item", "bundle"]);
var LotRefSchema = z.object({
  kind: LotKindSchema,
  id: z.number().int().positive()
});
function lotKeyOf(ref) {
  return ref.kind === "item" ? `item:${ref.id}` : `bundle:${ref.id}`;
}
function lotRefOfLine(row) {
  if (row.auctionItemId !== null) return { kind: "item", id: row.auctionItemId };
  if (row.bundleId !== null) return { kind: "bundle", id: row.bundleId };
  return null;
}
function namesExactlyOneLot(row) {
  return row.auctionItemId === null !== (row.bundleId === null);
}
var EXACTLY_ONE_LOT_MESSAGE = "exactly one of auctionItemId / bundleId must be set";

// src/wire/CatalogOrder.ts
var CATALOG_ORDER_MAX_LOTS = 5e3;
var CATALOG_KIND_RANK = { bundle: 0, item: 1 };
var CATALOG_POSITION_BUCKET = { positioned: 0, unpositioned: 1 };
var UNPOSITIONED_CATALOG_POSITION = 0;
var CatalogOrderSaveInputSchema = z.object({
  lots: z.array(LotRefSchema).max(CATALOG_ORDER_MAX_LOTS),
  // catalogOrderRevisionOf() over `lots` with the positions the admin started
  // arranging from. A mismatch means another save landed in between.
  baseRevision: z.string().min(1)
}).refine(({ lots }) => new Set(lots.map(lotKeyOf)).size === lots.length, {
  message: "each lot may appear only once",
  path: ["lots"]
});
var CatalogLotPositionSchema = LotRefSchema.extend({
  catalogPosition: z.number().int().positive().nullable()
});
var CatalogOrderSaveResponseSchema = z.object({
  lots: z.array(CatalogLotPositionSchema)
});
function catalogOrderKeyOf(lot) {
  const bucket = lot.catalogPosition === null ? CATALOG_POSITION_BUCKET.unpositioned : CATALOG_POSITION_BUCKET.positioned;
  return [bucket, lot.catalogPosition ?? UNPOSITIONED_CATALOG_POSITION, lot.id, CATALOG_KIND_RANK[lot.kind]];
}
function compareCatalogOrderKeys(left, right) {
  const [leftBucket, leftPosition, leftId, leftKindRank] = left;
  const [rightBucket, rightPosition, rightId, rightKindRank] = right;
  return leftBucket - rightBucket || leftPosition - rightPosition || leftId - rightId || leftKindRank - rightKindRank;
}
function compareCatalogLots(left, right) {
  return compareCatalogOrderKeys(catalogOrderKeyOf(left), catalogOrderKeyOf(right));
}
function hash53(text) {
  let h1 = 3735928559;
  let h2 = 1103547991;
  for (let index = 0; index < text.length; index++) {
    const code = text.charCodeAt(index);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507) ^ Math.imul(h2 ^ h2 >>> 13, 3266489909);
  h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507) ^ Math.imul(h1 ^ h1 >>> 13, 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}
function catalogOrderRevisionOf(lots) {
  const entries = lots.map((lot) => `${lotKeyOf(lot)}@${lot.catalogPosition ?? ""}`).sort();
  return hash53(entries.join("|"));
}
var PUBLIC_CATALOG_LISTED_STATUSES = ["published", "open"];
function isListedInPublicCatalog(status) {
  return PUBLIC_CATALOG_LISTED_STATUSES.some((listedStatus) => listedStatus === status);
}
function lotRefOfSummary(summary) {
  if (summary.bundleId !== null) return { kind: "bundle", id: summary.bundleId };
  return { kind: "item", id: summary.itemId };
}
var PublicItemSummarySchema = z.object({
  // Exactly one of itemId/bundleId is set — items and bundles are separate
  // PK spaces that can collide numerically, so a single ambiguous `id`
  // risks matching the wrong entity (e.g. in WS frame handlers that look
  // up a row by id). Mirrors the itemId/bundleId nullable pattern used by
  // the WS frames in wire/ws/frames.ts.
  itemId: z.number().int().positive().nullable(),
  title: z.string().min(1),
  descriptionHtml: z.string().nullable(),
  thumb: z.string().url().nullable(),
  primaryPhoto: PhotoVariantSchema.nullable(),
  photos: z.array(PhotoVariantSchema).optional(),
  status: AuctionItemStatusSchema,
  startingBidCents: CentsSchema,
  bidIncrementCents: CentsSchema,
  fairMarketValueCents: CentsSchema,
  currentHighBidCents: CentsSchema,
  bidCount: z.number().int().nonnegative(),
  categoryId: z.number().int().positive(),
  categoryName: z.string().min(1),
  donorAttribution: z.string().nullable(),
  closedAt: Iso8601Schema.nullable(),
  // Bundles are their own DB entity (`bundles` table, joined to ordinary
  // AuctionItem rows via `bundle_components`) — NOT an AuctionItem flagged
  // isBundle. On a bundle row, `isBundle: true`, `itemId: null`, and
  // `bundleId` is the bundle's own PK (used by clients to disambiguate
  // routing to `/bundles/:id` vs `/items/:id`).
  // isBundle optional + backward compatible — older clients treat absence
  // as false. `memberCount` rolls up the component-item count for display
  // (e.g., "3 items included") so the bidder side doesn't need to
  // separately fetch member items.
  isBundle: z.boolean().optional(),
  // GT1-446 Flow B (design contract — server does not emit this yet):
  // paused bundles accept no new bids; the client disables the bid CTA
  // and explains why.
  isPaused: z.boolean().optional(),
  // GT1-446: bundle rows carry one photo slot per member (displayOrder,
  // capped at 3; null = that member has no photo) so the catalog card can
  // compose the stacked cover when the bundle has no photo of its own.
  memberPhotoSlots: z.array(PhotoVariantSchema.nullable()).optional(),
  bundleId: z.number().int().positive().nullable(),
  memberCount: z.number().int().nonnegative().optional()
}).refine((v) => v.itemId == null !== (v.bundleId == null), {
  message: "exactly one of itemId or bundleId must be set",
  path: ["itemId"]
});
var PublicItemSummaryPageSchema = z.object({
  items: z.array(PublicItemSummarySchema),
  pageInfo: PageInfoSchema
});
var PublicBidEntrySchema = z.object({
  // Identity of the underlying bid row, matching `bid.placed`'s
  // payload.bidId. GT1-469 decoupled WS fan-out from the HTTP response, so a
  // `bid.placed` frame can now land AFTER a fetch that already contains the
  // same bid; clients dedup on this to avoid rendering it twice. Optional for
  // backward compatibility with clients/responses that predate it — no id
  // means fall back to prepending. Same numeric-bid-id exposure the
  // bid.placed / bid.voided frames already make.
  bidId: z.number().int().positive().optional(),
  amountCents: CentsSchema,
  bidderHandle: z.string().min(1),
  placedAt: Iso8601Schema,
  isMine: z.boolean()
});
var BundleMemberSchema = z.object({
  itemId: z.number().int().positive(),
  title: z.string().min(1),
  primaryPhoto: PhotoVariantSchema.nullable(),
  fairMarketValueCents: CentsSchema.nullable(),
  // GT1-446: "What's included" shows each member's description + donor.
  // Null when empty — the client omits the row rather than render a
  // blank label. donorAttribution honors anonymity like item rows.
  descriptionHtml: z.string().nullable().optional(),
  donorAttribution: z.string().nullable().optional()
});
var PublicItemDetailSchema = z.object({
  id: z.number().int().positive(),
  // Exactly one of itemId/bundleId is set — mirrors PublicItemSummary's
  // itemId/bundleId nullable pattern. Items and bundles are separate PK
  // spaces that can collide numerically, so WS-frame-matching code should
  // compare against these, not `id`, when checking whether an incoming
  // frame targets the currently-open detail.
  itemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  auctionEventUuid: z.string().uuid(),
  title: z.string().min(1),
  descriptionHtml: z.string(),
  photos: z.array(PhotoVariantSchema),
  status: AuctionItemStatusSchema,
  startingBidCents: CentsSchema,
  bidIncrementCents: CentsSchema,
  fairMarketValueCents: CentsSchema,
  currentHighBidCents: CentsSchema,
  bidCount: z.number().int().nonnegative(),
  minimumNextBidCents: CentsSchema,
  categoryId: z.number().int().positive(),
  categoryName: z.string().min(1),
  donorAttribution: z.string().nullable(),
  redemption: RedemptionSchema.nullable(),
  // Winner-facing pickup/fulfillment instructions (plain text). Optional
  // for back-compat; the item path supplies the column value (null when
  // unset) and the bundle path supplies null explicitly (BundlesTable has
  // no equivalent column).
  pickupInstructions: z.string().nullable().optional(),
  closedAt: Iso8601Schema.nullable(),
  // Recent bids (most recent first, capped server-side at 10).
  bidHistory: z.array(PublicBidEntrySchema),
  // People watching this item. Real wishlist persistence is post-MVP,
  // so this is a deterministic mock (stable per item) sized off bidCount.
  watcherCount: z.number().int().nonnegative(),
  // Authenticated bidder's current max ceiling for this item. Null when
  // the bidder has no active max bid. Absent on unauthenticated responses.
  myMaxAmountCents: CentsSchema.nullable().optional(),
  // Bundle fields — optional + backward compatible. When `isBundle` is
  // true, `bundleMembers` enumerates the constituents the bidder takes
  // home with the single bundle bid. `memberCount` mirrors the catalog
  // summary so the page can render a count without measuring the array.
  isBundle: z.boolean().optional(),
  // GT1-446 Flow B (design contract — server does not emit this yet):
  // paused bundles accept no new bids; the client disables the bid CTA
  // and explains why.
  isPaused: z.boolean().optional(),
  memberCount: z.number().int().nonnegative().optional(),
  bundleMembers: z.array(BundleMemberSchema).optional()
}).refine((v) => v.itemId == null !== (v.bundleId == null), {
  message: "exactly one of itemId or bundleId must be set",
  path: ["itemId"]
});
var RegistrationInputSchema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().nullable().optional(),
  emailOptIn: z.boolean().optional(),
  smsOptIn: z.boolean().optional(),
  paymentMethodToken: z.string().optional(),
  /**
   * GT1-488: the rail the token was minted on, as declared by the
   * tokenizing client. Omit it and the server falls back to
   * getTokenDetails-based inference (card, promoted to google_pay/apple_pay)
   * — the pre-GT1-488 behaviour, which can never yield `ach`. Restricted to
   * StoredMethodType: `terminal` is charge-path-only, and paypal/venmo
   * produce no reusable token so they are rejected server-side.
   */
  paymentMethodType: StoredMethodTypeSchema.optional(),
  /**
   * Non-sensitive display fields captured at tokenization time. Card-shaped
   * by origin but meaningful for ACH too: `paymentMethodBrand` carries the
   * card brand or the bank name, `paymentMethodLast4` the card or bank
   * account tail. Wallet rails (google_pay / apple_pay / paypal / venmo)
   * have neither — both stay optional and omitting both is valid.
   */
  paymentMethodBrand: z.string().trim().min(1).max(32).optional(),
  paymentMethodLast4: z.string().regex(/^\d{4}$/).optional(),
  tcAccepted: z.boolean().optional(),
  // Required for magic-link gated registrations. Domain validates presence
  // and token integrity; missing/invalid tokens return INVALID_REGISTRATION_TOKEN.
  registrationToken: z.string().optional()
}).superRefine((data, ctx) => {
  if (data.smsOptIn === true && (data.phone == null || data.phone.trim() === "")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["phone"],
      message: "Phone number is required when SMS alerts are enabled."
    });
  }
});
var RegistrationResultSchema = z.object({
  bidder: BidderSchema,
  jwt: z.string().min(1)
});
var BidderUpdateInputSchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  phone: z.string().nullable().optional(),
  emailOptIn: z.boolean().optional(),
  smsOptIn: z.boolean().optional(),
  paymentMethodToken: z.string().optional(),
  // GT1-488 — same semantics as RegistrationInputSchema.paymentMethodType.
  // PATCH /api/v1/me is the activation-gate tokenization path, so it needs
  // the same declaration or an ACH token stored here would land as 'card'.
  paymentMethodType: StoredMethodTypeSchema.optional(),
  tcAccepted: z.boolean().optional()
}).strict();
var MagicLinkRequestSchema = z.object({
  eventUuid: z.string().uuid(),
  email: z.string().email(),
  redirectTo: z.string().refine((v) => v.startsWith("/") && !v.startsWith("//") && !/[\r\n]/.test(v), {
    message: "redirectTo must be a safe relative path"
  }).optional()
});
var MagicLinkRequestResultSchema = z.object({
  status: z.union([z.literal("sent"), z.literal("email_failed")])
});
var MagicLinkExchangeSchema = z.object({
  token: z.string().min(1)
});
function isSafeRelativePath(v) {
  if (!v.startsWith("/")) return false;
  if (v.startsWith("//") || v.startsWith("/\\")) return false;
  return !/[\r\n]/.test(v);
}
var RedirectToSchema = z.string().refine(isSafeRelativePath, { message: "redirectTo must be a safe relative path" }).optional();
var EventSlugSchema = z.string().min(1);
var MagicLinkExchangeResultSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("authenticated"),
    jwt: z.string().min(1),
    bidder: BidderSchema,
    eventSlug: EventSlugSchema,
    redirectTo: RedirectToSchema
  }),
  z.object({
    kind: z.literal("registration"),
    email: z.string().email(),
    grantToken: z.string().min(1),
    eventSlug: EventSlugSchema,
    redirectTo: RedirectToSchema
  }),
  // A dead (consumed/expired) grant whose caller is already holding a valid
  // session for the bidder it was issued to. No payload: the caller already
  // has a JWT and bidder record from that session, so there is nothing new
  // to hand back — this variant only tells the client to proceed to the
  // link's destination instead of showing an error.
  z.object({
    kind: z.literal("already_authenticated"),
    eventSlug: EventSlugSchema,
    redirectTo: RedirectToSchema
  })
]);
var MAX_BID_AMOUNT_CENTS = 1e7;
var BidPlacementInputSchema = z.object({
  itemId: z.number().int().positive().optional(),
  bundleId: z.number().int().positive().optional(),
  amountCents: CentsSchema,
  type: BidTypeSchema
}).refine(
  (v) => v.itemId === void 0 !== (v.bundleId === void 0),
  "exactly one of itemId or bundleId is required"
);
var BidPlacementResultSchema = z.object({
  bid: BidSchema,
  // The post-commit item or bundle row, so the client can show the new
  // current high bid + version without re-fetching.
  item: AuctionItemSchema.nullable(),
  bundle: BundleSchema.nullable(),
  // Authoritative post-commit bid count for the target (GT1-380) — the
  // same value already broadcast in the bid.placed / item.high_bid_changed
  // WS frames. Lets the client set bidCount from the response instead of
  // incrementing a possibly-already-updated local value.
  bidCount: z.number().int().nonnegative()
});
var InvoiceDeclineInputSchema = z.object({
  lineItemIds: z.array(z.number().int().positive()).min(1)
});
var InvoiceDonationInputSchema = z.object({
  donationCents: CentsSchema
});
var DcfMethodSchema = z.enum(["card", "ach", "paypal", "venmo", "terminal"]);
var DcfCardBrandSchema = z.enum(["visa", "mastercard", "amex", "discover", "unknown"]);
var DCF_CARD_BRAND_ALIASES = /* @__PURE__ */ new Map([
  ["visa", "visa"],
  ["mc", "mastercard"],
  ["mastercard", "mastercard"],
  ["master card", "mastercard"],
  ["amex", "amex"],
  ["american express", "amex"],
  ["americanexpress", "amex"],
  ["disc", "discover"],
  ["discover", "discover"],
  ["unknown", "unknown"]
]);
function toWireCardBrand(raw) {
  if (raw == null) return "unknown";
  const key = raw.trim().toLowerCase().replace(/[\s_-]+/g, " ");
  return DCF_CARD_BRAND_ALIASES.get(key) ?? "unknown";
}
function sameFeeContext(a, b) {
  if (a === null || b === null) return a === b;
  return a.method === b.method && a.cardBrand === b.cardBrand;
}
function dcfMethodForPaymentMethod(paymentMethodType) {
  switch (paymentMethodType) {
    case "ach":
      return "ach";
    case "paypal":
      return "paypal";
    case "venmo":
      return "venmo";
    case "terminal":
      return "terminal";
    default:
      return "card";
  }
}
var FeeCoverageInputSchema = z.object({
  enabled: z.boolean(),
  method: DcfMethodSchema.optional(),
  cardBrand: DcfCardBrandSchema.optional()
});
var FeeCoverageQuoteSchema = z.object({
  method: DcfMethodSchema,
  // null for every non-card method — only the card rail has a brand surcharge.
  cardBrand: DcfCardBrandSchema.nullable(),
  feeCoverageCents: CentsSchema,
  totalWithFeeCents: CentsSchema,
  taxDeductibleCents: CentsSchema,
  // Effective rate with the brand surcharge already folded in — not the base
  // rate plus a surcharge the client would have to add itself.
  rateBps: z.number().int().nonnegative(),
  fixedCents: CentsSchema,
  // True when the cap clipped the gross-up, which is why two rails can quote an
  // identical fee at different rates.
  capped: z.boolean()
});
var FeeCoveragePreviewSchema = z.object({
  invoiceUuid: UuidSchema,
  // The gross-up base every quote was computed on (subtotal + donation).
  // Carried so a client can detect a stale matrix — a donation edit changes the
  // base, and a fee quoted against the old base is simply the wrong number.
  baseCents: CentsSchema,
  capCents: CentsSchema,
  quotes: z.array(FeeCoverageQuoteSchema)
});
var ChargeInputSchema = z.object({
  paymentMethodToken: z.string().min(1),
  idempotencyKey: z.string().min(1).max(128),
  // The actual payment method used (card, google_pay, apple_pay, ach, paypal, venmo).
  // Backend maps this to a merchant account slot internally:
  //   'paypal' | 'venmo' → slot 'digital_wallet'
  //   everything else   → slot 'general'
  // terminal is excluded: this schema covers stored-token charges only;
  // card-present charges use the dedicated TerminalChargeInputSchema endpoint.
  paymentMethodType: StoredMethodTypeSchema,
  // When paying with a stored card, the frontend passes the card's public ID
  // so the backend resolves the correct token (instead of always picking the
  // most-recent default via __stored__).
  paymentMethodId: z.string().uuid().optional(),
  // Brand of the card being charged, for the authoritative fee recompute. A
  // freshly tokenized card has no bidder_payment_methods row to read the brand
  // from, and the browser already learned it from the tokenizer; without it the
  // recompute would price an amex at the card base rate. Absent for stored cards
  // (the backend reads the row) and for the non-card rails.
  cardBrand: DcfCardBrandSchema.optional(),
  // ADR-016 — what the client reviewed. Required on the bidder self-checkout
  // path (enforced in the bidder handler); omitted on admin / bulk / terminal
  // charges, which keep charging the server total as read.
  expectedRevision: z.number().int().nonnegative().optional(),
  expectedTotalCents: CentsSchema.optional()
});
var BidderChargeInputSchema = ChargeInputSchema.extend({
  expectedRevision: z.number().int().nonnegative(),
  expectedTotalCents: CentsSchema
});
var ChargeResultSchema = z.object({
  invoice: InvoiceSchema,
  transaction: PaymentTransactionSchema
});
var RefundInputSchema = z.object({
  amountCents: CentsSchema,
  reason: z.string().min(1).max(500),
  // Optional: the specific invoice line items this refund covers, addressed by
  // public line-item id (uuid) — internal numeric ids never cross the wire. When
  // present, the backend records the refunded amount against each line so the
  // settlement drawer can tag partially/fully refunded lines. Deduped so a
  // repeated id can't inflate the covered total.
  lineItemUuids: z.array(z.string().uuid()).min(1).refine((ids) => new Set(ids).size === ids.length, "lineItemUuids must be unique").optional()
});
var OfflinePaymentInputSchema = z.object({
  amountCents: CentsSchema,
  paymentMethod: PaymentMethodSchema.refine(
    (m) => m === "cash" || m === "check" || m === "wire",
    "paymentMethod must be cash, check, or wire"
  ),
  reference: z.string().max(200).optional(),
  receivedAt: Iso8601Schema.optional()
});
var OfferStatusSchema = z.enum(["pending", "accepted", "declined", "expired", "cancelled"]);
var OfferDetailSchema = z.object({
  uuid: z.string().uuid(),
  // The offered lot. Exactly one of the pair is set, and `lotKind` names which,
  // so a page can pick its noun ("item" / "bundle") without testing ids.
  itemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  lotKind: LotKindSchema,
  bidderId: z.number().int().positive(),
  amountCents: CentsSchema,
  status: OfferStatusSchema,
  // The lot's title: the item's, or the bundle's own.
  itemTitle: z.string(),
  expiresAt: Iso8601Schema,
  createdAt: Iso8601Schema
}).refine(
  (o) => o.lotKind === "item" ? o.itemId !== null && o.bundleId === null : o.bundleId !== null && o.itemId === null,
  { message: "lotKind must match the one id that is set", path: ["lotKind"] }
);

// src/wire/MyBidsResponse.ts
var MyBidsStatusSchema = z.enum(["winning", "losing", "won", "lost", "removed", "released"]);
var BidderItemSummarySchema = z.object({
  ...BidderInvoicePaymentContextSchema.shape,
  itemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  title: z.string().min(1),
  primaryPhoto: PhotoVariantSchema.nullable(),
  status: AuctionItemStatusSchema,
  myMaxAmountCents: CentsSchema.nullable(),
  myLastBidAmountCents: CentsSchema,
  currentHighBidCents: CentsSchema,
  minimumNextBidCents: CentsSchema,
  // Carried alongside minimumNextBidCents so consumers can recompute the
  // next-bid amount after a WS-driven currentHighBidCents update without a
  // refetch — mirrors PublicItemSummary/PublicItemDetail.
  bidIncrementCents: CentsSchema,
  closedAt: Iso8601Schema.nullable(),
  myBidsStatus: MyBidsStatusSchema,
  latestBidByStaff: z.boolean().optional(),
  // Winner-facing pickup/fulfillment instructions for the lot (plain text),
  // read from the item or — for a bundle row — from the bundle's own column.
  // Carried on the summary so My Wins can answer "how do I get this" without
  // a per-row detail fetch; the you-won email is otherwise the only place it
  // ever appears. Optional for back-compat; the server always supplies it
  // (null when unset).
  pickupInstructions: z.string().nullable().optional(),
  // Bundle indicator + rolled-up constituent count. Bundles are their own
  // DB entity (`bundles` table, distinct PK space from AuctionItems, joined
  // to component items via `bundle_components`) — when the row represents
  // a bundle, `itemId` is null and `bundleId` is the bundle's own PK, so a
  // bidder can hold a position on the bundle as a single lot.
  // `memberCount` powers the BidCard "<n> items included" subline so
  // the bidder sees at a glance that this row is a bundle.
  isBundle: z.boolean().optional(),
  memberCount: z.number().int().nonnegative().optional(),
  invoiceUuid: z.string().uuid().nullable(),
  // Payment status of the invoice referenced by invoiceUuid, populated for
  // 'won' rows once invoice generation completes. Lets My-bids render the
  // authoritative paid/pending state from the single /me/bids response
  // without a secondary invoice fetch. Null when no invoice exists yet
  // and for non-won rows.
  invoicePaymentStatus: InvoicePaymentStatusSchema.nullable()
});
var BidderOutstandingOfferSchema = z.object({
  offerUuid: UuidSchema,
  itemTitle: z.string().min(1),
  amountCents: CentsSchema,
  expiresAt: Iso8601Schema,
  status: OfferStatusSchema
});
var BidderPaymentLineSchema = z.object({
  title: z.string().min(1),
  amountCents: CentsSchema
});
var BidderPaymentSchema = z.object({
  /** The invoice this charge settled. Public id — internal ids never ship. */
  invoiceUuid: UuidSchema,
  /** Human-readable receipt identity, matching the emailed receipt. */
  receiptNumber: z.string().min(1),
  amountCents: CentsSchema,
  /** When the charge settled. */
  paidAt: Iso8601Schema,
  /** Rail as a bidder reads it ("Credit Card", "ACH Bank Transfer"). */
  methodLabel: z.string().min(1),
  /** The rail itself, for its icon. */
  method: PaymentMethodSchema.optional(),
  /** Submitted and still clearing; settles or fails later. */
  clearing: z.boolean().optional(),
  /** Last four of the card, when the rail has one. */
  methodLast4: z.string().nullable(),
  /** The lots this payment covered. Empty for a donation-only invoice. */
  lines: z.array(BidderPaymentLineSchema),
  /** The rendered receipt, once one exists. Null while it is still generating. */
  receiptPdfUrl: z.string().nullable()
});
var BidderReleasedRefundSchema = z.object({
  amountCents: CentsSchema,
  state: z.enum(["settled", "pending", "failed"]),
  /** When a failed refund failed; after a grace period the bidder is told to contact the organization. */
  failedAt: Iso8601Schema.optional()
});
var BidderReleasedLotSchema = z.object({
  lot: LotRefSchema,
  title: z.string().min(1),
  primaryPhoto: PhotoVariantSchema.nullable(),
  releasedBy: z.enum(["admin", "bidder"]),
  paidCents: CentsSchema,
  refund: BidderReleasedRefundSchema.optional()
});
var BidderInvoiceTotalsSchema = z.object({
  dueCents: CentsSchema,
  paidCents: CentsSchema,
  clearingCents: CentsSchema,
  refundedCents: CentsSchema,
  refundSettlingCents: CentsSchema,
  /** Refunds that failed and are still owed to the bidder. */
  owedBackCents: CentsSchema
});
var MyBidsResponseSchema = z.object({
  // The authenticated bidder's own public UUID. Used by the frontend to
  // compare against currentHighBidderUuid in WS frames so that
  // item.high_bid_changed can move items between Winning/Losing without
  // relying solely on bid.outbid (which can be missed during the subscribe
  // handshake gap). Public UUID — internal DB ids are never exposed on the wire.
  bidderUuid: z.string().uuid(),
  winning: z.array(BidderItemSummarySchema),
  losing: z.array(BidderItemSummarySchema),
  won: z.array(BidderItemSummarySchema),
  lost: z.array(BidderItemSummarySchema),
  // GT1-442 (bid-void-design §6.2): items where the bidder has ≥1 voided bid
  // and 0 active bids. Server-computed bucket, rows carry myBidsStatus:'removed'.
  removed: z.array(BidderItemSummarySchema),
  released: z.array(BidderReleasedLotSchema).optional(),
  invoiceTotals: BidderInvoiceTotalsSchema.optional(),
  spendingCents: CentsSchema,
  // Outstanding offers addressed to this bidder. Empty array when none;
  // always present so the Wins callout never has to special-case a missing
  // key. Server filters to non-expired pending rows (and lazy-expires any
  // past-deadline pending offer it touches).
  outstandingOffers: z.array(BidderOutstandingOfferSchema),
  // Every settled charge this bidder has made in this auction, newest first.
  // Empty array when none; always present so My Wins never special-cases a
  // missing key. Charge grain, not invoice grain — see BidderPaymentSchema.
  //
  // Optional for back-compat with a deployed server that predates it: a bidder
  // page that shipped ahead of the API renders no payments rather than throwing
  // on parse. The server always supplies it.
  payments: z.array(BidderPaymentSchema).optional()
});
var BIDDER_CLIENT_FLAG_KEYS = [];
var BidderClientFlagsSchema = z.object({});
var BidPlacedBySchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("bidder") }),
  z.object({
    kind: z.literal("admin"),
    adminId: z.number().int().positive(),
    adminName: z.string().min(1)
  })
]);

// src/wire/ws/frames.ts
var AdminChannelSchema = z.string().regex(/^admin:.+$/);
var FrameMeta = {
  channel: z.string().min(1),
  ts: Iso8601Schema
};
var BidPlacedFrameSchema = z.object({
  type: z.literal("bid.placed"),
  ...FrameMeta,
  payload: z.object({
    itemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    bidId: z.number().int().positive(),
    bidderId: z.number().int().positive(),
    bidderUuid: z.string().uuid().optional(),
    bidderDisplay: z.string().min(1),
    // Full (un-anonymized) name of the bidder. Populated by the backend on all channels;
    // bidder-web should ignore it and use bidderDisplay instead.
    fullBidderName: z.string().optional(),
    amountCents: CentsSchema,
    // Final high bid after proxy resolution. May differ from amountCents when a
    // competing max auto-responded. Consumers should prefer this over amountCents
    // for display — item.high_bid_changed carries the same value but arrives after.
    currentHighBidCents: CentsSchema.optional(),
    bidCount: z.number().int().nonnegative(),
    bidType: BidTypeSchema,
    placedAt: Iso8601Schema
  })
});
var AdminBidAttributionFrameSchema = z.object({
  type: z.literal("bid.admin_attribution"),
  ...FrameMeta,
  channel: AdminChannelSchema,
  payload: z.object({
    itemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    bidId: z.number().int().positive(),
    placedBy: BidPlacedBySchema
  })
});
var BidOutbidFrameSchema = z.object({
  type: z.literal("bid.outbid"),
  ...FrameMeta,
  payload: z.object({
    itemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    previousAmountCents: CentsSchema,
    currentHighBidCents: CentsSchema,
    // Public UUID of the current high bidder. Internal DB ids are never exposed on the wire.
    currentHighBidderUuid: z.string().uuid()
  })
});
var BidMaxExceededFrameSchema = z.object({
  type: z.literal("bid.max_exceeded"),
  ...FrameMeta,
  payload: z.object({
    itemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    maxAmountCents: CentsSchema,
    currentHighBidCents: CentsSchema
  })
});
var BidMaxRaisedFrameSchema = z.object({
  type: z.literal("bid.max_raised"),
  ...FrameMeta,
  channel: AdminChannelSchema,
  payload: z.object({
    itemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    bidId: z.number().int().positive(),
    bidderId: z.number().int().positive(),
    bidderUuid: UuidSchema,
    fullBidderName: z.string(),
    // The unchanged price the max row was written at.
    amountCents: CentsSchema,
    maxAmountCents: CentsSchema,
    placedAt: Iso8601Schema,
    placedBy: BidPlacedBySchema.optional()
  })
});
var BidVoidedFrameSchema = z.object({
  type: z.literal("bid.voided"),
  ...FrameMeta,
  payload: z.object({
    bidId: z.number().int().positive(),
    // Every bid id removed by this void action (target + cascade). Always
    // includes `bidId`. Length ≥ 1.
    voidedBidIds: z.array(z.number().int().positive()).min(1),
    itemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    // Public UUID of the bidder whose bid(s) were removed. Internal DB ids are
    // never exposed on the event channel. Nullable: the producer resolves this
    // from the bidders table and emits null if the lookup misses, so the
    // declared shape has to admit it (it previously did not — the producer
    // passed `string | null` into a non-nullable field).
    bidderUuid: z.string().uuid().nullable(),
    // Amount of the targeted (primary) voided bid, for display on consumers.
    amountCents: CentsSchema,
    recalculatedHighBidCents: CentsSchema,
    // Public UUID of the leader AFTER the void, or null if the item has no
    // remaining bids. Lets a bidder client decide from this ONE frame whether
    // it was the removed bidder or the newly promoted leader. Driving that off
    // the trailing item.high_bid_changed instead would misfire, because that
    // frame is also emitted on ordinary bid placement — a bidder would be told
    // "you're now winning" every time they bid.
    recalculatedHighBidderUuid: z.string().uuid().nullable(),
    reason: z.string()
  })
});
var ItemStatusChangedFrameSchema = z.object({
  type: z.literal("item.status_changed"),
  ...FrameMeta,
  payload: z.object({
    // Exactly one of itemId/bundleId is set, mirroring the bid.* frames.
    itemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    status: AuctionItemStatusSchema,
    closedAt: Iso8601Schema.nullable()
  })
});
var ItemHighBidChangedFrameSchema = z.object({
  type: z.literal("item.high_bid_changed"),
  ...FrameMeta,
  payload: z.object({
    // Exactly one of itemId/bundleId is set, mirroring the bid.* frames.
    itemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    currentHighBidCents: CentsSchema,
    // Public UUID of the current high bidder. Internal DB ids are never
    // exposed on the event channel because every connected bidder receives it.
    currentHighBidderUuid: z.string().uuid().nullable(),
    // GT1-442: producers MUST send the real recalculated bidCount + version
    // here (the void path previously emitted placeholder bidCount:0/version:0
    // — inv-be §2). Schema already requires both; this is a producer contract
    // note, not a shape change.
    bidCount: z.number().int().nonnegative(),
    version: z.number().int().nonnegative()
  })
});
var EventOpenedFrameSchema = z.object({
  type: z.literal("event.opened"),
  ...FrameMeta,
  payload: z.object({ eventId: z.number().int().positive(), openedAt: Iso8601Schema })
});
var EventClosingSoonFrameSchema = z.object({
  type: z.literal("event.closing_soon"),
  ...FrameMeta,
  payload: z.object({
    eventId: z.number().int().positive(),
    minutesRemaining: z.number().int().nonnegative()
  })
});
var EventClosedFrameSchema = z.object({
  type: z.literal("event.closed"),
  ...FrameMeta,
  payload: z.object({ eventId: z.number().int().positive(), closedAt: Iso8601Schema })
});
var EventArchivedFrameSchema = z.object({
  type: z.literal("event.archived"),
  ...FrameMeta,
  payload: z.object({ eventId: z.number().int().positive(), archivedAt: Iso8601Schema })
});
var EventPausedFrameSchema = z.object({
  type: z.literal("event.paused"),
  ...FrameMeta,
  payload: z.object({
    eventId: z.number().int().positive(),
    pausedAt: Iso8601Schema,
    reason: z.string().nullable()
  })
});
var EventResumedFrameSchema = z.object({
  type: z.literal("event.resumed"),
  ...FrameMeta,
  payload: z.object({
    eventId: z.number().int().positive(),
    resumedAt: Iso8601Schema,
    // The close time bidding resumes against. Resume can move it, so clients
    // take it from here rather than trusting the value they held before the
    // pause. Optional for ADR-004 additive-change safety.
    auctionCloseAt: Iso8601Schema.optional()
  })
});
var EventExtendedFrameSchema = z.object({
  type: z.literal("event.extended"),
  ...FrameMeta,
  payload: z.object({
    eventId: z.number().int().positive(),
    newCloseAt: Iso8601Schema
  })
});
var BidderRegisteredFrameSchema = z.object({
  type: z.literal("bidder.registered"),
  ...FrameMeta,
  payload: z.object({
    bidderId: z.number().int().positive(),
    bidderUuid: UuidSchema,
    entryPath: BidderEntryPathSchema,
    registeredAt: Iso8601Schema
  })
});
var BidderActivatedFrameSchema = z.object({
  type: z.literal("bidder.activated"),
  ...FrameMeta,
  payload: z.object({
    bidderId: z.number().int().positive(),
    bidderUuid: UuidSchema
  })
});
var InvoiceCreatedFrameSchema = z.object({
  type: z.literal("invoice.created"),
  ...FrameMeta,
  payload: z.object({
    invoiceId: z.number().int().positive(),
    invoiceUuid: z.string().uuid(),
    bidderId: z.number().int().positive(),
    totalCents: CentsSchema
  })
});
var InvoiceProcessingFrameSchema = z.object({
  type: z.literal("invoice.processing"),
  ...FrameMeta,
  payload: z.object({
    invoiceId: z.number().int().positive(),
    invoiceUuid: z.string().uuid()
  })
});
var InvoicePaidFrameSchema = z.object({
  type: z.literal("invoice.paid"),
  ...FrameMeta,
  payload: z.object({
    invoiceId: z.number().int().positive(),
    invoiceUuid: z.string().uuid(),
    paidAt: Iso8601Schema,
    transactionId: z.number().int().positive()
  })
});
var InvoiceFailedFrameSchema = z.object({
  type: z.literal("invoice.failed"),
  ...FrameMeta,
  payload: z.object({
    invoiceId: z.number().int().positive(),
    invoiceUuid: z.string().uuid(),
    failureCode: z.string().min(1),
    failureMessage: z.string()
  })
});
var InvoiceVoidedFrameSchema = z.object({
  type: z.literal("invoice.voided"),
  ...FrameMeta,
  payload: z.object({
    invoiceUuid: z.string().uuid()
  })
});
var InvoiceRefundedFrameSchema = z.object({
  type: z.literal("invoice.refunded"),
  ...FrameMeta,
  payload: z.object({
    invoiceId: z.number().int().positive(),
    invoiceUuid: z.string().uuid(),
    refundCents: CentsSchema,
    refundedTransactionId: z.number().int().positive()
  })
});
var InvoiceReleasedFrameSchema = z.object({
  type: z.literal("invoice.released"),
  ...FrameMeta,
  payload: z.object({
    invoiceUuid: z.string().uuid(),
    itemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    refundedCents: CentsSchema,
    obligationStatus: z.enum(["pending", "submitted", "succeeded", "void", "failed"])
  })
});
var InvoiceRefundFailedFrameSchema = z.object({
  type: z.literal("invoice.refund_failed"),
  ...FrameMeta,
  payload: z.object({
    invoiceUuid: z.string().uuid(),
    itemId: z.number().int().positive().nullable(),
    amountCents: CentsSchema,
    lastError: z.string()
  })
});
var InvoiceReceiptReadyFrameSchema = z.object({
  type: z.literal("invoice.receipt_ready"),
  ...FrameMeta,
  payload: z.object({
    invoiceUuid: z.string().uuid(),
    pdfUrl: z.string()
  })
});
var OfferCreatedFrameSchema = z.object({
  type: z.literal("offer.created"),
  ...FrameMeta,
  payload: z.object({
    offerPublicId: z.string().uuid(),
    // Exactly one of auctionItemId/bundleId is set: the offered lot.
    auctionItemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable()
  }).refine(namesExactlyOneLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] })
});
var OfferAcceptedFrameSchema = z.object({
  type: z.literal("offer.accepted"),
  ...FrameMeta,
  payload: z.object({
    offerPublicId: z.string().uuid(),
    // Exactly one of auctionItemId/bundleId is set: the offered lot.
    auctionItemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    invoiceUuid: z.string().uuid().optional(),
    bidderId: z.number().int().positive().optional()
  }).refine(namesExactlyOneLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] })
});
var OfferDeclinedFrameSchema = z.object({
  type: z.literal("offer.declined"),
  ...FrameMeta,
  payload: z.object({
    offerPublicId: z.string().uuid(),
    // Exactly one of auctionItemId/bundleId is set: the offered lot.
    auctionItemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    bidderId: z.number().int().positive().optional()
  }).refine(namesExactlyOneLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] })
});
var OfferExpiredFrameSchema = z.object({
  type: z.literal("offer.expired"),
  ...FrameMeta,
  payload: z.object({
    offerPublicId: z.string().uuid(),
    // Exactly one of auctionItemId/bundleId is set: the offered lot.
    auctionItemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable()
  }).refine(namesExactlyOneLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] })
});
var OfferCancelledFrameSchema = z.object({
  type: z.literal("offer.cancelled"),
  ...FrameMeta,
  payload: z.object({
    offerPublicId: z.string().uuid(),
    // Exactly one of auctionItemId/bundleId is set: the offered lot.
    auctionItemId: z.number().int().positive().nullable(),
    bundleId: z.number().int().positive().nullable(),
    bidderId: z.number().int().positive().optional()
  }).refine(namesExactlyOneLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] })
});
var namesReassignedLot = (p) => namesExactlyOneLot({ auctionItemId: p.itemId, bundleId: p.bundleId });
var InvoiceReassignedFrameSchema = z.object({
  type: z.literal("invoice.reassigned"),
  ...FrameMeta,
  // Two payload shapes, never mixed: a transfer names the invoice the lot
  // left and who holds it now. A cancelled sale names nobody, because the
  // whole point is that the lot ends with no owner. A bare lot id is invalid:
  // a consumer must always be able to tell which of the two happened.
  //
  // Both shapes name the lot with the itemId/bundleId pair, exactly one set,
  // mirroring the bid.* and item.* frames.
  payload: z.union([
    z.object({
      itemId: z.number().int().positive().nullable(),
      bundleId: z.number().int().positive().nullable(),
      invoiceUuid: z.string().uuid(),
      newInvoiceUuid: z.string().uuid().nullable(),
      targetBidderUuid: z.string().uuid()
    }).refine(namesReassignedLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] }),
    z.object({
      itemId: z.number().int().positive().nullable(),
      bundleId: z.number().int().positive().nullable(),
      saleCanceled: z.literal(true)
    }).refine(namesReassignedLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] })
  ])
});
var InvoiceUpdatedFrameSchema = z.object({
  type: z.literal("invoice.updated"),
  ...FrameMeta,
  payload: z.object({
    invoiceUuid: z.string().uuid(),
    revision: z.number().int().nonnegative(),
    totalCents: CentsSchema
  })
});
var PaymentProcessingFrameSchema = z.object({
  type: z.literal("payment.processing"),
  ...FrameMeta,
  payload: z.object({
    invoiceId: z.number().int().positive(),
    paymentMethod: z.string()
  })
});
var PaymentFailedFrameSchema = z.object({
  type: z.literal("payment.failed"),
  ...FrameMeta,
  payload: z.object({
    invoiceId: z.number().int().positive(),
    failureCode: z.string().min(1),
    failureMessage: z.string()
  })
});
var NotificationSentFrameSchema = z.object({
  type: z.literal("notification.sent"),
  ...FrameMeta,
  payload: z.object({
    notificationLogId: z.number().int().positive(),
    bidderId: z.number().int().positive().nullable(),
    type: z.string(),
    channel: z.enum(["email", "sms", "push"])
  })
});
var NotificationFailedFrameSchema = z.object({
  type: z.literal("notification.failed"),
  ...FrameMeta,
  payload: z.object({
    notificationLogId: z.number().int().positive(),
    bidderId: z.number().int().positive().nullable(),
    type: z.string(),
    channel: z.enum(["email", "sms", "push"]),
    failureReason: z.string()
  })
});
var CrmSyncedFrameSchema = z.object({
  type: z.literal("crm.synced"),
  ...FrameMeta,
  payload: z.object({
    bidderId: z.number().int().positive(),
    invoiceId: z.number().int().positive().nullable(),
    crmAccountId: z.string()
  })
});
var CrmFailedFrameSchema = z.object({
  type: z.literal("crm.failed"),
  ...FrameMeta,
  payload: z.object({
    bidderId: z.number().int().positive(),
    invoiceId: z.number().int().positive().nullable(),
    lastError: z.string()
  })
});
var CrmInvoiceSyncFailedFrameSchema = z.object({
  type: z.literal("crm.invoice_sync_failed"),
  ...FrameMeta,
  payload: z.object({
    bidderId: z.number().int().positive(),
    invoiceId: z.number().int().positive().nullable().optional(),
    lastError: z.string()
  })
});
var CrmConstituentSyncedFrameSchema = z.object({
  type: z.literal("crm.constituent_synced"),
  ...FrameMeta,
  payload: z.object({
    bidderId: z.number().int().positive(),
    crmAccountId: z.string()
  })
});
var CrmRefundSyncDeferredFrameSchema = z.object({
  type: z.literal("crm.refund_sync_deferred"),
  ...FrameMeta,
  payload: z.object({
    bidderId: z.number().int().positive(),
    invoiceId: z.number().int().positive(),
    refundedAmountCents: z.number().int().nonnegative()
  })
});
var ConnectionLostFrameSchema = z.object({
  type: z.literal("connection.lost"),
  ...FrameMeta,
  payload: z.object({}).optional()
});
var PongFrameSchema = z.object({
  type: z.literal("pong"),
  ts: Iso8601Schema
});
var ErrorFrameSchema = z.object({
  type: z.literal("error"),
  ...FrameMeta,
  payload: z.object({
    code: z.string().min(1),
    message: z.string().min(1)
  })
});
var PresenceChangedFrameSchema = z.object({
  type: z.literal("presence.changed"),
  ...FrameMeta,
  payload: z.object({
    eventUuid: z.string().uuid(),
    onlineBidderCount: z.number().int().nonnegative()
  })
});
var FeatureFlagChangedFrameSchema = z.object({
  type: z.literal("feature_flag.changed"),
  ...FrameMeta,
  payload: z.object({
    key: z.string().min(1)
  })
});
var WS_FRAME_TYPES = [
  "bid.placed",
  "bid.admin_attribution",
  "bid.outbid",
  "bid.max_exceeded",
  "bid.voided",
  "bid.max_raised",
  "item.status_changed",
  "item.high_bid_changed",
  "event.opened",
  "event.closing_soon",
  "event.closed",
  "event.archived",
  "event.paused",
  "event.resumed",
  "event.extended",
  "bidder.registered",
  "bidder.activated",
  "invoice.created",
  "invoice.processing",
  "invoice.paid",
  "invoice.failed",
  "invoice.voided",
  "invoice.refunded",
  "invoice.released",
  "invoice.refund_failed",
  "invoice.receipt_ready",
  "invoice.reassigned",
  "invoice.updated",
  "offer.created",
  "offer.accepted",
  "offer.declined",
  "offer.expired",
  "offer.cancelled",
  "payment.processing",
  "payment.failed",
  "notification.sent",
  "notification.failed",
  "crm.synced",
  "crm.failed",
  "crm.invoice_sync_failed",
  "crm.constituent_synced",
  "crm.refund_sync_deferred",
  "presence.changed",
  "feature_flag.changed",
  "connection.lost",
  "pong",
  "error"
];
var WsFrameSchema = z.discriminatedUnion("type", [
  BidPlacedFrameSchema,
  AdminBidAttributionFrameSchema,
  BidOutbidFrameSchema,
  BidMaxExceededFrameSchema,
  BidVoidedFrameSchema,
  BidMaxRaisedFrameSchema,
  ItemStatusChangedFrameSchema,
  ItemHighBidChangedFrameSchema,
  EventOpenedFrameSchema,
  EventClosingSoonFrameSchema,
  EventClosedFrameSchema,
  EventArchivedFrameSchema,
  EventPausedFrameSchema,
  EventResumedFrameSchema,
  EventExtendedFrameSchema,
  BidderRegisteredFrameSchema,
  BidderActivatedFrameSchema,
  InvoiceCreatedFrameSchema,
  InvoiceProcessingFrameSchema,
  InvoicePaidFrameSchema,
  InvoiceFailedFrameSchema,
  InvoiceVoidedFrameSchema,
  InvoiceRefundedFrameSchema,
  InvoiceReleasedFrameSchema,
  InvoiceRefundFailedFrameSchema,
  InvoiceReceiptReadyFrameSchema,
  InvoiceReassignedFrameSchema,
  InvoiceUpdatedFrameSchema,
  OfferCreatedFrameSchema,
  OfferAcceptedFrameSchema,
  OfferDeclinedFrameSchema,
  OfferExpiredFrameSchema,
  OfferCancelledFrameSchema,
  PaymentProcessingFrameSchema,
  PaymentFailedFrameSchema,
  NotificationSentFrameSchema,
  NotificationFailedFrameSchema,
  CrmSyncedFrameSchema,
  CrmFailedFrameSchema,
  CrmInvoiceSyncFailedFrameSchema,
  CrmConstituentSyncedFrameSchema,
  CrmRefundSyncDeferredFrameSchema,
  PresenceChangedFrameSchema,
  FeatureFlagChangedFrameSchema,
  ConnectionLostFrameSchema,
  PongFrameSchema,
  ErrorFrameSchema
]);
var SubscribeOpSchema = z.object({
  op: z.literal("subscribe"),
  channels: z.array(z.string().min(1)).min(1)
});
var UnsubscribeOpSchema = z.object({
  op: z.literal("unsubscribe"),
  channels: z.array(z.string().min(1)).min(1)
});
var PongOpSchema = z.object({
  op: z.literal("pong")
});
var ClientOpSchema = z.discriminatedUnion("op", [
  SubscribeOpSchema,
  UnsubscribeOpSchema,
  PongOpSchema
]);
var CLIENT_OP_TYPES = ["subscribe", "unsubscribe", "pong"];
var channels = {
  event: (uuid) => `event:${uuid}`,
  item: (id) => `item:${id}`,
  bidder: (uuid) => `bidder:${uuid}`,
  admin: (salesforceId) => `admin:${salesforceId}`
};
var AuctionEventFieldsSchema = z.object({
  slug: z.string().min(1).max(120).regex(/^[a-z0-9-]+$/, "slug must be lowercase alnum plus dashes"),
  name: z.string().min(1),
  descriptionHtml: z.string(),
  auctionOpenAt: Iso8601Schema,
  auctionCloseAt: Iso8601Schema,
  registrationOpenAt: Iso8601Schema.nullable().optional(),
  registrationCloseAt: Iso8601Schema.nullable().optional(),
  registrationCapacity: z.number().int().positive().nullable().optional(),
  timezone: z.string().min(1),
  closingSoonMinutes: z.number().int().nonnegative(),
  defaultBidIncrementCents: z.number().int().positive(),
  defaultBidIncrementPercent: z.number().int().min(1).max(100).nullable().optional(),
  defaultStartingBidPercent: z.number().int().min(1).max(100).optional(),
  defaultStartingBidCents: z.number().int().positive().nullable().optional(),
  proxyBidding: z.boolean(),
  onlineAccess: OnlineAccessSchema,
  paymentMethodRequirement: PaymentMethodRequirementSchema,
  autoCheckoutMode: AutoCheckoutModeSchema,
  donorCoveredFeesEnabled: z.boolean(),
  termsAndConditionsHtml: z.string().nullable().optional(),
  // Omitted on create → server defaults ON when the org has a global
  // default T&C, OFF otherwise (GT1-348).
  termsAndConditionsEnabled: z.boolean().optional(),
  campaignId: z.string().nullable().optional(),
  campaignName: z.string().nullable().optional(),
  crmAutoSyncEnabled: z.boolean().optional(),
  logoUrl: z.string().url().nullable().optional(),
  bannerUrl: z.string().url().nullable().optional(),
  brandColor: z.string().nullable().optional(),
  sponsor: SponsorInfoSchema.nullable().optional()
});
var AuctionEventCreateInputSchema = AuctionEventFieldsSchema.extend({
  defaultBidIncrementCents: z.number().int().positive().default(2500),
  // NO defaults on the percent fields — percent mode is opt-in on the wire.
  //
  // defaultBidIncrementCents is a required field here, so every caller sends
  // one. Defaulting the percent would silently override the amount they just
  // asked for: an auction created with a $25 increment would price a $500
  // item at $50. A caller that says nothing about percent means fixed mode,
  // and createAuction gives an omitted starting-bid percent the legacy value.
  //
  // The product default for a new auction (30% start / 10% step) belongs to
  // the admin UI, which sends both fields explicitly — see DEFAULT_DRAFT.
  proxyBidding: z.boolean().default(false),
  // Omitting any of these three means "inherit the org default" — the server
  // reads organizations.default_* and stamps the result onto the new auction
  // (GT1-526). They are `.optional()` rather than `.default()` for that
  // reason: a wire-level default would resolve the value before the server
  // ever sees that the caller said nothing, which is what previously made
  // organizations.default_donor_covered_fees impossible to honour.
  paymentMethodRequirement: PaymentMethodRequirementSchema.optional(),
  autoCheckoutMode: AutoCheckoutModeSchema.optional(),
  donorCoveredFeesEnabled: z.boolean().optional()
}).refine(
  (data) => new Date(data.auctionCloseAt) > new Date(data.auctionOpenAt),
  { message: "Close date must be after open date", path: ["auctionCloseAt"] }
);
var AuctionEventUpdateInputSchema = AuctionEventFieldsSchema.partial().strict().refine(
  (data) => {
    if (data.auctionOpenAt && data.auctionCloseAt) {
      return new Date(data.auctionCloseAt) > new Date(data.auctionOpenAt);
    }
    return true;
  },
  { message: "Close date must be after open date", path: ["auctionCloseAt"] }
);
var ItemFieldsSchema = z.object({
  donorId: z.number().int().positive().nullable().optional(),
  donorName: z.string().min(1).nullable().optional(),
  categoryId: z.number().int().positive().nullable().optional(),
  title: z.string().min(1),
  descriptionHtml: z.string(),
  photos: z.array(PhotoVariantSchema),
  // Absent or null → the server resolves the event's configured starting-bid
  // default (percent of FMV, or the fixed amount) and stamps the result.
  // Null must stay accepted: on a PATCH it is the only way to distinguish
  // "clear this and re-resolve from the default" from "leave it unchanged",
  // which an absent key already means.
  startingBidCents: CentsSchema.nullable().optional(),
  bidIncrementCents: CentsSchema.refine((n) => n > 0, "increment must be > 0").nullable().optional(),
  fairMarketValueCents: CentsSchema,
  reservePriceCents: CentsSchema.nullable().optional(),
  anonymousDonor: z.boolean(),
  redemption: RedemptionSchema.nullable().optional(),
  // Winner-facing pickup/fulfillment instructions: how, where, and when the
  // winner collects or receives the item. Plain text (rendered {{ }} on the
  // bidder site and inside notification emails). Optional + no default per
  // the GT1-428 rule above.
  pickupInstructions: z.string().max(2e3).nullable().optional()
});
var ItemCreateInputSchema = ItemFieldsSchema.extend({
  descriptionHtml: z.string().default(""),
  photos: z.array(PhotoVariantSchema).default([]),
  anonymousDonor: z.boolean().default(false)
});
var ItemUpdateInputSchema = ItemFieldsSchema.partial().strict();
var PICKUP_INSTRUCTIONS_MAX_LENGTH = 2e3;
var IMPORT_MAX_ROWS = 500;
var ItemImportRowSchema = ItemFieldsSchema.pick({
  donorId: true,
  donorName: true,
  categoryId: true,
  title: true,
  startingBidCents: true,
  bidIncrementCents: true,
  fairMarketValueCents: true,
  reservePriceCents: true
}).extend({
  categoryName: z.string().min(1).nullable().optional(),
  descriptionHtml: z.string().optional(),
  anonymousDonor: z.boolean().optional(),
  // Winner-facing pickup note — mirrors ItemFieldsSchema.pickupInstructions,
  // re-declared (rather than picked) so the cap stays keyed to the named
  // constant above instead of a second literal `2000`.
  pickupInstructions: z.string().max(PICKUP_INSTRUCTIONS_MAX_LENGTH).nullable().optional()
}).strict();
var ItemImportInputSchema = z.object({
  rows: z.array(z.unknown()).min(1).max(IMPORT_MAX_ROWS)
});
var BundleComponentInputSchema = z.object({
  auctionItemId: z.number().int().positive(),
  displayOrder: z.number().int().nonnegative()
});
var BundleFieldsSchema = z.object({
  // Optional — null defaults to a per-auction "Uncategorized" category on
  // the server (find-or-create). Matches the design-canonical bundle UX
  // where the create drawer doesn't surface category selection.
  categoryId: z.number().int().positive().nullable().optional(),
  title: z.string().min(1),
  descriptionHtml: z.string().default(""),
  photos: z.array(PhotoVariantSchema).default([]),
  // Absent or null → the server resolves the auction's configured pricing
  // default against the bundle's own FMV and stamps the result. Null must
  // stay accepted on both create and update: it is the only way a caller can
  // ask for the calculated amount rather than assert a typed-in one.
  startingBidCents: CentsSchema.nullable().optional(),
  bidIncrementCents: CentsSchema.refine((n) => n > 0, "increment must be > 0").nullable().optional(),
  fairMarketValueCents: CentsSchema,
  reservePriceCents: CentsSchema.nullable().optional(),
  // Winner-facing pickup/fulfillment instructions for the bundle (plain
  // text). Capped wider than ItemFieldsSchema's 2000: the admin drawer seeds
  // this by concatenating every member's instructions, so the item cap would
  // reject a prefill the product deliberately generates.
  pickupInstructions: z.string().max(8e3).nullable().optional(),
  components: z.array(BundleComponentInputSchema).min(2, "bundle requires >= 2 components")
});
var BundleCreateInputSchema = BundleFieldsSchema;
var BundleUpdateInputSchema = BundleFieldsSchema.partial().strict();
var BundleLifecycleResultSchema = z.object({
  bundle: BundleSchema,
  voidedBidCount: z.number().int().nonnegative(),
  notifiedBidderCount: z.number().int().nonnegative(),
  replayed: z.boolean()
});
var DonorCreateInputSchema = z.object({
  name: z.string().min(1),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
  anonymousDefault: z.boolean().default(false),
  notes: z.string().nullable().optional()
});
var DonorUpdateInputSchema = DonorCreateInputSchema.partial().strict();
var DonorMergeInputSchema = z.object({
  // Donor IDs to merge into the receiver. Server preserves the
  // receiver, reassigns auctionItems.donor_id, soft-deletes the merged
  // rows.
  mergeFromIds: z.array(z.number().int().positive()).min(1)
});
var CategoryCreateInputSchema = z.object({
  name: z.string().min(1),
  displayOrder: z.number().int().nonnegative().default(0),
  origin: CategoryOriginSchema.default("custom")
});
var CategoryUpdateInputSchema = CategoryCreateInputSchema.partial().strict();
var WalkupRegistrationInputSchema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phone: z.string().nullable().optional(),
  emailOptIn: z.boolean().optional(),
  smsOptIn: z.boolean().optional(),
  paymentMethodToken: z.string().optional(),
  /**
   * GT1-488: the rail the token was minted on, same semantics as
   * RegistrationInputSchema.paymentMethodType. Load-bearing here because this
   * schema STRIPS unknown keys — before this field existed, a bank token taken
   * at the registration desk parsed clean with the declaration silently
   * discarded, so bidderRegistration stored it as `method_type: 'card'` and
   * locked the general merchant slot instead of the ACH one. Slice 7's
   * rail-restricted auto-charge would then route the close-time charge to a card
   * merchant and Neon Pay would reject it.
   */
  paymentMethodType: StoredMethodTypeSchema.optional(),
  /**
   * Non-sensitive display fields captured at tokenization time. Card-shaped by
   * origin but meaningful for ACH too: `paymentMethodBrand` carries the card
   * brand or the bank name, `paymentMethodLast4` the card or bank-account tail.
   */
  paymentMethodBrand: z.string().trim().min(1).max(32).optional(),
  paymentMethodLast4: z.string().regex(/^\d{4}$/).optional(),
  tcAccepted: z.boolean().optional()
});
var WalkupRegistrationResultSchema = z.object({
  bidder: BidderSchema,
  // walk-up registrations don't issue a bidder JWT — the bidder
  // typically isn't holding a device for this flow. Magic-link flow
  // covers the post-event token-bearing path.
  jwt: z.string().nullable()
});
var AdminRoleSchema = z.enum(["admin", "staff"]);
var AdminMeSchema = z.object({
  id: z.number().int().positive(),
  sub: z.string().min(1),
  role: AdminRoleSchema,
  orgId: z.number().int().positive(),
  salesforceId: z.string().min(1),
  // Org subdomain slug — lets the admin shell route a bare-domain (SSO-tile)
  // landing to the org's {orgSlug}.admin.* subdomain. Always present on
  // /admin/me (the redirect's source); optional on other AdminMe payloads
  // (mock-sso, ssoCallback login response) which don't need it.
  orgSlug: z.string().nullable().optional(),
  // Org display name — populated by /admin/me for analytics (Pendo account
  // name). Optional on other AdminMe payloads (ssoCallback, supervisor,
  // mock-sso, e2e-auth) which don't need it.
  orgName: z.string().nullable().optional(),
  displayName: z.string().nullable(),
  email: z.string().email().nullable(),
  isSupervisor: z.boolean().optional(),
  // organizations.is_sandbox — an internal/alpha org rather than a paying one.
  // Consumers gate analytics on it, so absent must never be read as false:
  // it means "this payload predates the field", not "this org is real".
  isSandbox: z.boolean().optional()
});
var OrgAccessEntrySchema = z.object({
  ssoOrgId: z.number().int().positive(),
  salesforceId: z.string().nullable(),
  role: AdminRoleSchema.nullable()
});
var OrgSelectionEntrySchema = z.object({
  ssoOrgId: z.number().int().positive(),
  name: z.string().min(1),
  orgSlug: z.string().nullable()
});
var OrgSelectionRequiredResponseSchema = z.object({
  selectionRequired: z.literal(true),
  /**
   * Short-lived signed token (≤10 min) authorizing a subsequent
   * POST /api/v1/admin/org-select. Distinct issuer/audience from the
   * admin session JWT — admin auth middleware must reject it.
   */
  selectionToken: z.string().min(1),
  /** Provisioned eligible orgs (enriched from the local organizations table). */
  organizations: z.array(OrgSelectionEntrySchema).min(2)
});
var OrgSelectRequestSchema = z.object({
  ssoOrgId: z.number().int().positive()
});
var OrgAdoptRequestSchema = z.object({
  ssoOrgId: z.number().int().positive()
});
var OrgAdoptResponseSchema = z.object({
  jwt: z.string().min(1),
  admin: AdminMeSchema,
  orgSlug: z.string().nullable()
});
var OrgSelectResponseSchema = OrgAdoptResponseSchema;
var OrgEnterRequestSchema = z.object({
  orgSlug: z.string().min(1).max(63)
});
var OrgEnterResponseSchema = OrgAdoptResponseSchema;
var SessionRefreshResponseSchema = z.object({
  jwt: z.string()
});
var SupervisorMeSchema = z.object({
  sub: z.string().min(1),
  email: z.string().email(),
  displayName: z.string().nullable()
});
var SupervisorPermissionSchema = z.enum(["provisioning", "org_deactivation", "financial_metrics"]);
var SupervisorAccessSchema = z.object({
  isSuperAdmin: z.boolean(),
  permissions: z.array(SupervisorPermissionSchema)
});
function supervisorHas(access, requirement) {
  if (access.isSuperAdmin) return true;
  return requirement !== "super_admin" && access.permissions.includes(requirement);
}
var SupervisorOrganizationSchema = z.object({
  name: z.string(),
  salesforceId: z.string(),
  orgSlug: z.string().nullable(),
  ssoOrgId: z.string().nullable(),
  isSandbox: z.boolean(),
  createdAt: z.string()
});
var CreateOrganizationRequestSchema = z.object({
  ssoOrgId: z.number().int().positive(),
  name: z.string().min(1),
  salesforceId: z.string().nullable(),
  ein: z.string().nullable(),
  billingStreet: z.string().nullable().optional(),
  billingCity: z.string().nullable().optional(),
  billingState: z.string().nullable().optional(),
  billingZip: z.string().nullable().optional(),
  billingCountry: z.string().nullable().optional(),
  crmOrgId: z.string().nullable(),
  // Only settable when crmOrgId is entered manually (no SSO CRM instance match).
  // Null when crmOrgId came from SSO discovery — the deployment's own default applies.
  crmEnvironment: z.enum(["uat", "prod"]).nullable(),
  crmInstanceId: z.string().nullable(),
  orgSlug: z.string().nullable(),
  // Defaults to `auctions@${orgSlug ?? crmOrgId ?? 'org'}.neonone.com` server-side when omitted.
  fromAddress: z.string().email().nullable().optional(),
  existingSsoInstanceId: z.number().int().positive().nullable().optional(),
  isSandbox: z.boolean().default(true),
  neonPaySandbox: z.boolean().default(true),
  initialAdminUserId: z.number().int().positive(),
  initialAdminSub: z.string().min(1),
  initialAdminEmail: z.string().email(),
  initialAdminDisplayName: z.string().nullable(),
  // Supervisor merchant ID overrides — set immediately on provisioning if
  // provided, and lock the corresponding slot's org-admin dropdown.
  generalMerchantOverride: z.string().min(1).nullable().optional(),
  achMerchantOverride: z.string().min(1).nullable().optional(),
  digitalWalletMerchantOverride: z.string().min(1).nullable().optional(),
  ippMerchantOverride: z.string().min(1).nullable().optional()
});
var AssumeOrgRequestSchema = z.object({
  salesforceId: z.string().min(1)
});
var AssumeOrgResponseSchema = z.object({
  jwt: z.string(),
  admin: AdminMeSchema,
  orgSlug: z.string().nullable()
});
var CreateSupervisorRequestSchema = z.object({
  email: z.string().email(),
  displayName: z.string().nullable().optional(),
  isSuperAdmin: z.boolean().optional(),
  // Omitted = ['financial_metrics']; an explicit [] = base support access only.
  permissions: z.array(SupervisorPermissionSchema).optional()
});
var UpdateSupervisorAccessRequestSchema = SupervisorAccessSchema;
var SupervisorListItemSchema = z.object({
  sub: z.string().nullable(),
  email: z.string().email(),
  displayName: z.string().nullable(),
  createdAt: z.string(),
  ...SupervisorAccessSchema.shape
});
var SupervisorOrgDetailSchema = z.object({
  name: z.string(),
  salesforceId: z.string(),
  orgSlug: z.string().nullable(),
  ssoOrgId: z.string().nullable(),
  ssoInstanceId: z.number().nullable(),
  emailInstanceId: z.string().nullable(),
  fromAddress: z.string().nullable(),
  ein: z.string().nullable(),
  billingStreet: z.string().nullable(),
  billingCity: z.string().nullable(),
  billingState: z.string().nullable(),
  billingZip: z.string().nullable(),
  billingCountry: z.string().nullable(),
  crmOrgId: z.string().nullable(),
  crmEnvironment: z.enum(["uat", "prod"]).nullable(),
  isSandbox: z.boolean(),
  neonPaySandbox: z.boolean(),
  createdAt: z.string(),
  // Supervisor merchant ID overrides. Non-null locks the corresponding
  // slot's org-admin Settings dropdown.
  generalMerchantOverride: z.string().nullable(),
  achMerchantOverride: z.string().nullable(),
  digitalWalletMerchantOverride: z.string().nullable(),
  ippMerchantOverride: z.string().nullable(),
  // Read-only — the slot's currently-active NeonPay merchant ID (whether set
  // via CRM-driven admin selection or a supervisor override), so a
  // supervisor can see what they'd be locking a slot to.
  currentGeneralMerchantId: z.string().nullable(),
  currentAchMerchantId: z.string().nullable(),
  currentDigitalWalletMerchantId: z.string().nullable(),
  currentIppMerchantId: z.string().nullable()
});
var UpdateOrganizationRequestSchema = z.object({
  name: z.string().min(1).optional(),
  orgSlug: z.string().nullable().optional(),
  ssoOrgId: z.string().nullable().optional(),
  ssoInstanceId: z.number().nullable().optional(),
  emailInstanceId: z.string().nullable().optional(),
  fromAddress: z.string().email().nullable().optional(),
  ein: z.string().nullable().optional(),
  billingStreet: z.string().nullable().optional(),
  billingCity: z.string().nullable().optional(),
  billingState: z.string().nullable().optional(),
  billingZip: z.string().nullable().optional(),
  billingCountry: z.string().nullable().optional(),
  crmOrgId: z.string().nullable().optional(),
  crmEnvironment: z.enum(["uat", "prod"]).nullable().optional(),
  isSandbox: z.boolean().optional(),
  neonPaySandbox: z.boolean().optional(),
  generalMerchantOverride: z.string().min(1).nullable().optional(),
  achMerchantOverride: z.string().min(1).nullable().optional(),
  digitalWalletMerchantOverride: z.string().min(1).nullable().optional(),
  ippMerchantOverride: z.string().min(1).nullable().optional()
});
var SsoOrgDetailSchema = z.object({
  id: z.number(),
  name: z.string(),
  ein: z.string().nullable().optional(),
  billingStreet: z.string().nullable().optional(),
  billingCity: z.string().nullable().optional(),
  billingState: z.string().nullable().optional(),
  billingZip: z.string().nullable().optional(),
  billingCountry: z.string().nullable().optional(),
  salesforceId: z.string().nullable().optional(),
  isTestAccount: z.boolean().optional()
});
var SsoOrgUserSchema = z.object({
  userId: z.number(),
  sub: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  givenName: z.string().nullable(),
  familyName: z.string().nullable(),
  isOrgAdmin: z.boolean()
});
var SsoInstanceSchema = z.object({
  id: z.number(),
  organizationId: z.number(),
  applicationId: z.number(),
  customerId: z.string().nullable(),
  active: z.boolean()
});
var SsoUserLookupResponseSchema = z.object({
  user: SsoOrgUserSchema,
  crmInstances: z.array(SsoInstanceSchema),
  auctionsInstances: z.array(SsoInstanceSchema)
});
var AuctionsRoleSchema = z.enum(["admin", "staff"]);
var OrgMemberSchema = z.object({
  // NeonSSO numeric user id. This is what a role grant is keyed on; `sub` is
  // not available from the member listing (see getOrganizationMembers).
  userId: z.number().int(),
  email: z.string(),
  name: z.string().nullable(),
  // Admin of the NeonSSO ORGANIZATION — distinct from an Auctions role, and
  // not something this surface can change.
  isOrgAdmin: z.boolean(),
  // Auctions role held on this org's instance; null when they have no access.
  // A role id we cannot map to admin/staff also reads as null.
  role: AuctionsRoleSchema.nullable()
});
var OrgMemberListResponseSchema = z.object({
  ssoOrgId: z.number().int(),
  // Null when the org has never been linked to an Auctions instance; the UI
  // shows members but cannot grant until one exists.
  ssoInstanceId: z.number().int().nullable(),
  members: z.array(OrgMemberSchema)
});
var GrantOrgMemberRoleRequestSchema = z.object({
  userId: z.number().int(),
  role: AuctionsRoleSchema
});
var DeactivateSsoInstanceResponseSchema = z.object({
  ssoInstanceId: z.number().int(),
  // Count of role grants revoked, for the confirmation message.
  revokedUserCount: z.number().int(),
  // The org's new placeholder salesforceId. The original is freed for the
  // replacement org and preserved in audit_logs.
  salesforceId: z.string(),
  previousSalesforceId: z.string()
});
var StuckChargeReasonSchema = z.enum(["indeterminate", "never_retried", "unconfirmed"]);
var StuckChargeSchema = z.object({
  invoiceUuid: z.string(),
  // The auction the invoice belongs to. Every admin invoice surface is nested
  // under an auction, so this is what makes the row reachable: without it a
  // supervisor can read the incident but has no route to the invoice itself.
  auctionUuid: z.string(),
  // Cross-org list — orgs are identified by salesforceId; internal numeric
  // org_id never crosses the wire (requirements §2.5).
  orgName: z.string(),
  salesforceId: z.string(),
  bidderName: z.string(),
  bidderEmail: z.string(),
  amountCents: z.number().int(),
  reason: StuckChargeReasonSchema,
  // Charge rail the stuck attempt used. 'unknown' for never_retried rows —
  // there is no transaction row to read a method off of.
  paymentMethod: z.string(),
  // ISO-8601. Charge-transaction createdAt for indeterminate rows; invoice
  // updatedAt (the moment it was claimed 'processing') for never_retried.
  stuckSince: z.string(),
  // Internal payment_transactions.id — null for never_retried (no row yet).
  paymentTransactionId: z.number().int().nullable(),
  failureMessage: z.string().nullable()
});
var StuckRefundSchema = z.object({
  invoiceUuid: z.string(),
  // The auction the invoice belongs to. Every admin invoice surface is nested
  // under an auction, so this is what makes the row reachable: without it a
  // supervisor can read the incident but has no route to the invoice itself.
  auctionUuid: z.string(),
  // Cross-org list — orgs are identified by salesforceId; internal numeric
  // org_id never crosses the wire (requirements §2.5).
  orgName: z.string(),
  salesforceId: z.string(),
  bidderName: z.string(),
  bidderEmail: z.string(),
  /** The refund's own amount, not the invoice total. */
  amountCents: z.number().int(),
  paymentMethod: z.string(),
  /** ISO-8601 — when the refund row was written, i.e. when we called. */
  stuckSince: z.string(),
  /** Internal payment_transactions.id. Always present, unlike a stuck charge. */
  paymentTransactionId: z.number().int(),
  failureMessage: z.string().nullable()
});
var ResolveStuckRefundRequestSchema = z.object({
  resolution: z.enum(["succeeded", "failed"]),
  // Neon Pay refund id the supervisor located in the processor dashboard.
  // Required for 'succeeded' — the backend re-reads the charge from Neon Pay
  // and refuses unless that refund is really there and really settled.
  refundId: z.string().min(1).optional(),
  // Always required: a supervisor override of payment state must be
  // justified in the audit log.
  reason: z.string().min(1)
}).superRefine((val, ctx) => {
  if (val.resolution === "succeeded" && !val.refundId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["refundId"],
      message: 'refundId is required when resolution is "succeeded"'
    });
  }
});
var ResolveStuckChargeRequestSchema = z.object({
  resolution: z.enum(["succeeded", "failed"]),
  // NeonPay charge id the supervisor located in the processor dashboard.
  // Required for 'succeeded' — the backend re-reads the charge from NeonPay
  // and refuses to mark the invoice paid unless it verifies.
  chargeId: z.string().min(1).optional(),
  // Always required: a supervisor override of payment state must be
  // justified in the audit log.
  reason: z.string().min(1)
}).superRefine((val, ctx) => {
  if (val.resolution === "succeeded" && !val.chargeId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["chargeId"],
      message: 'chargeId is required when resolution is "succeeded"'
    });
  }
});
var StrandedVerificationHoldSchema = z.object({
  /** `bidder_payment_methods.public_id` — the retry-release path parameter. */
  paymentMethodPublicId: z.string(),
  // Cross-org list — orgs are identified by salesforceId; internal numeric
  // org_id never crosses the wire (requirements §2.5).
  orgName: z.string(),
  salesforceId: z.string(),
  bidderName: z.string(),
  bidderEmail: z.string(),
  /** `npcharge_…` — the operator's handle for this hold in the Neon Pay portal. */
  neonPayChargeId: z.string(),
  /**
   * The authorized amount still held on the card. A hold, not a balance: it is
   * never presented as money to collect, and there is no path from this list
   * that captures it.
   */
  holdAmountCents: z.number().int(),
  /** ISO-8601 — when the authorization was placed. */
  authorizedAt: z.string(),
  /** Release attempts so far, inline and by the reaper. */
  releaseAttempts: z.number().int(),
  /** Operator-safe summary of the last release failure. */
  lastReleaseError: z.string().nullable()
});
var StrandedHoldReleaseStatusSchema = z.enum(["released", "pending", "failed"]);
var RetryHoldReleaseResultSchema = z.object({
  paymentMethodPublicId: z.string(),
  releaseStatus: StrandedHoldReleaseStatusSchema,
  releaseAttempts: z.number().int(),
  lastReleaseError: z.string().nullable()
});
var PresignedSlotSchema = z.object({
  key: z.string(),
  uploadUrl: z.string().url(),
  expiresAt: Iso8601Schema,
  variants: z.object({
    thumb: z.string().url(),
    medium: z.string().url(),
    original: z.string().url()
  }),
  /**
   * Headers the uploading client MUST send verbatim on the presigned S3 PUT.
   *
   * The presigned-PUT signature covers only `host` — `cache-control` is in the
   * SigV4 always-unsignable set and the presigner force-adds `content-type` to
   * it — so setting these on the server's signing PutObjectCommand is a no-op.
   * S3 stores only what the *client* actually sends, which is why the backend
   * has to hand them over instead of applying them itself.
   *
   * Optional and additive: an admin-web build predating GT1-431 still parses a
   * newer backend's response, and a newer admin-web tolerates an older backend
   * (or the feature flag being off) by spreading nothing.
   *
   * Every header named here must also appear in the photos bucket's CORS
   * `allowed_headers` (see each env's `storage.tf` under
   * auctions-backend/infra/environments) or the browser's preflight rejects
   * the PUT.
   */
  requiredHeaders: z.record(z.string(), z.string()).optional()
});
var BrandingPresignedSlotSchema = z.object({
  key: z.string(),
  uploadUrl: z.string().url(),
  cdnUrl: z.string().url(),
  expiresAt: Iso8601Schema,
  /**
   * Headers the uploading client MUST send verbatim on the presigned S3 PUT.
   *
   * The presigned-PUT signature covers only `host` — `cache-control` is in the
   * SigV4 always-unsignable set and the presigner force-adds `content-type` to
   * it — so setting these on the server's signing PutObjectCommand is a no-op.
   * S3 stores only what the *client* actually sends, which is why the backend
   * has to hand them over instead of applying them itself. presignBranding()
   * set both for as long as that path existed and neither ever took effect;
   * every branding object written before GT1-431 has no cache header.
   *
   * The value here is deliberately shorter-lived than the item-photo one
   * (PresignedSlotSchema): a logo is a mutable slot an admin re-uploads, not a
   * content-addressed immutable object.
   *
   * Optional and additive: an admin-web build predating GT1-431 still parses a
   * newer backend's response, and a newer admin-web tolerates an older backend
   * (or the feature flag being off) by spreading nothing.
   *
   * Every header named here must also appear in the photos bucket's CORS
   * `allowed_headers` (see each env's `storage.tf` under
   * auctions-backend/infra/environments) or the browser's preflight rejects
   * the PUT.
   */
  requiredHeaders: z.record(z.string(), z.string()).optional()
});
var BULK_SELECTION_MAX = 250;
var BulkItemIdsInputSchema = z.object({
  itemIds: z.array(z.number().int().positive()).min(1).max(BULK_SELECTION_MAX)
});
var BulkWithdrawInputSchema = BulkItemIdsInputSchema.extend({
  reason: z.string().max(500).optional()
});
var BulkItemResultSchema = z.object({
  itemId: z.number().int().positive(),
  success: z.boolean(),
  error: z.string().optional()
});
var BulkActionResponseSchema = z.object({
  results: z.array(BulkItemResultSchema)
});
var ReapplyDefaultsInputSchema = z.object({
  itemIds: z.array(z.number().int().positive()),
  bundleIds: z.array(z.number().int().positive()).optional()
}).refine((v) => v.itemIds.length + (v.bundleIds?.length ?? 0) >= 1, {
  message: "select at least one item or bundle"
}).refine((v) => v.itemIds.length + (v.bundleIds?.length ?? 0) <= BULK_SELECTION_MAX, {
  message: `selection exceeds ${BULK_SELECTION_MAX} rows`
});
var ReapplyDefaultsSkipSchema = z.object({
  id: z.number().int().positive(),
  kind: z.enum(["item", "bundle"]),
  reason: z.string()
});
var ReapplyDefaultsResponseSchema = z.object({
  applied: z.number().int().nonnegative(),
  skipped: z.array(ReapplyDefaultsSkipSchema),
  // Echo of the percentages actually applied, so the confirmation can name
  // them without re-reading the auction and risking a different answer than
  // the one this run used. Null means that field is in fixed mode.
  startingBidPercent: z.number().int().min(1).max(100).nullable(),
  bidIncrementPercent: z.number().int().min(1).max(100).nullable()
});
var ItemListQuerySchema = z.object({
  offset: z.coerce.number().int().nonnegative().default(0),
  limit: z.coerce.number().int().min(1).max(200).default(25),
  search: z.string().max(200).optional(),
  status: z.string().optional(),
  categoryId: z.string().optional(),
  sortField: z.string().optional(),
  sortOrder: z.enum(["asc", "desc"]).optional()
});
var ItemListResponseSchema = z.object({
  items: z.array(AuctionItemSchema),
  totalCount: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().positive()
});
var BidCountsResponseSchema = z.record(z.string(), z.number().int().nonnegative());
var AdminBidEntryFields = z.object({
  id: z.number().int().positive(),
  itemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  itemTitle: z.string().nullable(),
  bidderId: z.number().int().positive(),
  bidderUuid: UuidSchema,
  bidderDisplay: z.string(),
  amountCents: CentsSchema,
  bidType: BidTypeSchema,
  // GT1-442 follow-up: a `max` bid stores its MATERIALIZED ladder price in
  // amountCents (what it currently bids) and its ceiling here. Bid History
  // showed only amountCents, so a $250 max read as a $50 bid. Admin-only —
  // never add this to a bidder-facing shape. Null on standard bids.
  maxAmountCents: CentsSchema.nullable(),
  voided: z.boolean(),
  voidReason: z.string().nullable(),
  // GT1-442 (bid-void-design §8): surface the voiding admin + timestamp on
  // voided rows in Bid History. Null on non-voided rows.
  voidedByAdminId: z.number().int().positive().nullable(),
  voidedAt: Iso8601Schema.nullable(),
  // Display name of the voiding admin for the Bid History row (§8 shows the
  // admin, not just the id). Null on non-voided rows or when unresolved.
  voidedByAdminName: z.string().nullable(),
  placedAt: Iso8601Schema,
  placedBy: BidPlacedBySchema.optional()
});
var AdminBidEntrySchema = AdminBidEntryFields.superRefine((entry, ctx) => {
  if (entry.bidType === "max") {
    if (entry.maxAmountCents === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["maxAmountCents"],
        message: "a max bid must carry its ceiling"
      });
      return;
    }
    if (entry.maxAmountCents < entry.amountCents) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["maxAmountCents"],
        message: "a max bid ceiling cannot be below its materialized amount"
      });
    }
    return;
  }
  if (entry.maxAmountCents !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["maxAmountCents"],
      message: "a standard bid must not carry a ceiling"
    });
  }
});
var AdminBidsResponseSchema = z.object({
  bids: z.array(AdminBidEntrySchema),
  nextCursor: z.number().int().positive().nullable()
});
var SaleStateSchema = z.enum([
  "sold",
  "invoicing",
  "needs_buyer",
  "offer_outstanding",
  "offer_ended",
  "sale_canceled",
  "unsold"
]);
var SaleStateReasonSchema = z.enum(["declined", "lapsed", "cancelled"]);

// src/wire/admin/LiveDashboardItem.ts
var LiveDashboardItemSchema = z.object({
  id: z.number().int().positive(),
  title: z.string().min(1),
  status: AuctionItemStatusSchema,
  placedBy: BidPlacedBySchema.optional(),
  saleState: SaleStateSchema.optional(),
  saleStateReason: SaleStateReasonSchema.nullable().optional(),
  startingBidCents: CentsSchema,
  fairMarketValueCents: CentsSchema,
  currentHighBidCents: CentsSchema,
  bidCount: z.number().int().nonnegative(),
  topBidderName: z.string().nullable(),
  topBidderUuid: UuidSchema.nullable(),
  closedAt: Iso8601Schema.nullable(),
  primaryPhotoThumbUrl: z.string().url().nullable(),
  primaryPhotoMediumUrl: z.string().url().nullable(),
  bids: z.array(AdminBidEntrySchema),
  // Bundles are their own DB entity (`bundles` table) merged into this
  // same list — on a bundle row, `isBundle: true` and `id`/`bundleId` are
  // the SAME value (bundleId is a self-reference), mirroring the
  // PublicItemSummary convention used on the bidder-facing catalog.
  isBundle: z.boolean().optional(),
  bundleId: z.number().int().positive().nullable().optional(),
  memberCount: z.number().int().nonnegative().optional()
});
var LiveDashboardItemsResponseSchema = z.object({
  items: z.array(LiveDashboardItemSchema)
});
var VoidBidRequestSchema = z.object({
  reason: z.string().min(1).max(500)
});
var VoidBidResponseSchema = z.object({
  itemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable().optional(),
  voidedBidIds: z.array(z.number().int().positive()).min(1),
  recalculatedHighBidCents: CentsSchema,
  recalculatedHighBidderUuid: UuidSchema.nullable()
}).refine((v) => v.itemId != null !== ((v.bundleId ?? null) != null), {
  message: "exactly one of itemId/bundleId must be set",
  path: ["itemId"]
});
var ResumeEndTimeSchema = z.enum(["restore_remaining", "original"]);
var ResumeAuctionRequestSchema = z.object({ endTime: ResumeEndTimeSchema.default("restore_remaining") }).default({ endTime: "restore_remaining" });
var LiveDashboardStatsSchema = z.object({
  registeredBidderCount: z.number().int().nonnegative(),
  onlineBidderCount: z.number().int().nonnegative()
});
var BidderActivityFactsSchema = z.object({
  itemTitle: z.string(),
  theirMaxBidCents: CentsSchema,
  currentHighBidCents: CentsSchema,
  isLeading: z.boolean(),
  lastBidAt: Iso8601Schema,
  primaryPhotoThumbUrl: z.string().nullable()
});
var BidderActivityItemSchema = z.object({
  kind: LotKindSchema.default("item")
}).passthrough().pipe(z.discriminatedUnion("kind", [
  z.object({
    ...BidderActivityFactsSchema.shape,
    kind: z.literal("item"),
    itemId: z.number().int().positive(),
    bundleId: z.never().optional()
  }),
  z.object({
    ...BidderActivityFactsSchema.shape,
    kind: z.literal("bundle"),
    bundleId: z.number().int().positive(),
    itemId: z.never().optional()
  })
]));
var BidderActivitySchema = z.object({
  bidder: z.object({
    uuid: UuidSchema,
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    registeredAt: Iso8601Schema,
    activated: z.boolean(),
    isOnline: z.boolean()
  }),
  items: z.array(BidderActivityItemSchema),
  totalBidCount: z.number().int().nonnegative(),
  leadingCount: z.number().int().nonnegative(),
  spendingCents: CentsSchema
});
var NeonPayMerchantDetailSchema = z.object({
  merchantId: z.string(),
  name: z.string(),
  hasGooglePay: z.boolean(),
  hasApplePay: z.boolean(),
  chargesEnabled: z.boolean(),
  /**
   * GT1-488: the payment processor behind this Neon Pay merchant, normalised to
   * a lowercase slug (`payrix`, `stripe`, `pay_gateway`, `paypal`, …). Null when
   * Neon Pay did not report one — never defaulted, because "we don't know the
   * processor" and "the processor is X" drive different capability answers.
   * Distinct from `hasGooglePay` / `hasApplePay`, which stay `?? true`
   * fail-open: an absent boolean must not hide a wallet button.
   */
  processor: z.string().nullable().default(null)
});
var MerchantDetailsResponseSchema = z.object({
  merchants: z.array(NeonPayMerchantDetailSchema)
});
var CrmPaymentGatewaySchema = z.object({
  merchantId: z.string(),
  type: z.enum(["CC", "ACH", "PPV", "IPP"]),
  default: z.boolean()
});
var CrmGatewaySlotSchema = z.object({
  slot: z.enum(["general", "ach", "digital_wallet", "ipp"]),
  options: z.array(z.object({
    merchantId: z.string(),
    types: z.array(z.enum(["CC", "ACH", "PPV", "IPP"])),
    isDefault: z.boolean()
  }))
});
var CrmPaymentGatewaysResponseSchema = z.object({
  slots: z.array(CrmGatewaySlotSchema)
});
var MethodAvailabilitySchema = z.enum(["available", "unavailable"]);
var PlatformAvailabilitySchema = z.object({
  ach: MethodAvailabilitySchema,
  applePay: MethodAvailabilitySchema,
  googlePay: MethodAvailabilitySchema,
  venmo: MethodAvailabilitySchema,
  paypal: MethodAvailabilitySchema
});
var MethodBlockedReasonSchema = z.enum([
  "platform_disabled",
  "merchant_missing",
  "processor_unsupported"
]);
var MethodAvailabilityDetailSchema = z.object({
  /**
   * Effective availability: platform flag AND a merchant for the rail's slot
   * AND the processor not being known-incapable. Strictly narrower than
   * `platformAvailability.<method>`, which is flag-only — do NOT treat the two
   * as interchangeable. The org's own on/off toggle is deliberately NOT folded
   * in: the Settings page renders that toggle itself, and folding it in would
   * make "you turned it off" indistinguishable from "it can't work".
   */
  availability: MethodAvailabilitySchema,
  /** Null exactly when `availability === 'available'`. */
  reason: MethodBlockedReasonSchema.nullable().default(null),
  /**
   * Processor capability for this rail on its own. 'unknown' is NOT a blocker —
   * it means we could not ask Neon Pay (or have not yet), and is treated as
   * 'supported' for every gating decision so an outage cannot close checkout.
   */
  capability: MethodCapabilitySchema.default("unknown")
});
var MethodAvailabilityDetailsSchema = z.object({
  /** Processor slug behind the org's general merchant; null when unknown. */
  processor: z.string().nullable().default(null),
  ach: MethodAvailabilityDetailSchema,
  applePay: MethodAvailabilityDetailSchema,
  googlePay: MethodAvailabilityDetailSchema,
  venmo: MethodAvailabilityDetailSchema,
  paypal: MethodAvailabilityDetailSchema,
  /**
   * Card-present terminal payments. Absent from PlatformAvailabilitySchema
   * entirely (it predates the IPP slot), so this is the only place the Settings
   * page can learn why the terminal rail is off.
   */
  ipp: MethodAvailabilityDetailSchema
});
var ApplePayProvisioningStateSchema = z.enum([
  "not_started",
  "pending",
  "provisioned",
  "failed"
]);
var ApplePayProvisioningErrorCodeSchema = z.enum([
  "bidder_domain_unresolved",
  "merchant_missing",
  "processor_unsupported",
  "association_file_unavailable",
  "association_file_publish_failed",
  "platform_credentials_unconfigured",
  "domain_registration_failed",
  "merchant_domains_unreadable"
]);
var ApplePayProvisioningSchema = z.object({
  state: ApplePayProvisioningStateSchema,
  /** Bidder host the file + registration are for. Null before the first attempt. */
  domain: z.string().nullable().default(null),
  /** Canonical processor the association file was sourced for. */
  processor: z.string().nullable().default(null),
  /** Null unless `state === 'failed'`. */
  errorCode: ApplePayProvisioningErrorCodeSchema.nullable().default(null),
  /** Operator-facing sentence naming the next action. Null unless failed. */
  errorMessage: z.string().nullable().default(null),
  /** Provisioning attempts so far, including the in-flight one. */
  attempts: z.number().int().nonnegative().default(0),
  lastAttemptAt: z.string().nullable().default(null),
  provisionedAt: z.string().nullable().default(null)
});
var OrgPaymentConfigSchema = z.object({
  status: z.enum(["ready", "pending", "unconfigured"]),
  merchantId: z.string().nullable(),
  achMerchantId: z.string().nullable(),
  digitalWalletMerchantId: z.string().nullable(),
  ippMerchantId: z.string().nullable(),
  lastVerifiedAt: z.string().nullable(),
  achEnabled: z.boolean(),
  applePayEnabled: z.boolean(),
  googlePayEnabled: z.boolean(),
  venmoEnabled: z.boolean(),
  paypalEnabled: z.boolean(),
  /** Org-admin on/off toggle for IPP (card-terminal) payments. */
  ippEnabled: z.boolean(),
  /** When true, the mock card field renders and MockNeonPayClient processes charges. */
  testMode: z.boolean(),
  /**
   * Supervisor-set merchant ID override. Non-null both pins the slot's
   * active NeonPay merchant and locks the org-admin dropdown for that slot.
   */
  generalMerchantOverride: z.string().nullable(),
  achMerchantOverride: z.string().nullable(),
  digitalWalletMerchantOverride: z.string().nullable(),
  ippMerchantOverride: z.string().nullable(),
  /**
   * Read-only platform-level availability per method (feature flag layer —
   * global flag + sandbox scope + supervisor org override). Never mutated via
   * PaymentConfigUpdateSchema; the customer's own toggles above are preserved
   * even while a method is platform-disabled.
   */
  platformAvailability: PlatformAvailabilitySchema,
  /**
   * GT1-488: read-only, richer sibling of `platformAvailability` — same
   * per-method question, but answered across all three layers (flag, merchant
   * slot, processor capability) and carrying the reason plus an `ipp` key.
   * `platformAvailability` is left byte-identical so existing consumers keep
   * working; new UI should prefer this field.
   *
   * Optional on the wire on purpose: `OrgSettings` is constructed as a typed
   * literal in admin-web's store and test fixtures, so a required field here
   * would be a breaking change for every one of them. The backend always
   * populates it.
   */
  methodAvailability: MethodAvailabilityDetailsSchema.optional(),
  /**
   * GT1-488 slice 4: read-only Apple Pay domain-provisioning status for this
   * org. `applePayEnabled` above is the admin's intent; this is whether the
   * platform has finished making it work (association file stored + the org's
   * bidder domain registered on the Neon Pay merchant).
   *
   * Optional on the wire for the same reason as `methodAvailability`: admin-web
   * builds `OrgSettings` as typed literals in its store and fixtures. The
   * backend always populates it — `state: 'not_started'` when Apple Pay has
   * never been switched on.
   */
  applePayProvisioning: ApplePayProvisioningSchema.optional()
});
var OrgEmailConfigSchema = z.object({
  /** Null until a supervisor assigns the CRM email instance for this org. */
  instanceId: z.string().nullable(),
  fromAddress: z.string(),
  fromName: z.string(),
  replyTo: z.string().nullable(),
  /**
   * Live result of Neon CRM's own sender-domain verification check
   * (`POST /senders/verifySenderDomain`), read at request time — never
   * cached. Not used to decide the From address at send time: Neon CRM's
   * own send path self-verifies and self-substitutes independently of
   * anything Auctions stores. This exists purely so Settings can show the
   * admin a warning before they've sent anything. null = the check could
   * not be completed (treat as "unknown", not "unverified").
   */
  senderDomainVerified: z.boolean().nullable(),
  /**
   * Neon CRM's own computed address it would substitute for fromAddress
   * while the sender domain is unverified. Null when verified or when the
   * check couldn't complete — the only case the UI renders this is
   * `senderDomainVerified === false`, where it is always populated.
   */
  fallbackFromAddress: z.string().nullable()
});
var OrgSettingsSchema = z.object({
  orgSlug: z.string().nullable(),
  paymentConfig: OrgPaymentConfigSchema,
  crmConfig: z.object({
    crmOrgId: z.string().nullable(),
    crmEnvironment: z.enum(["uat", "prod"]).nullable(),
    // CRM access is platform-wide: apiConfigured reflects whether the
    // environment's Neon One token is present, not any per-org credential.
    apiConfigured: z.boolean()
  }),
  emailConfig: OrgEmailConfigSchema.nullable(),
  defaultDonorCoveredFees: z.boolean(),
  defaultPaymentMethodRequirement: z.enum(["required", "allow_pay_later"]),
  defaultAutoCheckoutMode: z.enum(["auto_charge", "offer_default"]),
  defaultTermsAndConditionsHtml: z.string().nullable(),
  defaultTemplatesSummary: z.array(z.object({ kind: z.string(), subject: z.string() })),
  crmAutoSyncEnabled: z.boolean(),
  /**
   * Display name of the admin who last flipped `crmAutoSyncEnabled`. Null when
   * the default has never been changed, or when the actor had no resolvable
   * display name at write time — never fabricated. Optional on the wire so
   * admin-web fixtures that build `OrgSettings` as typed literals stay valid;
   * the backend always populates it (null for fresh orgs).
   */
  crmAutoSyncLastChangedBy: z.string().nullable().optional(),
  /**
   * ISO-8601 timestamp of the last flip of `crmAutoSyncEnabled`. Null until
   * the first change. Optional for the same fixture reason as
   * `crmAutoSyncLastChangedBy`; backend always populates it.
   */
  crmAutoSyncLastChangedAt: z.string().nullable().optional()
});
var PaymentConfigUpdateSchema = z.object({
  merchantId: z.string().min(1).optional(),
  achMerchantId: z.string().min(1).optional(),
  digitalWalletMerchantId: z.string().min(1).optional(),
  ippMerchantId: z.string().min(1).optional(),
  achEnabled: z.boolean().optional(),
  applePayEnabled: z.boolean().optional(),
  googlePayEnabled: z.boolean().optional(),
  venmoEnabled: z.boolean().optional(),
  paypalEnabled: z.boolean().optional(),
  ippEnabled: z.boolean().optional(),
  testMode: z.boolean().optional()
});
var CONTROL_CHARS = /[\u0000-\u001F\u007F]/;
var OrgSettingsUpdateSchema = z.object({
  orgSlug: z.string().regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/).optional(),
  defaultDonorCoveredFees: z.boolean().optional(),
  defaultPaymentMethodRequirement: z.enum(["required", "allow_pay_later"]).optional(),
  defaultAutoCheckoutMode: z.enum(["auto_charge", "offer_default"]).optional(),
  paymentConfig: PaymentConfigUpdateSchema.optional(),
  crmConfig: z.object({
    crmOrgId: z.string().optional(),
    crmEnvironment: z.enum(["uat", "prod"]).optional()
  }).optional(),
  /**
   * Org admins own the sender identity (GT1-482). instanceId is deliberately
   * ABSENT: it is supervisor-owned and routes mail through a CRM email
   * instance, so it must not be settable from the org-admin surface. Zod strips
   * it from the payload; the supervisor routes write that column directly.
   */
  emailConfig: z.object({
    fromAddress: z.string().trim().email().optional(),
    // Control characters — CR/LF above all — would let a display name break out
    // of the From header into a header-injection payload.
    fromName: z.string().trim().min(1).max(120).refine((v) => !CONTROL_CHARS.test(v), {
      message: "Sender name must not contain line breaks or control characters"
    }).optional(),
    replyTo: z.string().trim().email().nullable().optional()
  }).optional(),
  defaultTermsAndConditionsHtml: z.string().nullable().optional(),
  crmAutoSyncEnabled: z.boolean().optional()
});
var AdminUserListItemSchema = z.object({
  sub: z.string().min(1),
  displayName: z.string().nullable(),
  email: z.string().email().nullable(),
  createdAt: z.string()
});
var AdminUserListResponseSchema = z.object({
  users: z.array(AdminUserListItemSchema),
  portalUrl: z.string()
});
var RegenerateReceiptResponseSchema = z.object({
  jobId: z.string().min(1),
  requestedAt: Iso8601Schema
});
var ReceiptRegenStatusSchema = z.enum(["pending", "succeeded", "failed"]);
var ReceiptRegenerationSchema = z.object({
  jobId: z.string().min(1),
  status: ReceiptRegenStatusSchema
});

// src/wire/admin/InvoiceAdmin.ts
var InvoiceLineItemSummarySchema = z.object({
  id: z.number().int().positive(),
  itemId: z.number().int().positive().nullable(),
  itemTitle: z.string(),
  amountCents: CentsSchema
});
var InvoiceSummarySchema = z.object({
  uuid: UuidSchema,
  bidderUuid: UuidSchema,
  bidderName: z.string(),
  bidderEmail: z.string().email(),
  itemCount: z.number().int().min(0),
  subtotalCents: CentsSchema,
  donationCents: CentsSchema,
  feeCoverageCents: CentsSchema,
  totalCents: CentsSchema,
  // Net cents refunded — sum of succeeded refund transactions. Lets every
  // admin surface show the same net-of-refunds amount (`totalCents -
  // refundedCents`) the settlement console already uses. Optional for wire
  // back-compat: absent means 0.
  refundedCents: CentsSchema.optional(),
  status: InvoicePaymentStatusSchema,
  // The bidder's stored payment method, or null when they have none.
  //
  // GT1-488: `methodType` is what lets a consumer say "Bank account" instead of
  // guessing from `brand`. Optional so pre-GT1-488 producers and fixtures still
  // parse (absent ≡ 'card', the baseline rail).
  //
  // `brand` / `last4` became nullable in the same change: they are nullable
  // columns and the non-card rails legitimately carry neither (Apple/Google Pay
  // have no brand and no tail; an ACH token may report a tail with no bank
  // name). Requiring both is what made the producer emit `null` for a stored
  // ACH — i.e. claim "no payment method on file" for a bidder who has one.
  paymentMethodOnFile: z.object({
    brand: z.string().nullable(),
    last4: z.string().nullable(),
    methodType: StoredMethodTypeSchema.optional()
  }).nullable(),
  lastFailureCode: z.string().nullable(),
  lastFailureMessage: z.string().nullable(),
  // NeonPay charge id (e.g. "npcharge_…") of the latest succeeded charge, so
  // admins can trace and reconcile the payment in the NeonPay portal. Null for
  // offline (cash/check/wire) or not-yet-paid invoices. Optional for wire
  // back-compat.
  neonPayChargeId: z.string().nullable().optional(),
  updatedAt: Iso8601Schema
});
var InvoiceDetailSchema = InvoiceSummarySchema.extend({
  lineItems: z.array(InvoiceLineItemSummarySchema),
  receiptPdfUrl: z.string().nullable().optional(),
  receiptPdfGeneratedAt: Iso8601Schema.nullable().optional(),
  receiptRegeneration: ReceiptRegenerationSchema.nullable().optional(),
  // True when an in-flight (pending) card-terminal charge exists for this
  // invoice. Admin-web uses this to resume the terminal-wait UI on open rather
  // than inferring a terminal wait from a raw 'processing' status — which would
  // misfire if another async payment method is added later. (GT1-327)
  hasPendingTerminalCharge: z.boolean().optional()
});
var InvoiceSummaryListResponseSchema = z.object({
  items: z.array(InvoiceSummarySchema)
});
var CountSchema = z.number().int().nonnegative();
var EventMoneySchema = z.object({
  collectedCents: CentsSchema,
  clearingCents: CentsSchema,
  unconfirmedCents: CentsSchema,
  outstandingCents: CentsSchema,
  expectedRevenueCents: CentsSchema,
  itemSalesCents: CentsSchema,
  donationsCents: CentsSchema,
  donorCoveredFeesCents: CentsSchema,
  refundsCents: CentsSchema,
  refundSettlingCents: CentsSchema,
  owedBackCents: CentsSchema,
  biddersOwingCount: CountSchema,
  biddersSettledCount: CountSchema,
  declinedInvoicesCount: CountSchema,
  itemsPaidForCount: CountSchema,
  donationsCount: CountSchema,
  biddersCoveringFeesCount: CountSchema,
  refundsCount: CountSchema
});

// src/wire/admin/SettlementInvoice.ts
var SettlementStatusSchema = z.enum([
  "paid_auto",
  "paid_online",
  "paid_offline",
  "awaiting",
  "processing",
  "clearing",
  "unconfirmed",
  "failed",
  "no_card",
  "partially_refunded",
  "refunded",
  // ADR-012 §4. Terminal empty invoice — nothing collected, nothing owed.
  // Not collectible; settled feed label "Void".
  "void"
]);
var ItemDispositionSchema = z.enum([
  "never_offered",
  "offer_outstanding",
  "offer_declined",
  "offer_lapsed",
  "offer_cancelled",
  "reassigned"
]);
var BidStackEntrySchema = z.object({
  bidderUuid: UuidSchema,
  name: z.string(),
  amountCents: CentsSchema,
  placedAt: Iso8601Schema,
  placedBy: BidPlacedBySchema.optional()
});
var RefundStateSchema = z.enum(["none", "partial", "full", "failed"]);
var SettlementBundleSchema = z.object({
  id: z.number().int().positive(),
  title: z.string(),
  contents: z.array(z.object({
    itemId: z.number().int().positive(),
    title: z.string()
  }))
});
var SettlementInFlightChargeSchema = z.object({
  rail: PaymentMethodSchema,
  // Null before a submission is known; terminal sessions never imply clearing.
  submittedAt: Iso8601Schema.nullable()
});
var SettlementLineItemSchema = z.object({
  // Public line-item id (uuid). Refund and decline requests send this back
  // verbatim — internal numeric line-item ids never reach the wire.
  lineId: UuidSchema,
  // The lot this line charges for. An auction_item line names exactly one of
  // the pair (auction_items.id for a single item, bundles.id for a bundle);
  // donation / fee-coverage lines name neither. Enforced by the refine below.
  // Numeric on purpose: the next-highest-offer endpoint addresses items by
  // this id. Resolve the pair with `lotRefOfLine`, never by testing one side.
  auctionItemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  // Catalog lot number the attention feed prefixes as "Lot NN" (inventory
  // §7.1). Same integer as auctionItemId today — kept as its own field so
  // feed meta never has to invent a number from an id, and so a future
  // per-event lot sequence can land without renaming the wire. Null on
  // bundle lines (a bundle has no catalog lot number) and on donation /
  // fee-coverage lines (no lot to name).
  lotNumber: z.number().int().positive().nullable(),
  kind: InvoiceLineItemTypeSchema,
  itemTitle: z.string(),
  amountCents: CentsSchema,
  fmvCents: CentsSchema.nullable(),
  taxDeductibleCents: CentsSchema,
  declined: z.boolean(),
  // Settled refund cents only. A failed obligation takes precedence over
  // partial/full in refundState; submitted refunds stay in inFlightRefundCents.
  refundedCents: CentsSchema,
  refundState: RefundStateSchema.optional(),
  saleState: SaleStateSchema.optional(),
  saleStateReason: SaleStateReasonSchema.nullable().optional(),
  bundle: SettlementBundleSchema.nullable().optional(),
  // Cents on this line already claimed by a refund that Neon Pay accepted but
  // has not settled yet (an ACH refund takes days). Summed from the line's own
  // refund_obligations in a live status, which is what makes it per-LINE:
  // payment_transactions is invoice-scoped and carries no line attribution.
  //
  // Money in flight is money already spoken for. A line carrying it must never
  // be refunded a second time — refundedCents stays 0 until the webhook lands,
  // so nothing else downstream would catch the duplicate — but it must still be
  // releasable, or a lot sits unsellable for days waiting on a refund that has
  // already been accepted (ADR-014).
  inFlightRefundCents: CentsSchema,
  // Cents that can still be sent back on this line: what was collected on it,
  // less refunded, less in flight. Computed on the server and authoritative. 0
  // on an invoice that collected nothing, and 0 on a line released before its
  // invoice was paid, since the charge never included it (GT1-586). Do not
  // derive this from the invoice's status plus amountCents − refundedCents:
  // the status says money was taken on the invoice, not on this line.
  refundableCents: CentsSchema,
  // ── GT1-445 reassignment context ──────────────────────────────────────
  // Null / empty / zero on non-auction_item lines. These are what let every
  // settlement surface answer "can this item be reassigned, to whom, and where
  // has it already gone?" without a second request to the catalog or the bids
  // feed. All required: the builder always emits them, and this schema is
  // applied with `.safeParse` at the response boundary — a field missing here
  // is silently stripped from the payload rather than rejected.
  // Primary photo, falling back to the first. Null when the item has none.
  photo: PhotoVariantSchema.nullable(),
  itemStatus: AuctionItemStatusSchema.nullable(),
  // GT1-440. Non-null when an admin canceled this item's completed sale.
  // itemStatus is 'unsold' for BOTH a canceled sale and a lot that closed with
  // no qualifying bid, so this timestamp is the only thing that tells them
  // apart -- which is what every item-centric surface needs in order to stop
  // rendering the invoice's payment status ('Awaiting checkout') over a sale
  // that no longer exists.
  //
  // Whether the departing winner is still owed money is NOT carried here: it is
  // `declined && refundedCents < amountCents` on a paid invoice, which self-
  // clears as the refund lands instead of needing a second field kept in sync.
  saleCanceledAt: Iso8601Schema.nullable(),
  // Non-voided bids on the item. A voided bid is not a bid any more.
  bidCount: z.number().int().nonnegative(),
  // One row per bidder at their best non-voided bid, highest first. INCLUDES
  // the current winner — the item drawer lists everyone; excluding the winner
  // is the reassign dialog's job, not the wire's.
  bidStack: z.array(BidStackEntrySchema),
  pendingOffer: z.object({
    offerPublicId: UuidSchema,
    bidderUuid: UuidSchema,
    bidderName: z.string(),
    amountCents: CentsSchema,
    expiresAt: Iso8601Schema
  }).nullable(),
  // Where the item went when it left this line: the line is declined AND the
  // item is now held by someone else. A declined line with no new owner is an
  // unresolved item, not a reassigned one.
  reassignedTo: z.object({
    bidderUuid: UuidSchema,
    bidderName: z.string(),
    amountCents: CentsSchema
  }).nullable(),
  // ADR-015 §5. Server-derived lot disposition. Non-auction_item lines carry
  // `never_offered` (no lot to place). Do not infer client-side from
  // pendingOffer / declined — three terminal offer states read identically on
  // those fields alone.
  disposition: ItemDispositionSchema,
  // AC14 buckets. Derived from disposition: outstanding waits on the bidder;
  // declined / lapsed / cancelled need the admin. never_offered and reassigned
  // sit in neither.
  needsAdminAction: z.boolean(),
  waitingOnBidder: z.boolean()
}).superRefine((line, ctx) => {
  if (line.kind === "auction_item") {
    if (!namesExactlyOneLot(line)) {
      ctx.addIssue({ code: "custom", message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] });
    }
  } else if (line.auctionItemId !== null || line.bundleId !== null) {
    ctx.addIssue({
      code: "custom",
      message: `a ${line.kind} line names no lot`,
      path: ["auctionItemId"]
    });
  }
});
var InvoiceLastFailureSchema = z.object({
  /** "Visa ····4242" / "Bank account ····6789" / "Apple Pay" / "Card" / … */
  methodLabel: z.string(),
  /** Processor decline code (e.g. `insufficient_funds`, `do_not_honor`). */
  code: z.string(),
  /** Human-readable decline reason suitable for admin meta copy; empty when none was given. */
  message: z.string(),
  attemptedAt: Iso8601Schema,
  /**
   * True when the processor indicated the method must not be retried
   * (`do_not_honor`, `closed_account`, …). Soft declines like
   * `insufficient_funds` stay false so the drawer can offer same-card retry.
   */
  hardDecline: z.boolean()
});
var SettlementInvoiceSchema = z.object({
  invoiceUuid: UuidSchema,
  // ADR-012 §7 / Q-UI-2. Human-readable identity unique per auction event
  // ("INV-1041"). Assigned at insert, never reused. Every meta line, switcher
  // option and consequence sentence names the invoice with this.
  invoiceNumber: z.string().min(1),
  // ADR-012 §7. Orders a bidder's invoice set — drawer entry point is "open
  // invoice, else most recent by createdAt".
  createdAt: Iso8601Schema,
  bidderUuid: UuidSchema,
  bidderName: z.string(),
  bidderEmail: z.string().nullable(),
  status: SettlementStatusSchema,
  linkSent: z.boolean().optional(),
  // Authoritative backend aggregates (cents), rendered directly instead of
  // being recomputed client-side (GT1-439).
  subtotalCents: CentsSchema,
  donationCents: CentsSchema,
  feeCoverageCents: CentsSchema,
  // The bidder's/operator's stored fee-coverage decision (migration 0094).
  // null = never answered, so a surface may still apply its own default;
  // false = explicitly declined and must NOT be re-derived back on from
  // feeCoverageCents.
  feeCoverageOptedIn: z.boolean().nullable(),
  totalCents: CentsSchema,
  taxDeductibleCents: CentsSchema,
  refundedCents: CentsSchema,
  // Cents of refunds submitted to NeonPay but not yet confirmed succeeded.
  // Async terminal/ACH refunds sit in 'pending' until the charge.refunded
  // webhook lands.
  pendingRefundCents: CentsSchema,
  refundSettledCents: CentsSchema.optional(),
  refundPendingCents: CentsSchema.optional(),
  owedBackCents: CentsSchema.optional(),
  inFlightCharge: SettlementInFlightChargeSchema.nullable().optional(),
  // "Visa ····4242" | "Bank account ····6789" | "Apple Pay" | "Cash" | null.
  // Free-form: the settlement UI renders it verbatim and has no methodType of
  // its own to fall back on, which is why the rail vocabulary is resolved
  // server-side (GT1-488) rather than client-side.
  paymentMethodLabel: z.string().nullable(),
  /**
   * The method this bidder has ON FILE — "Visa ····4242", "Bank account" — or
   * null when there is nothing stored.
   *
   * A different question from `paymentMethodLabel`, and the two must not be
   * collapsed. "Is there something we can charge?" is true before any money
   * moves; "what paid for this?" is false until a charge settles. Reading the
   * payment label for the on-file question showed a bidder with a card on file
   * as "None on file" until their invoice was collected, and hid the Charge
   * action that depends on it.
   */
  storedMethodLabel: z.string().nullable(),
  storedPaymentMethod: StoredCardSchema.nullable().optional(),
  // GT1-495 — the stored card's brand as the fee tables name it, for the
  // donor-covered-fee quote. null means "no brand known" and must be read as
  // "use the card BASE rate", not as an error: a stored ACH or wallet has no
  // brand, and a card row written before enrichment ran may have none either.
  paymentMethodBrand: DcfCardBrandSchema.nullable(),
  /**
   * Q-UI-8 — attempted method + decline reason of the most recent failed
   * charge. Powers the attention feed's "{method} declined · {reason}" meta
   * and the drawer's hard-decline retry suppression. Null when none.
   */
  lastFailure: InvoiceLastFailureSchema.nullable(),
  lineItems: z.array(SettlementLineItemSchema),
  receiptPdfUrl: z.string().nullable(),
  receiptPdfGeneratedAt: Iso8601Schema.nullable(),
  crmCampaignId: z.string().nullable(),
  crmCampaignName: z.string().nullable(),
  crmSyncStatus: CrmSyncStatusSchema.nullable(),
  neonPayTransactionId: z.string().nullable()
});
var OutstandingOfferSchema = z.object({
  offerPublicId: UuidSchema,
  // The offered lot: exactly one of the pair is set.
  auctionItemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  itemTitle: z.string(),
  bidderUuid: UuidSchema,
  bidderName: z.string(),
  amountCents: CentsSchema,
  expiresAt: Iso8601Schema,
  // Open invoice the accept would append to, when one exists.
  openInvoiceUuid: UuidSchema.nullable(),
  openInvoiceNumber: z.string().nullable()
}).refine(namesExactlyOneLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] });
var StrandedLotReasonSchema = z.enum([
  "released",
  "offer_declined",
  "offer_lapsed",
  "offer_cancelled",
  "refunded_unreleased"
]);
var SettlementLotContext = {
  lotNumber: z.number().int().positive().nullable().optional(),
  fmvCents: CentsSchema.nullable().optional(),
  photo: PhotoVariantSchema.nullable().optional(),
  bidStack: z.array(BidStackEntrySchema).optional(),
  saleState: SaleStateSchema.optional()
};
var StrandedLotSchema = z.object({
  ...SettlementLotContext,
  // The stranded lot: exactly one of the pair is set.
  auctionItemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  itemTitle: z.string(),
  amountCents: CentsSchema,
  reason: StrandedLotReasonSchema,
  // Invoice the lot still sits on (released/refunded line), when any.
  invoiceUuid: UuidSchema.nullable(),
  invoiceNumber: z.string().nullable()
}).refine(namesExactlyOneLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] });
var UninvoicedLotSchema = z.object({
  ...SettlementLotContext,
  auctionItemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  itemTitle: z.string(),
  winningBidCents: CentsSchema,
  bidderUuid: UuidSchema,
  bidderName: z.string(),
  closedAt: Iso8601Schema
}).refine(namesExactlyOneLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] });
var UnbidLotSchema = z.object({
  ...SettlementLotContext,
  auctionItemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  itemTitle: z.string(),
  closedAt: Iso8601Schema.nullable()
}).refine(namesExactlyOneLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] });
var RefundFailureSchema = z.object({
  obligationPublicId: UuidSchema,
  invoiceUuid: UuidSchema,
  invoiceNumber: z.string(),
  /**
   * The invoice line the refund failed on — the only identifier that names
   * every failure.
   *
   * `auctionItemId` / `bundleId` below are both null on a donation or
   * donor-covered-fee line, so a client resolving a failure through them can
   * only ever act on lot failures: a fee refund that failed reported "that lot
   * is no longer on this invoice" and could not be retried at all. Match on
   * this.
   */
  lineItemUuid: UuidSchema,
  // The lot the failed line charged for, when it charged for one. At most one
  // of the pair is set.
  auctionItemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  itemTitle: z.string().nullable(),
  amountCents: CentsSchema,
  lastError: z.string(),
  failedAt: Iso8601Schema
}).refine((f) => f.auctionItemId === null || f.bundleId === null, {
  message: "at most one of auctionItemId / bundleId may be set",
  path: ["bundleId"]
});
var SettlementBidderSummarySchema = z.object({
  bidderUuid: UuidSchema,
  bidderName: z.string(),
  bidderEmail: z.string().nullable(),
  // Null when the bidder holds no open (pending|failed) invoice.
  openInvoiceUuid: UuidSchema.nullable(),
  openInvoiceNumber: z.string().nullable(),
  hasOutstandingOffer: z.boolean(),
  storedPaymentMethod: StoredCardSchema.nullable().optional()
});
var CustodyEventKindSchema = z.enum([
  "won_at_close",
  "unsold_at_close",
  "offered",
  "accepted",
  "declined",
  "expired",
  "cancelled",
  "reassigned_now",
  "sold_to_bidder",
  "refund_released",
  "refunded",
  "released",
  "checkout_declined",
  "sale_cancelled"
]);
var CustodyEventSchema = z.object({
  kind: CustodyEventKindSchema,
  at: Iso8601Schema,
  // Bidder the event is about, when known. Never an admin — the trail records
  // what happened to the lot, not who clicked.
  bidderUuid: UuidSchema.nullable(),
  bidderName: z.string().nullable(),
  amountCents: CentsSchema.nullable(),
  // Free-form note the trail may carry (e.g. which invoice an accept appended to).
  note: z.string().nullable(),
  // Origin chip when known (e.g. "Offer sent from the console").
  origin: z.string().nullable()
}).superRefine((event, context) => {
  if (event.kind === "sold_to_bidder" && (event.amountCents === null || event.amountCents === 0)) {
    context.addIssue({ code: "custom", path: ["amountCents"], message: "a sale requires a positive amount" });
  }
});
var ItemCustodyHistorySchema = z.object({
  // The lot this trail is about: exactly one of the pair is set.
  auctionItemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  disposition: ItemDispositionSchema,
  needsAdminAction: z.boolean(),
  waitingOnBidder: z.boolean(),
  // Oldest first. Newest entry is where the lot stands now.
  events: z.array(CustodyEventSchema)
}).refine(namesExactlyOneLot, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] });
var SettlementPayloadSchema = z.object({
  // Invoice-grain roster. A bidder with two settled invoices appears twice.
  items: z.array(SettlementInvoiceSchema),
  bidders: z.array(SettlementBidderSummarySchema),
  outstandingOffers: z.array(OutstandingOfferSchema),
  strandedLots: z.array(StrandedLotSchema),
  uninvoicedLots: z.array(UninvoicedLotSchema).optional(),
  unbidLots: z.array(UnbidLotSchema).optional(),
  refundFailures: z.array(RefundFailureSchema),
  eventMoney: EventMoneySchema.optional()
});
var CampaignCreateInputSchema = z.object({
  name: z.string().min(1).max(255)
});
var CampaignCreateResultSchema = z.object({
  id: z.string(),
  name: z.string()
});
var TerminalStatusSchema = z.enum([
  "active",
  "deactivated",
  "not_configured"
]);
var TerminalSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  location: z.string().nullable(),
  deviceType: z.string().nullable(),
  serialNumber: z.string().nullable(),
  status: TerminalStatusSchema
});
var TerminalListResponseSchema = z.object({
  terminals: z.array(TerminalSchema),
  // Whether the resolved terminal ('ipp') merchant runs against Neon Pay's
  // sandbox. Drives the admin-web "simulate tap" dev controls — those may only
  // render for a genuinely sandboxed merchant, never a live one. False when no
  // terminal merchant is configured (empty list).
  sandbox: z.boolean()
});
var TerminalChargeInputSchema = z.object({
  terminalId: z.number().int().positive(),
  idempotencyKey: z.string().min(1).max(128)
});
var TerminalCancelResultSchema = z.object({
  invoice: InvoiceSchema
});
var TerminalStatusResultSchema = z.object({
  status: InvoicePaymentStatusSchema,
  failureCode: z.string().nullable()
});
var MerchantPreviewChangeInputSchema = z.object({
  // The slot(s) the admin is about to change. Duplicates are tolerated;
  // the server dedupes.
  slots: z.array(MerchantLockSlotSchema).min(1).max(4)
});
var AffectedEventSchema = z.object({
  id: UuidSchema,
  // auction event public uuid
  name: z.string(),
  slot: MerchantLockSlotSchema,
  lockedMerchantPublicId: UuidSchema,
  // orgMerchantAccounts.publicId — never the internal id
  lockedMerchantName: z.string()
  // neonPayMerchantId (opaque to admin-web; used for display)
});
var MerchantPreviewChangeResponseSchema = z.object({
  affectedEvents: z.array(AffectedEventSchema)
});
var FeatureFlagOrgOverrideSchema = z.object({
  salesforceId: z.string(),
  orgName: z.string(),
  enabled: z.boolean()
});
var FeatureFlagScheduledTransitionSchema = z.object({
  scheduledAt: Iso8601Schema,
  targetEnabled: z.boolean()
});
var FeatureFlagSchema = z.object({
  key: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  sandboxOnly: z.boolean(),
  orgOverrides: z.array(FeatureFlagOrgOverrideSchema),
  // Single pending ON/OFF transition for this flag (global enabled bit), or null.
  scheduledTransition: FeatureFlagScheduledTransitionSchema.nullable(),
  updatedAt: z.string()
});
var FeatureFlagUpdateSchema = z.object({
  enabled: z.boolean().optional(),
  description: z.string().min(1).optional(),
  sandboxOnly: z.boolean().optional()
}).strict();
var FeatureFlagOverrideInputSchema = z.object({
  enabled: z.boolean()
}).strict();
var FeatureFlagScheduleInputSchema = z.object({
  scheduledAt: Iso8601Schema,
  targetEnabled: z.boolean()
}).strict();
var CLIENT_FLAG_KEYS = [
  "settlement.move-to-next-bidder",
  "settlement.activity-feed",
  // Card-present terminal payments (GT1-327). Gates the "Credit Card Terminal"
  // method in the settlement console; orgs opt in via a supervisor override.
  "payments.terminal",
  // Admin bid-void (GT1-442). Gates the void control in the settlement console;
  // an unreleased dark-launch feature, off until an org opts in via a supervisor
  // override. Not a bidder flag — already-voided bids still render on bidder-web.
  "bids.void",
  "bids.place-for-bidder",
  "settlement.sell-to-bidder"
];
var EvaluatedClientFlagsSchema = z.object({
  "settlement.move-to-next-bidder": z.boolean(),
  "settlement.activity-feed": z.boolean(),
  "payments.terminal": z.boolean(),
  "bids.void": z.boolean(),
  "bids.place-for-bidder": z.boolean(),
  "settlement.sell-to-bidder": z.boolean()
});
var BidderSegmentSchema = z.enum([
  // Every registered bidder, activated or not. Deliberately wider than the
  // trigger-driven broadcast audience: an unactivated registrant is exactly who
  // a "finish your registration" or "we open tomorrow" email is for.
  "all",
  // Holds the high bid on at least one lot. Pre-close this reads "currently
  // leading", post-close "won" — the same predicate answers both.
  "winning",
  // Bid on at least one lot where they do NOT hold the high bid. Per-lot, not
  // per-bidder: someone leading lot A and outbid on lot B is in this segment as
  // well as `winning`, because they really were outbid on something and are
  // worth inviting back. Post-close it reads "bid and didn't win".
  "outbid",
  // Has an invoice awaiting payment. Excludes charges already submitted to the
  // processor, so a donor mid-settlement is never asked to pay again.
  "unpaid",
  // Has a settled invoice.
  "paid"
]);
var SegmentCountSchema = z.object({
  segment: BidderSegmentSchema,
  // Bidders matching the segment.
  total: z.number().int().nonnegative(),
  // Of those, the ones who will actually be emailed. Lower than `total`
  // whenever someone in the segment has unsubscribed or hard-bounced.
  emailEligible: z.number().int().nonnegative()
});
var BroadcastAudienceResponseSchema = z.object({
  segments: z.array(SegmentCountSchema)
});
var TiptapDocSchema = z.object({
  type: z.literal("doc"),
  content: z.array(z.unknown())
});
var SubjectSchema = z.string().trim().min(1).max(998);
var BroadcastSendInputSchema = z.object({
  subject: SubjectSchema,
  tiptapJson: TiptapDocSchema,
  segment: BidderSegmentSchema
});
var BroadcastStatusSchema = z.enum([
  "queued",
  "sending",
  "sent",
  "partially_failed",
  "failed"
]);
var BroadcastSendResultSchema = z.object({
  // Correlates to notification_logs.broadcast_id for every row of this send.
  broadcastId: z.string().uuid(),
  status: BroadcastStatusSchema,
  // Audience size resolved at enqueue. The worker re-resolves at delivery, so
  // treat the pre-send number shown in the UI as advisory and this one as the
  // figure the send was actually committed against.
  recipientCount: z.number().int().nonnegative(),
  optedOutCount: z.number().int().nonnegative(),
  // True when an idempotency key matched an earlier send: nothing was queued a
  // second time and the original result is being replayed.
  replayed: z.boolean()
});
var BroadcastTestSendInputSchema = z.object({
  subject: SubjectSchema,
  tiptapJson: TiptapDocSchema,
  recipients: z.array(z.string().email()).min(1).max(5)
});
var BroadcastTestSendResultSchema = z.object({
  sent: z.number().int().nonnegative()
});
var BroadcastComposerConfigSchema = z.object({
  // Full resolution set — needed to parse merge-tag pills already in a draft.
  mergeTags: z.array(z.unknown()),
  // Curated subset offered in the picker.
  insertableMergeTags: z.array(z.unknown()),
  // The organization's name, for the frozen CAN-SPAM footer. Never the event
  // name: the footer identifies the sender, not the auction.
  orgName: z.string(),
  brandTokens: z.record(z.string(), z.unknown())
});
var BroadcastSummarySchema = z.object({
  broadcastId: z.string().uuid(),
  segment: BidderSegmentSchema,
  subject: z.string(),
  status: BroadcastStatusSchema,
  recipientCount: z.number().int().nonnegative().nullable(),
  optedOutCount: z.number().int().nonnegative().nullable(),
  createdAt: Iso8601Schema,
  sendCompletedAt: Iso8601Schema.nullable()
});
function lotIdsNamed(r) {
  return namesExactlyOneLot({ auctionItemId: r.itemId, bundleId: r.bundleId });
}
var ReassignModeSchema = z.enum(["offer", "now"]);
var PlacementPriceBasisSchema = z.enum(["bid", "fmv", "custom"]);
var PlacementCollectSchema = z.enum(["charge", "notify"]);
var PlacementCollectionOutcomeSchema = z.enum([
  "charged",
  "submitted",
  "declined",
  "refused",
  "unconfirmed",
  "notified"
]);
var PlacementCollectionSchema = z.object({
  outcome: PlacementCollectionOutcomeSchema,
  chargedTotalCents: CentsSchema.optional(),
  reason: z.string().optional()
});
var PlacementFields = {
  targetBidderUuid: UuidSchema,
  mode: ReassignModeSchema,
  notify: z.boolean(),
  priceBasis: PlacementPriceBasisSchema.optional(),
  priceCents: z.number().int().positive().optional(),
  collect: PlacementCollectSchema.optional()
};
function validatePlacement(request, context) {
  if (request.priceBasis === void 0 !== (request.priceCents === void 0)) {
    context.addIssue({
      code: "custom",
      path: ["priceCents"],
      message: "price basis and amount are required together"
    });
  }
  if (request.mode === "offer" && request.collect !== void 0) {
    context.addIssue({ code: "custom", path: ["collect"], message: "offers do not collect payment" });
  }
}
var ReassignItemRequestSchema = z.object({
  lineItemPublicId: UuidSchema,
  ...PlacementFields
}).superRefine(validatePlacement);
var ReassignLotRequestSchema = z.object({
  lot: LotRefSchema,
  ...PlacementFields,
  priceBasis: PlacementPriceBasisSchema,
  priceCents: z.number().int().positive()
}).strict().superRefine(validatePlacement);
var PlacementRequestSchema = z.union([ReassignItemRequestSchema, ReassignLotRequestSchema]);
var ReassignItemResultSchema = z.object({
  mode: ReassignModeSchema,
  // True when this request replayed an earlier commit with the same
  // Idempotency-Key. Nothing was written and nobody was notified a second
  // time; the payload is the original outcome verbatim.
  replayed: z.boolean(),
  itemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  lineItemPublicId: UuidSchema.nullable(),
  targetBidderUuid: UuidSchema,
  // Populated in `offer` mode only. `expiresAt` is the DB-returned deadline the
  // server will actually enforce, so the UI never promises a window it won't honour.
  offer: z.object({ publicId: UuidSchema, expiresAt: Iso8601Schema }).nullable(),
  // Populated in `now` mode only — the invoice the item now sits on. Either
  // the target's existing OPEN invoice (appended to) or one minted for this
  // placement: ADR-012 replaced the one-invoice-per-bidder constraint with
  // uniqueness over the open set, so a bidder who has already paid simply gets
  // a second invoice rather than being refused the lot. 'new' names the item's
  // new home, not necessarily a new record — compare against the candidate's
  // `openInvoiceUuid` to tell which of the two happened.
  newInvoiceUuid: UuidSchema.nullable(),
  // A pending offer this action cancelled, if there was one.
  supersededOfferPublicId: UuidSchema.nullable(),
  notificationsSent: z.boolean(),
  amountCents: CentsSchema.optional(),
  collection: PlacementCollectionSchema.optional()
}).refine(lotIdsNamed, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] });
var CancelSaleRequestSchema = z.object({
  notify: z.boolean().default(true)
});
var CancelSaleResultSchema = z.object({
  itemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  replayed: z.boolean(),
  notificationsSent: z.boolean()
}).refine(lotIdsNamed, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] });
var ReassignTargetConsequenceSchema = z.enum(["appends", "new_invoice"]);
var ChargeEligibilitySchema = z.discriminatedUnion("eligible", [
  z.object({ eligible: z.literal(true) }),
  z.object({ eligible: z.literal(false), reason: z.enum(["not_auto_charge", "no_card"]) })
]);
var PlacementOpenInvoiceSchema = z.object({
  number: z.string(),
  totalCents: CentsSchema,
  otherUnpaidLineCount: z.number().int().nonnegative()
});
var ReassignTargetSchema = z.object({
  bidderUuid: UuidSchema,
  name: z.string(),
  email: z.string().nullable().optional(),
  cardLabel: z.string().nullable().optional(),
  chargeEligibility: ChargeEligibilitySchema.optional(),
  bidCents: CentsSchema.nullable().optional(),
  openInvoice: PlacementOpenInvoiceSchema.nullable().optional(),
  // Best surviving bid on this lot, or null when the candidate never bid
  // (charge then falls to FMV / line amount at confirm time).
  amountCents: z.number().int().nonnegative().nullable(),
  openInvoiceUuid: UuidSchema.nullable(),
  // Human-readable identity for the consequence copy ("Appends to INV-1041").
  // Null exactly when openInvoiceUuid is null.
  openInvoiceNumber: z.string().nullable(),
  consequence: ReassignTargetConsequenceSchema
});
var ReassignTargetsResponseSchema = z.object({
  itemId: z.number().int().positive().nullable(),
  bundleId: z.number().int().positive().nullable(),
  targets: z.array(ReassignTargetSchema)
}).refine(lotIdsNamed, { message: EXACTLY_ONE_LOT_MESSAGE, path: ["bundleId"] });
var RefundObligationStatusSchema = z.enum([
  "pending",
  "submitted",
  "succeeded",
  "void",
  "failed"
]);
var RefundAndReleaseInputSchema = z.object({
  // Public line-item ids (uuids). Internal numeric ids never cross the wire.
  // Deduped so a repeated id cannot double-release the same lot under one key.
  lineItemUuids: z.array(UuidSchema).min(1).refine((ids) => new Set(ids).size === ids.length, "lineItemUuids must be unique"),
  reason: z.string().min(1).max(500)
});
var ReleasedLineSchema = z.object({
  lineItemUuid: UuidSchema,
  // 0 for unpaid or already-fully-refunded lines; still released.
  refundedCents: CentsSchema,
  obligationStatus: RefundObligationStatusSchema
});
var RefundAndReleaseResultSchema = z.object({
  // Settlement projection of the invoice after the releases (may be `void` when
  // the last unpaid lot left with nothing else owed).
  invoice: SettlementInvoiceSchema,
  released: z.array(ReleasedLineSchema)
});
var METRICS_PAGE_SIZE = 25;
var METRICS_CSV_ROW_LIMIT = 5e3;
var METRICS_MAX_RANGE_MONTHS = 24;
var METRICS_LOOKUP_LIMIT = 20;
var SupervisorMetricsMoneyEnvironmentSchema = z.enum(["live", "sandbox", "test", "offline"]);
var SupervisorMetricsChannelSchema = z.enum(["auto_charge", "bidder_checkout", "admin"]);
var SupervisorMetricsStoredMethodSchema = z.enum(["stored", "entered"]);
var SupervisorMetricsBucketSchema = z.enum(["live", "upcoming", "closed"]);
var SupervisorMetricsUpcomingStatusSchema = z.enum(["all", "scheduled", "drafts"]);
var SupervisorMetricsTabSchema = z.enum(["upcoming_live", "closed"]);
var SupervisorMetricsGroupBySchema = z.enum([
  "method",
  "channel",
  "environment",
  "storedMethod",
  "checkoutMode"
]);
var SupervisorMetricsCategorySchema = z.enum(["real", "no_bids", "likely_test"]);
var SupervisorMetricsInclusionSchema = z.enum(["include", "exclude"]);
var SupervisorMetricsDurationBucketSchema = z.enum([
  "under_1_day",
  "1_to_3_days",
  "3_to_7_days",
  "1_to_2_weeks",
  "over_2_weeks"
]);
var DEFAULT_METRICS_TIMEZONE = "America/Chicago";
var ZONE_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_+\-/]*$/;
function isIntlTimezone(value) {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}
var SupervisorMetricsTimezoneSchema = z.string().refine((value) => ZONE_NAME_PATTERN.test(value) && isIntlTimezone(value), { message: "expected an IANA time zone name" });
var PageFilterObjectSchema = z.object({
  orgSalesforceIds: z.array(z.string()).optional(),
  eventPublicIds: z.array(UuidSchema).optional(),
  from: IsoDateSchema,
  to: IsoDateSchema,
  includeSandboxOrgs: z.boolean().default(false),
  includeLikelyTests: z.boolean().default(false),
  newOrgsOnly: z.boolean().default(false),
  timezone: SupervisorMetricsTimezoneSchema.default(DEFAULT_METRICS_TIMEZONE)
});
var DateRangeBoundsSchema = PageFilterObjectSchema.pick({ from: true, to: true });
function isValidMetricsRange(value) {
  const parsed = DateRangeBoundsSchema.safeParse(value);
  if (!parsed.success) return false;
  const input = parsed.data;
  const start = /* @__PURE__ */ new Date(`${input.from}T00:00:00Z`);
  const end = /* @__PURE__ */ new Date(`${input.to}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  const lastDay = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + METRICS_MAX_RANGE_MONTHS + 1, 0));
  const limit = new Date(Date.UTC(
    lastDay.getUTCFullYear(),
    lastDay.getUTCMonth(),
    Math.min(start.getUTCDate(), lastDay.getUTCDate())
  ));
  return end >= start && end <= limit;
}
var rangeRefinement = { message: "date range must be ordered and at most 24 months", path: ["to"] };
var SupervisorMetricsPageFilterSchema = PageFilterObjectSchema.refine(isValidMetricsRange, rangeRefinement);
var SupervisorMetricsPaymentFilterSchema = z.object({
  moneyEnvironments: z.array(SupervisorMetricsMoneyEnvironmentSchema).default(["live", "offline"]),
  paymentMethods: z.array(PaymentMethodSchema).optional(),
  channels: z.array(SupervisorMetricsChannelSchema).optional(),
  storedMethod: SupervisorMetricsStoredMethodSchema.optional()
});
var SupervisorMetricsSelectionSchema = z.object({ selectedEventPublicId: UuidSchema.optional() });
var ARRAY_QUERY_KEYS = /* @__PURE__ */ new Set([
  "orgSalesforceIds",
  "eventPublicIds",
  "moneyEnvironments",
  "paymentMethods",
  "channels"
]);
var BOOLEAN_QUERY_KEYS = /* @__PURE__ */ new Set([
  "includeSandboxOrgs",
  "includeLikelyTests",
  "newOrgsOnly",
  "showStaleDrafts"
]);
function normalizeQueryValue(key, value) {
  if (ARRAY_QUERY_KEYS.has(key)) {
    if (Array.isArray(value)) return value.flatMap((part) => typeof part === "string" ? part.split(",") : [part]);
    return typeof value === "string" ? value.split(",") : value;
  }
  if (Array.isArray(value)) return value.length === 1 ? normalizeQueryValue(key, value[0]) : value;
  if (BOOLEAN_QUERY_KEYS.has(key) && value === "true") return true;
  if (BOOLEAN_QUERY_KEYS.has(key) && value === "false") return false;
  return value;
}
function normalizeSupervisorMetricsQuery(input) {
  if (typeof input === "string") return normalizeSupervisorMetricsQuery(new URLSearchParams(input));
  if (!input || typeof input !== "object") return input;
  const entries = input instanceof URLSearchParams ? [...input.entries()] : Object.entries(input);
  const grouped = {};
  for (const [rawKey, value] of entries) {
    const key = rawKey.replace(/\[\]$/, "");
    if (Object.prototype.hasOwnProperty.call(grouped, key)) grouped[key] = [grouped[key], value].flat();
    else grouped[key] = value;
  }
  return Object.fromEntries(Object.entries(grouped).map(([key, value]) => [key, normalizeQueryValue(key, value)]));
}
var OrgSortFieldSchema = z.enum([
  "name",
  "firstAuctionAt",
  "liveCount",
  "upcomingCount",
  "closedCount",
  "hiddenLikelyTestCount",
  "registeredBidders",
  "bidsPlaced",
  "soldTotalCents",
  "sellThroughRate",
  "valueRaisedRate",
  "expectedRevenueCents",
  "collectedCents",
  "outstandingCents"
]);
var EventSortFieldSchema = z.enum([
  "name",
  "orgName",
  "auctionOpenAt",
  "auctionCloseAt",
  "closedAt",
  "registeredBidders",
  "activeItems",
  "catalogValueCents",
  "averageFmvCents",
  "savedPaymentMethodRate",
  "bidsPlaced",
  "totalBids",
  "participationRate",
  "bidAmountCommittedCents",
  "averageBidsPerItem",
  "itemsWithNoBids",
  "soldTotalCents",
  "sellThroughRate",
  "averageWinningBidCents",
  "valueRaisedRate",
  "expectedRevenueCents",
  "collectedCents",
  "clearingCents",
  "refundedCents",
  "owedBackCents",
  "outstandingCents",
  "declinedCents",
  "settledInvoiceCount",
  "donationCents",
  "feesCoveredCents",
  "medianTimeToPaySeconds",
  "declineRate",
  "acceptedOfferCount"
]);
function makeSortSchema(fields) {
  const options = fields.options.flatMap((field) => [field, `-${field}`]);
  return z.enum(options);
}
var SupervisorMetricsOrgSortSchema = makeSortSchema(OrgSortFieldSchema);
var SupervisorMetricsEventSortSchema = makeSortSchema(EventSortFieldSchema);
var pagination = { page: z.coerce.number().int().positive().default(1) };
var pageQuery = (shape) => z.preprocess(
  normalizeSupervisorMetricsQuery,
  PageFilterObjectSchema.extend(shape).refine(isValidMetricsRange, rangeRefinement)
);
var SupervisorMetricsOverviewQuerySchema = pageQuery({});
var SupervisorMetricsOrgsQuerySchema = pageQuery({
  ...SupervisorMetricsPaymentFilterSchema.shape,
  ...pagination,
  sort: SupervisorMetricsOrgSortSchema.default("name")
});
var SupervisorMetricsEventsQuerySchema = pageQuery({
  ...SupervisorMetricsPaymentFilterSchema.shape,
  ...pagination,
  bucket: SupervisorMetricsBucketSchema.default("live"),
  upcomingStatus: SupervisorMetricsUpcomingStatusSchema.default("all"),
  showStaleDrafts: z.boolean().default(false),
  sort: SupervisorMetricsEventSortSchema.optional()
});
var SupervisorMetricsNeedsAttentionQuerySchema = pageQuery({ tab: SupervisorMetricsTabSchema.default("upcoming_live") });
var SupervisorMetricsSettingsQuerySchema = pageQuery(SupervisorMetricsSelectionSchema.shape);
var SupervisorMetricsClosedQuerySchema = pageQuery(SupervisorMetricsPaymentFilterSchema.shape);
var SupervisorMetricsPaymentsQuerySchema = pageQuery({
  ...SupervisorMetricsPaymentFilterSchema.shape,
  ...SupervisorMetricsSelectionSchema.shape,
  groupBy: SupervisorMetricsGroupBySchema.default("method")
});
var SupervisorMetricsDurationQuerySchema = pageQuery(SupervisorMetricsSelectionSchema.shape);
var SupervisorMetricsOrgLookupQuerySchema = z.preprocess(
  normalizeSupervisorMetricsQuery,
  z.object({ q: z.string().trim().max(200).default("") })
);
var SupervisorMetricsEventLookupQuerySchema = z.preprocess(
  normalizeSupervisorMetricsQuery,
  z.object({ q: z.string().trim().max(200).default(""), orgSalesforceIds: z.array(z.string()).optional() })
);
var SupervisorMetricsOverrideParamsSchema = z.object({ eventPublicId: UuidSchema });
var SupervisorMetricsOverrideRequestSchema = z.object({ inclusion: SupervisorMetricsInclusionSchema });
var CountSchema2 = z.number().int().nonnegative();
var AmountSchema = z.number().int();
var RateSchema = z.number().nonnegative().nullable();
var AverageCentsSchema = AmountSchema.nullable();
var GeneratedSchema = z.object({ generatedAt: Iso8601Schema });
var SupervisorMetricsReasonSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("name_match"), source: z.enum(["name", "slug"]), matchedTerm: z.string() }),
  z.object({
    kind: z.literal("dry_run"),
    uniqueBidders: CountSchema2,
    relatedEventPublicId: UuidSchema,
    relatedEventName: z.string(),
    relatedAuctionOpenAt: Iso8601Schema,
    daysUntilRelatedEvent: z.number().int()
  })
]);
var SupervisorMetricsOverrideSchema = z.object({ inclusion: SupervisorMetricsInclusionSchema, setByName: z.string() });
var SupervisorMetricsEventIdentitySchema = z.object({
  publicId: UuidSchema,
  name: z.string(),
  slug: z.string(),
  orgSalesforceId: z.string(),
  orgName: z.string(),
  orgSlug: z.string().nullable(),
  isSandbox: z.boolean(),
  status: AuctionEventStatusSchema,
  isPaused: z.boolean(),
  category: SupervisorMetricsCategorySchema,
  reason: SupervisorMetricsReasonSchema.nullable(),
  override: SupervisorMetricsOverrideSchema.nullable()
});
var SupervisorMetricsEventFiguresSchema = z.object({
  registeredBidders: CountSchema2,
  walkedUpBidders: CountSchema2,
  walkedUpRate: RateSchema,
  uniqueBidders: CountSchema2,
  participationRate: RateSchema,
  savedPaymentMethodBidders: CountSchema2,
  savedPaymentMethodRate: RateSchema,
  activeItems: CountSchema2,
  catalogValueCents: AmountSchema,
  averageFmvCents: AverageCentsSchema,
  bidsPlaced: CountSchema2,
  totalBids: CountSchema2,
  bidAmountCommittedCents: AmountSchema,
  averageBidsPerItem: RateSchema,
  itemsWithNoBids: CountSchema2,
  itemsWithNoBidsRate: RateSchema,
  soldItems: CountSchema2,
  unsoldItems: CountSchema2,
  soldTotalCents: AmountSchema,
  sellThroughRate: RateSchema,
  averageWinningBidCents: AverageCentsSchema,
  valueRaisedRate: RateSchema
});
var SupervisorMetricsInvoiceStatesSchema = z.object({
  paid: CountSchema2,
  clearing: CountSchema2,
  declined: CountSchema2,
  awaitingPayment: CountSchema2,
  refunded: CountSchema2
});
var SupervisorMetricsMoneySchema = z.object({
  expectedRevenueCents: AmountSchema,
  collectedCents: AmountSchema,
  clearingCents: AmountSchema,
  unconfirmedCents: AmountSchema,
  refundedCents: AmountSchema,
  refundSettlingCents: AmountSchema,
  owedBackCents: AmountSchema,
  outstandingCents: AmountSchema,
  outstandingInvoiceCount: CountSchema2,
  declinedCents: AmountSchema,
  declinedInvoiceCount: CountSchema2,
  settledInvoiceCount: CountSchema2,
  nonVoidInvoiceCount: CountSchema2,
  donationCents: AmountSchema,
  donationCount: CountSchema2,
  feesCoveredCents: AmountSchema,
  feeCoverageOptInRate: RateSchema,
  medianTimeToPaySeconds: z.number().nonnegative().nullable(),
  paidWithin7DaysRate: RateSchema,
  declineRate: RateSchema,
  acceptedOfferCount: CountSchema2,
  acceptedOfferCents: AmountSchema,
  invoiceStates: SupervisorMetricsInvoiceStatesSchema
});
var SupervisorMetricsAttentionCountsSchema = z.object({ upcomingLive: CountSchema2, closed: CountSchema2 });
var SupervisorMetricsOverviewResponseSchema = GeneratedSchema.extend({
  auctionCount: CountSchema2,
  liveCount: CountSchema2,
  upcomingCount: CountSchema2,
  closedCount: CountSchema2,
  organizationCount: CountSchema2,
  hiddenLikelyTestCount: CountSchema2,
  registeredBidders: CountSchema2,
  walkedUpBidders: CountSchema2,
  walkedUpRate: RateSchema,
  bidsPlaced: CountSchema2,
  totalBids: CountSchema2,
  uniqueBidders: CountSchema2,
  participationRate: RateSchema,
  soldTotalCents: AmountSchema,
  needsAttentionCounts: SupervisorMetricsAttentionCountsSchema,
  money: z.object({
    expectedRevenueCents: AmountSchema,
    collectedCents: AmountSchema,
    clearingCents: AmountSchema,
    outstandingCents: AmountSchema,
    outstandingInvoiceCount: CountSchema2
  }).nullable()
});
var SupervisorMetricsOrgMoneySchema = z.object({
  expectedRevenueCents: AmountSchema,
  collectedCents: AmountSchema,
  clearingCents: AmountSchema,
  outstandingCents: AmountSchema,
  outstandingInvoiceCount: CountSchema2
});
var SupervisorMetricsOrgFiguresSchema = z.object({
  liveCount: CountSchema2,
  upcomingCount: CountSchema2,
  closedCount: CountSchema2,
  hiddenLikelyTestCount: CountSchema2,
  registeredBidders: CountSchema2,
  bidsPlaced: CountSchema2,
  soldTotalCents: AmountSchema,
  sellThroughRate: RateSchema,
  valueRaisedRate: RateSchema,
  money: SupervisorMetricsOrgMoneySchema.nullable()
});
var SupervisorMetricsOrgRowSchema = SupervisorMetricsOrgFiguresSchema.extend({
  salesforceId: z.string(),
  name: z.string(),
  orgSlug: z.string().nullable(),
  isSandbox: z.boolean(),
  firstAuctionAt: Iso8601Schema.nullable()
});
var SupervisorMetricsOrgsResponseSchema = GeneratedSchema.extend({
  rows: z.array(SupervisorMetricsOrgRowSchema),
  totals: SupervisorMetricsOrgFiguresSchema,
  totalCount: CountSchema2
});
var SupervisorMetricsEventTotalsSchema = SupervisorMetricsEventFiguresSchema.extend({
  auctionCount: CountSchema2,
  money: SupervisorMetricsMoneySchema.nullable()
});
var SupervisorMetricsEventRowSchema = SupervisorMetricsEventIdentitySchema.extend({
  ...SupervisorMetricsEventFiguresSchema.shape,
  auctionOpenAt: Iso8601Schema,
  auctionCloseAt: Iso8601Schema,
  closedAt: Iso8601Schema.nullable(),
  isStaleDraft: z.boolean(),
  // Draft registration fields are unknown rather than zero.
  registeredBidders: CountSchema2.nullable(),
  savedPaymentMethodBidders: CountSchema2.nullable(),
  money: SupervisorMetricsMoneySchema.nullable()
});
var SupervisorMetricsEventsResponseSchema = GeneratedSchema.extend({
  rows: z.array(SupervisorMetricsEventRowSchema),
  totals: SupervisorMetricsEventTotalsSchema,
  totalCount: CountSchema2
});
var SupervisorMetricsMerchantReasonSchema = z.enum(["no_merchant", "test_processor", "sandbox_processor"]);
var attentionBase = { event: SupervisorMetricsEventIdentitySchema };
var SupervisorMetricsAttentionItemSchema = z.discriminatedUnion("kind", [
  z.object({ ...attentionBase, kind: z.literal("has_not_opened"), params: z.object({ scheduledOpenAt: Iso8601Schema }) }),
  z.object({ ...attentionBase, kind: z.literal("draft_past_open"), params: z.object({ scheduledOpenAt: Iso8601Schema }) }),
  z.object({ ...attentionBase, kind: z.literal("draft_opening_soon"), params: z.object({ scheduledOpenAt: Iso8601Schema }) }),
  z.object({ ...attentionBase, kind: z.literal("has_not_closed"), params: z.object({ scheduledCloseAt: Iso8601Schema }) }),
  z.object({ ...attentionBase, kind: z.literal("no_sender_email"), params: z.object({}) }),
  z.object({ ...attentionBase, kind: z.literal("sender_not_connected"), params: z.object({}) }),
  z.object({ ...attentionBase, kind: z.literal("sender_not_verified"), params: z.object({ fallbackAddress: z.string().email().nullable() }) }),
  z.object({ ...attentionBase, kind: z.literal("cannot_take_online_payments"), params: z.object({ reason: SupervisorMetricsMerchantReasonSchema }) }),
  z.object({ ...attentionBase, kind: z.literal("closed_without_invoices"), params: z.object({ soldItems: CountSchema2 }) }),
  z.object({ ...attentionBase, kind: z.literal("closed_no_bids"), params: z.object({}) }),
  // Counts only: needs attention reaches supervisors without financial_metrics, so it carries no amounts.
  z.object({ ...attentionBase, kind: z.literal("payment_unconfirmed"), params: z.object({ invoiceCount: CountSchema2 }) }),
  z.object({ ...attentionBase, kind: z.literal("refund_failed"), params: z.object({ invoiceCount: CountSchema2 }) })
]);
var SupervisorMetricsNeedsAttentionResponseSchema = GeneratedSchema.extend({ items: z.array(SupervisorMetricsAttentionItemSchema) });
var SupervisorMetricsSettingKeySchema = z.enum([
  "checkout",
  "payment_method_requirement",
  "donor_covered_fees",
  "proxy_bidding",
  "access",
  "terms_and_conditions",
  "crm_auto_sync",
  "registration_limit"
]);
var SupervisorMetricsSettingValueSchema = z.enum(["auto_charge", "offer_default", "required", "allow_pay_later", "open", "invite_only", "on", "off"]);
var SupervisorMetricsSettingsResponseSchema = GeneratedSchema.extend({
  auctionCount: CountSchema2,
  organizationCount: CountSchema2,
  rows: z.array(z.object({
    key: SupervisorMetricsSettingKeySchema,
    values: z.array(z.object({ value: SupervisorMetricsSettingValueSchema, count: CountSchema2, rate: RateSchema }))
  })),
  currentOrgPaymentMethods: z.array(z.object({ method: PaymentMethodSchema, organizationCount: CountSchema2, rate: RateSchema }))
});
var SupervisorMetricsClosedResponseSchema = GeneratedSchema.extend({
  auctionCount: CountSchema2,
  noBidsAuctionCount: CountSchema2,
  soldTotalCents: AmountSchema,
  soldItems: CountSchema2,
  offeredItems: CountSchema2,
  sellThroughRate: RateSchema,
  valueRaisedRate: RateSchema,
  averageWinningBidCents: AverageCentsSchema,
  money: SupervisorMetricsMoneySchema.nullable()
});
var SupervisorMetricsPaymentGroupSchema = z.object({
  value: z.union([
    PaymentMethodSchema,
    SupervisorMetricsChannelSchema,
    SupervisorMetricsMoneyEnvironmentSchema,
    SupervisorMetricsStoredMethodSchema,
    AutoCheckoutModeSchema,
    z.string()
  ]).nullable()
});
var SupervisorMetricsPaymentFiguresSchema = z.object({
  chargeCount: CountSchema2,
  collectedCents: AmountSchema,
  clearingCents: AmountSchema,
  declinedChargeCount: CountSchema2,
  refundedCents: AmountSchema,
  declineRate: RateSchema
});
var SupervisorMetricsPaymentRowSchema = z.object({ group: SupervisorMetricsPaymentGroupSchema, money: SupervisorMetricsPaymentFiguresSchema.nullable() });
var SupervisorMetricsPaymentsResponseSchema = GeneratedSchema.extend({
  groupBy: SupervisorMetricsGroupBySchema,
  rows: z.array(SupervisorMetricsPaymentRowSchema),
  totals: z.object({ money: SupervisorMetricsPaymentFiguresSchema.nullable() }),
  totalCount: CountSchema2
});
var SupervisorMetricsDurationFiguresSchema = z.object({
  auctionCount: CountSchema2,
  sellThroughRate: RateSchema,
  valueRaisedRate: RateSchema,
  averageBidsPerItem: RateSchema
});
var SupervisorMetricsDurationRowSchema = SupervisorMetricsDurationFiguresSchema.extend({ bucket: SupervisorMetricsDurationBucketSchema });
var SupervisorMetricsDurationResponseSchema = GeneratedSchema.extend({
  rows: z.array(SupervisorMetricsDurationRowSchema),
  totals: SupervisorMetricsDurationFiguresSchema,
  totalCount: CountSchema2
});
var SupervisorMetricsOrgLookupRowSchema = z.object({ salesforceId: z.string(), name: z.string(), orgSlug: z.string().nullable(), isSandbox: z.boolean() });
var SupervisorMetricsEventLookupRowSchema = z.object({ publicId: UuidSchema, name: z.string(), slug: z.string(), status: AuctionEventStatusSchema, orgName: z.string() });
var SupervisorMetricsOrgLookupResponseSchema = GeneratedSchema.extend({ rows: z.array(SupervisorMetricsOrgLookupRowSchema).max(METRICS_LOOKUP_LIMIT) });
var SupervisorMetricsEventLookupResponseSchema = GeneratedSchema.extend({ rows: z.array(SupervisorMetricsEventLookupRowSchema).max(METRICS_LOOKUP_LIMIT) });
var SupervisorMetricsOverrideResponseSchema = GeneratedSchema.extend({ eventPublicId: UuidSchema, override: SupervisorMetricsOverrideSchema.nullable() });
var SUPERVISOR_CALENDAR_MAX_WINDOW_DAYS = 45;
var DAY_MS = 864e5;
var SupervisorCalendarQuerySchema = z.preprocess(normalizeSupervisorMetricsQuery, z.object({
  start: Iso8601Schema,
  end: Iso8601Schema,
  includeSandboxOrgs: z.boolean().default(false),
  includeLikelyTests: z.boolean().default(false)
}).refine((input) => {
  const span = Date.parse(input.end) - Date.parse(input.start);
  return span > 0 && span <= SUPERVISOR_CALENDAR_MAX_WINDOW_DAYS * DAY_MS;
}, { message: `window must be ordered and at most ${SUPERVISOR_CALENDAR_MAX_WINDOW_DAYS} days`, path: ["end"] }));
var SupervisorCalendarAuctionSchema = SupervisorMetricsEventIdentitySchema.extend({
  auctionOpenAt: Iso8601Schema,
  auctionCloseAt: Iso8601Schema,
  closedAt: Iso8601Schema.nullable()
});
var SupervisorCalendarResponseSchema = GeneratedSchema.extend({
  auctions: z.array(SupervisorCalendarAuctionSchema),
  hiddenLikelyTestCount: CountSchema2
});
var AuctionEventArchiveFilterSchema = z.enum(["exclude", "include", "only"]);
var AuctionEventListQuerySchema = z.object({
  status: AuctionEventStatusSchema.optional(),
  archived: AuctionEventArchiveFilterSchema.default("exclude"),
  cursor: z.string().max(256).optional(),
  limit: z.string().regex(/^\d+$/).optional()
});
var ArchivePreviewSchema = z.object({
  status: AuctionEventStatusSchema,
  registeredBidders: z.number().int().nonnegative(),
  bids: z.number().int().nonnegative(),
  biddersWithOpenInvoice: z.number().int().nonnegative()
});
var UnarchiveInputSchema = z.object({
  name: AuctionEventCreateInputSchema.shape.name.optional(),
  slug: AuctionEventCreateInputSchema.shape.slug.optional()
});
var AdminBidPlacementInputSchema = z.object({
  lot: LotRefSchema,
  bidderUuid: UuidSchema,
  amountCents: z.number().int().positive().max(MAX_BID_AMOUNT_CENTS),
  type: BidTypeSchema
}).strict();
var WishlistIdsResponseSchema = z.array(z.number().int().positive());
var WishlistToggleResponseSchema = z.object({
  added: z.boolean()
});
var PaymentMethodWireSchema = z.object({
  id: UuidSchema,
  brand: z.string().nullable(),
  last4: z.string().nullable(),
  expiration: z.string().nullable(),
  methodType: StoredMethodTypeSchema,
  isDefault: z.boolean(),
  createdAt: Iso8601Schema
});
var AddPaymentMethodInputSchema = z.object({
  paymentMethodToken: z.string().min(1),
  /**
   * GT1-488: the rail the token was minted on, as declared by the tokenizing
   * client. Omitted → the server infers card (promoting to google_pay /
   * apple_pay from getTokenDetails), which is the pre-GT1-488 behaviour and
   * can never yield `ach`. paypal/venmo are accepted by the schema (they are
   * StoredMethodTypes on the charge side) but rejected by the server's
   * storable-method guard, since neither produces a reusable token.
   */
  paymentMethodType: StoredMethodTypeSchema.optional()
});
var PAYMENT_METHOD_TOKEN_FIELD = "paymentMethodToken";
var PAYMENT_METHOD_DECLINED_CODE = "PAYMENT_METHOD_DECLINED";
var PAYMENT_METHOD_DECLINED_MESSAGE = "This card was declined by the issuer and was not saved. Please try a different card.";
function paymentMethodDeclinedFieldErrors(message = PAYMENT_METHOD_DECLINED_MESSAGE) {
  return { fieldErrors: { [PAYMENT_METHOD_TOKEN_FIELD]: [message] } };
}
var SetDefaultPaymentMethodInputSchema = z.object({
  isDefault: z.literal(true)
});
var WaffleAppSchema = z.object({
  name: z.string(),
  fullLogoPath: z.string().url(),
  applicationName: z.string(),
  url: z.string().url(),
  current: z.boolean().optional()
});
var WaffleAppsResponseSchema = z.array(WaffleAppSchema);
var CrmEnvironment = /* @__PURE__ */ ((CrmEnvironment2) => {
  CrmEnvironment2["Uat"] = "uat";
  CrmEnvironment2["Prod"] = "prod";
  return CrmEnvironment2;
})(CrmEnvironment || {});
var CrmEnvironmentSchema = z.enum(CrmEnvironment);
var CrmIdentifierSchema = z.string().min(1);
var CrmEmailSchema = z.string().email();
var CrmEntryUrlSchema = z.string().url();
var OrgAccessGrantStatus = /* @__PURE__ */ ((OrgAccessGrantStatus2) => {
  OrgAccessGrantStatus2["Pending"] = "pending";
  OrgAccessGrantStatus2["Granted"] = "granted";
  OrgAccessGrantStatus2["Retrying"] = "retrying";
  OrgAccessGrantStatus2["Failed"] = "failed";
  OrgAccessGrantStatus2["Skipped"] = "skipped";
  return OrgAccessGrantStatus2;
})(OrgAccessGrantStatus || {});
var OrgAccessGrantReason = /* @__PURE__ */ ((OrgAccessGrantReason2) => {
  OrgAccessGrantReason2["NoSsoAccount"] = "no_sso_account";
  OrgAccessGrantReason2["NotOrgMember"] = "not_org_member";
  OrgAccessGrantReason2["Deactivated"] = "deactivated";
  OrgAccessGrantReason2["SsoError"] = "sso_error";
  return OrgAccessGrantReason2;
})(OrgAccessGrantReason || {});
var OrgAccessGrantStatusSchema = z.enum(OrgAccessGrantStatus);
var OrgAccessGrantReasonSchema = z.enum(OrgAccessGrantReason);

// src/wire/crmOnboarding/Provisioning.ts
var CRM_ONBOARDING_MAX_SELECTIONS = 500;
var CrmOnboardingStatus = /* @__PURE__ */ ((CrmOnboardingStatus2) => {
  CrmOnboardingStatus2["Provisioning"] = "provisioning";
  CrmOnboardingStatus2["Active"] = "active";
  CrmOnboardingStatus2["Failed"] = "failed";
  return CrmOnboardingStatus2;
})(CrmOnboardingStatus || {});
var CrmOnboardingFailureCode = /* @__PURE__ */ ((CrmOnboardingFailureCode2) => {
  CrmOnboardingFailureCode2["SalesforceIdMissing"] = "SALESFORCE_ID_MISSING";
  CrmOnboardingFailureCode2["SalesforceIdConflict"] = "SALESFORCE_ID_CONFLICT";
  CrmOnboardingFailureCode2["MultipleAuctionsInstances"] = "MULTIPLE_AUCTIONS_INSTANCES";
  CrmOnboardingFailureCode2["OrgLinkConflict"] = "ORG_LINK_CONFLICT";
  CrmOnboardingFailureCode2["OrgCrmIdMismatch"] = "ORG_CRM_ID_MISMATCH";
  CrmOnboardingFailureCode2["SsoOrgMismatch"] = "SSO_ORG_MISMATCH";
  CrmOnboardingFailureCode2["InitiatorNotGrantable"] = "INITIATOR_NOT_GRANTABLE";
  CrmOnboardingFailureCode2["SsoUnavailable"] = "SSO_UNAVAILABLE";
  CrmOnboardingFailureCode2["Internal"] = "INTERNAL";
  return CrmOnboardingFailureCode2;
})(CrmOnboardingFailureCode || {});
var CrmProfileImportStatus = /* @__PURE__ */ ((CrmProfileImportStatus2) => {
  CrmProfileImportStatus2["Pending"] = "pending";
  CrmProfileImportStatus2["Imported"] = "imported";
  CrmProfileImportStatus2["Partial"] = "partial";
  CrmProfileImportStatus2["Failed"] = "failed";
  CrmProfileImportStatus2["Skipped"] = "skipped";
  return CrmProfileImportStatus2;
})(CrmProfileImportStatus || {});
var CrmOnboardingStatusSchema = z.enum(CrmOnboardingStatus);
var CrmOnboardingFailureCodeSchema = z.enum(CrmOnboardingFailureCode);
var CrmProfileImportStatusSchema = z.enum(CrmProfileImportStatus);
var CrmOnboardingSubmitRequestSchema = z.object({
  selectedCrmUserIds: z.array(CrmIdentifierSchema).max(CRM_ONBOARDING_MAX_SELECTIONS)
});
var CrmOnboardingGrantSchema = z.object({
  crmUserId: CrmIdentifierSchema,
  email: CrmEmailSchema,
  displayName: z.string().nullable(),
  isInitiator: z.boolean(),
  status: OrgAccessGrantStatusSchema,
  reason: OrgAccessGrantReasonSchema.nullable(),
  alreadyHadAccess: z.boolean()
});
var CrmOnboardingSnapshotSchema = z.object({
  onboardingId: UuidSchema,
  status: CrmOnboardingStatusSchema,
  failure: z.object({ code: CrmOnboardingFailureCodeSchema, retryable: z.boolean() }).nullable(),
  entryUrl: CrmEntryUrlSchema.nullable(),
  orgName: z.string(),
  grants: z.array(CrmOnboardingGrantSchema),
  grantsSettled: z.boolean(),
  profileImport: CrmProfileImportStatusSchema,
  version: z.number().int().nonnegative(),
  startedAt: Iso8601Schema,
  activeAt: Iso8601Schema.nullable(),
  grantsSettledAt: Iso8601Schema.nullable()
});

// src/wire/crmOnboarding/Exchange.ts
var CrmOnboardingSessionState = /* @__PURE__ */ ((CrmOnboardingSessionState2) => {
  CrmOnboardingSessionState2["NotStarted"] = "not_started";
  CrmOnboardingSessionState2["InProgress"] = "in_progress";
  CrmOnboardingSessionState2["Active"] = "active";
  CrmOnboardingSessionState2["Failed"] = "failed";
  return CrmOnboardingSessionState2;
})(CrmOnboardingSessionState || {});
var CrmOnboardingSessionStateSchema = z.enum(CrmOnboardingSessionState);
var CrmOnboardingExchangeRequestSchema = z.object({
  assertion: z.string().min(1),
  environment: CrmEnvironmentSchema
});
var CrmOnboardingSessionSchema = z.object({
  onboardingToken: z.string().min(1),
  expiresAt: Iso8601Schema,
  org: z.object({ crmOrgId: CrmIdentifierSchema, environment: CrmEnvironmentSchema, name: z.string() }),
  initiator: z.object({
    crmUserId: CrmIdentifierSchema,
    email: CrmEmailSchema,
    displayName: z.string().nullable()
  }),
  state: CrmOnboardingSessionStateSchema,
  existingAuctionsOrg: z.object({ name: z.string() }).nullable(),
  snapshot: CrmOnboardingSnapshotSchema.nullable(),
  entryUrl: CrmEntryUrlSchema.nullable()
});
var CRM_ONBOARDING_MAX_USERS = 1e3;
var CrmOnboardingUserEligibility = /* @__PURE__ */ ((CrmOnboardingUserEligibility2) => {
  CrmOnboardingUserEligibility2["Eligible"] = "eligible";
  CrmOnboardingUserEligibility2["AlreadyAdmin"] = "already_admin";
  CrmOnboardingUserEligibility2["NoSsoAccount"] = "no_sso_account";
  CrmOnboardingUserEligibility2["Inactive"] = "inactive";
  return CrmOnboardingUserEligibility2;
})(CrmOnboardingUserEligibility || {});
var CrmOnboardingUserEligibilitySchema = z.enum(CrmOnboardingUserEligibility);
var CrmOnboardingUserSchema = z.object({
  crmUserId: CrmIdentifierSchema,
  email: CrmEmailSchema,
  displayName: z.string().nullable(),
  isInitiator: z.boolean(),
  eligibility: CrmOnboardingUserEligibilitySchema
});
var CrmOnboardingUsersResponseSchema = z.object({
  users: z.array(CrmOnboardingUserSchema).max(CRM_ONBOARDING_MAX_USERS),
  fetchedAt: Iso8601Schema
});
var CrmInstanceStatusValue = /* @__PURE__ */ ((CrmInstanceStatusValue2) => {
  CrmInstanceStatusValue2["Provisioning"] = "provisioning";
  CrmInstanceStatusValue2["Active"] = "active";
  CrmInstanceStatusValue2["Failed"] = "failed";
  CrmInstanceStatusValue2["Deactivated"] = "deactivated";
  return CrmInstanceStatusValue2;
})(CrmInstanceStatusValue || {});
var CrmInstanceEventType = /* @__PURE__ */ ((CrmInstanceEventType2) => {
  CrmInstanceEventType2["StatusChanged"] = "auctions.instance.status_changed";
  return CrmInstanceEventType2;
})(CrmInstanceEventType || {});
var CRM_INSTANCE_STATUS_SCHEMA_VERSION = 1;
var CrmInstanceStatusValueSchema = z.enum(CrmInstanceStatusValue);
var CrmInstanceStatusSchema = z.object({
  crmOrgId: CrmIdentifierSchema,
  environment: CrmEnvironmentSchema,
  instanceId: CrmIdentifierSchema.nullable(),
  entryUrl: CrmEntryUrlSchema.nullable(),
  status: CrmInstanceStatusValueSchema,
  version: z.number().int().nonnegative(),
  updatedAt: Iso8601Schema
});
var CrmInstanceStatusEventSchema = CrmInstanceStatusSchema.extend({
  eventId: UuidSchema,
  eventType: z.literal("auctions.instance.status_changed" /* StatusChanged */),
  schemaVersion: z.literal(CRM_INSTANCE_STATUS_SCHEMA_VERSION),
  occurredAt: Iso8601Schema,
  initiatedBy: z.object({ crmUserId: CrmIdentifierSchema }).nullable()
});
var ApiErrorCodeSchema = z.enum([
  "BAD_REQUEST",
  "UNAUTHORIZED",
  "JWT_EXPIRED",
  "ORG_SCOPE_VIOLATION",
  "ORG_NOT_PROVISIONED",
  // Multi-org SSO login: admin-API discovery returned zero Auctions orgs for a
  // user whose claims carried no org identity. Distinct from ORG_NOT_PROVISIONED
  // (claims path / org row missing in Auctions DB).
  "AUTH_403_NO_ORGS",
  // Multi-org org-adopt: chosen sso_org_id is not on the caller's discovery list.
  // Carries clearSession so admin-web drops the cookie and returns to the selector.
  "ORG_ACCESS_DENIED",
  // Multi-org org-adopt: chosen sso_org_id does not map to the tenant identified
  // by org_slug / access salesforceId (or is not provisioned in Auctions).
  "ORG_TENANT_MISMATCH",
  // Org switch refused: session not picker-bound, supervisor, or access is
  // down to a single organization.
  "ORG_SWITCH_UNAVAILABLE",
  // Login-time SSO admin-API discovery exceeded its overall wall-clock budget.
  "SSO_DISCOVERY_TIMEOUT",
  "ROLE_INSUFFICIENT",
  "SSO_TOKEN_REFRESH_FAILED",
  "NOT_FOUND",
  "CONFLICT",
  "URL_IN_USE",
  "AUCTION_ARCHIVED",
  "GONE",
  "VALIDATION_FAILED",
  "BID_TOO_LOW",
  "BID_TOO_HIGH",
  "MAX_BID_LOWER",
  "OUTBID",
  // A standard bid from the bidder who already holds the entity's high bid —
  // rejected outright rather than letting them inflate their own price
  // against no competing bidder. The one legitimate self-directed action is
  // raising a max ceiling, which resolves through 'max' bids instead.
  "ALREADY_HIGH_BIDDER",
  "ITEM_CLOSED",
  "PLACE_FOR_BIDDER_DISABLED",
  "SELL_TO_BIDDER_DISABLED",
  "EVENT_PAUSED",
  // GT1-442 (bid-void-design §2): admin bid voiding is only legal while the
  // event is Live. The API returns this when a stale admin client tries to
  // void against a non-open auction; the run-live UI branches on it to resync
  // rather than showing a generic failure.
  "AUCTION_NOT_LIVE",
  // GT1-445 (reassign-item-design §edge 5): a reassignment/cancel-sale hit an
  // item whose state changed underneath the admin's modal (line already
  // released, charge in flight, target's bid voided…). Clients re-read state
  // and re-render rather than showing a generic failure.
  "REASSIGN_CONFLICT",
  // ADR-014 / api-contracts.md §1.2: Neon Pay error or refund-failure on a
  // collectible refund-and-release. Nothing was released — the operation aborted.
  "REFUND_FAILED",
  // ADR-016 — bidder charge revalidation. The client reviewed a different
  // revision/total than the claimed row. 409 with details so bidder-web can
  // re-present the total rather than treating it as INTERNAL.
  "INVOICE_REVISION_STALE",
  // 409 when reconciliation rows change during CSV export; retry the export.
  // GT1-445: the manual reassign/cancel-sale endpoints are gated by the
  // settlement.move-to-next-bidder org flag; the automatic BR-14 offer path
  // is not. Without this entry the envelope parser downgrades the response to
  // INTERNAL and the flag message never reaches the client.
  "MOVE_TO_NEXT_BIDDER_DISABLED",
  "PROXY_BIDDING_DISABLED",
  "BIDDER_NOT_ACTIVATED",
  // GT1-529: distinct from BIDDER_NOT_ACTIVATED. The bidder IS activated —
  // activation latches and gates bidding only — but the auction gained terms
  // after they activated, so acceptance is outstanding. bidder-web reopens the
  // activation drawer on this code, which requires the client to be able to
  // tell the two apart.
  "TERMS_NOT_ACCEPTED",
  "RESERVE_NOT_MET",
  "CAPACITY_REACHED",
  "REGISTRATION_CLOSED",
  "PUBLISH_VALIDATION_FAILED",
  "IDEMPOTENT_REPLAY_MISMATCH",
  "RATE_LIMITED",
  "INTERNAL",
  // Both INTERNAL and INTERNAL_ERROR are real and mean different things.
  // INTERNAL is this project's vocabulary for a deliberate throw on a broken
  // server-side invariant. INTERNAL_ERROR is what @neon-one/api-framework's
  // error handler emits for ANY unhandled exception, and it must stay in this
  // catalog: ApiError.fromEnvelope rewrites an unrecognised code to
  // 'malformed error envelope', so leaving it out turns every genuine 500 into
  // a wire-shape complaint and hides the real failure from whoever is
  // debugging it. Do not collapse them, and do not throw INTERNAL_ERROR by
  // hand -- it is the framework's code, not ours.
  "INTERNAL_ERROR",
  "NOT_IMPLEMENTED",
  "UPSTREAM_FAILURE",
  "AI_DISABLED",
  "MISSING_REQUIRED_MERGE_FIELD",
  "MERCHANT_NOT_CONFIGURED",
  // The issuer refused the $1 authorization placed when a card is
  // stored, so the card is never written. The bidder recovers by entering a
  // different card, which is why every surface renders this inline on the card
  // field (see `paymentMethodDeclinedFieldErrors`) rather than as a dead-end
  // toast. Distinct from MERCHANT_NOT_CONFIGURED (our setup) and from a
  // checkout decline (money owed) — nothing was charged here.
  "PAYMENT_METHOD_DECLINED",
  "INVALID_REGISTRATION_TOKEN",
  // GT1-446: a bundle whose composition changed under active bids refuses new
  // bids until an admin reopens it.
  "BUNDLE_PAUSED",
  // GT1-446: a bundle's bids void as a whole stack on a composition change,
  // never one at a time — there is no per-bid bundle void to request.
  "BUNDLE_BID_NOT_INDIVIDUALLY_VOIDABLE",
  // Codes below were thrown by the server for some time before being
  // registered here. An unregistered code is not a cosmetic gap: any response
  // carrying one fails ApiFailureSchema, and ApiError.fromEnvelope rewrites the
  // whole envelope to INTERNAL / 'malformed error envelope' — so the server's
  // real message never reaches the client and whoever is debugging sees a wire
  // complaint instead of the failure. A code must land here in the same change
  // that first throws it.
  // Auth / session
  "ACCOUNT_REVOKED",
  "SESSION_MAX_LIFETIME",
  "FORBIDDEN",
  "SUPERVISOR_REQUIRED",
  // An active supervisor whose access level lacks what the route needs
  // (GT1-579). error.required names the missing requirement.
  "SUPERVISOR_PERMISSION_REQUIRED",
  // NeonSSO integration. SSO_API_ERROR carries `error.upstream` with the
  // admin API's own status and response body — supervisors are internal staff
  // diagnosing a broken tenant, so the upstream detail is forwarded verbatim.
  "SSO_API_ERROR",
  "SSO_CONFIG_ERROR",
  "SSO_EXCHANGE_FAILED",
  "SSO_USER_NOT_FOUND",
  "SSO_USER_NOT_IN_ORG",
  "SSO_USER_DEACTIVATED",
  // Supervisor management of Auctions access on an org's SSO instance
  // (GT1-561). SSO_ORG_UNKNOWN / SSO_INSTANCE_UNKNOWN mean the organizations
  // row has no sso_org_id or sso_instance_id yet — sso_org_id is cached on
  // first login, so an org nobody has signed into cannot be addressed here.
  "SSO_ORG_UNKNOWN",
  "SSO_INSTANCE_UNKNOWN",
  "NO_AUCTIONS_ACCESS",
  // Refuses to strand an org: staff cannot grant roles, so removing the only
  // admin leaves nobody who can restore access without a supervisor.
  "LAST_ORG_ADMIN",
  "ALREADY_DEACTIVATED",
  // Org provisioning / lookup
  "ORG_NOT_FOUND",
  "ORG_SLUG_CONFLICT",
  // Refuses to remove or demote the last super admin, who alone can manage
  // supervisors.
  "LAST_SUPER_ADMIN",
  "CRM_ASSERTION_INVALID",
  "CRM_ASSERTION_REPLAYED",
  "CRM_ADMIN_REQUIRED",
  "CRM_ORG_MISMATCH",
  "ONBOARDING_SESSION_INVALID",
  "CRM_SERVICE_AUTH_INVALID",
  "SSO_UNAVAILABLE",
  "MULTIPLE_AUCTIONS_INSTANCES",
  // NeonCRM / Neon Pay upstreams
  "CRM_NOT_CONFIGURED",
  "CRM_REQUEST_FAILED",
  "CRM_GATEWAY_FETCH_FAILED",
  "NEON_PAY_CREDENTIALS_MISSING",
  "NEON_PAY_MERCHANT_FETCH_FAILED",
  // Settlement / invoicing / charge
  "NOT_PAID",
  "NO_CARD_ON_FILE",
  "TERMINAL_PAYMENTS_DISABLED",
  "TERMINAL_CANCEL_FAILED",
  "HOLD_CONFLICT",
  "VOID_DISABLED",
  "CHARGE_CONFLICT",
  // Neon Pay refused the charge request outright; nothing was charged and the invoice
  // can be paid again.
  "CHARGE_REFUSED",
  "CHARGE_ALREADY_CLAIMED",
  "CHARGE_AMOUNT_MISMATCH",
  "CHARGE_ID_MISMATCH",
  "CHARGE_NOT_CAPTURED",
  "CHARGE_CAPTURED",
  "CHARGE_REFUNDED",
  "CHARGE_NOT_FOUND",
  "REFUND_CONFLICT",
  "REFUND_ALREADY_CLAIMED",
  "REFUND_NOT_FOUND",
  "REFUND_NOT_SETTLED",
  "REFUND_AMOUNT_MISMATCH",
  // GT1-586: a refund-and-release selection owes more than the charge has left
  // to refund, so the whole batch was refused before any refund was sent.
  "REFUND_EXCEEDS_CHARGE",
  "IDEMPOTENCY_KEY_REUSED",
  "METHOD_NOT_AVAILABLE",
  "PAYMENT_METHOD_NOT_CHARGEABLE",
  "PAYMENT_METHOD_NOT_STORABLE",
  "MERCHANT_LOCKED",
  // The Neon Pay webhook ack could not enqueue the notification. Neon Pay
  // never redelivers, so it is lost.
  "NEONPAY_WEBHOOK_ENQUEUE_FAILED",
  // 502 from POST /admin/invoices/:invoiceUuid/receipts/regenerate: the render
  // job could not be queued, so no new receipt PDF is coming.
  "RECEIPT_DISPATCH_FAILED",
  // Notifications / email / broadcast
  "BROADCAST_INSERT_FAILED",
  "TEMPLATE_COMPILATION_FAILED",
  "TEMPLATE_EDITING_DISABLED",
  "TEMPLATE_NOT_COMPILED",
  "TEMPLATE_TYPE_LOCKED",
  "INVALID_RESEND_STATUS",
  "UNSUPPORTED_CHANNEL",
  "ALWAYS_ON_NOTIFICATION",
  "POST_EVENT_THANK_YOU_IN_PAST",
  "POST_EVENT_THANK_YOU_ALREADY_SENT",
  "PAYMENT_FOLLOW_UP_IN_PAST",
  "PAYMENT_FOLLOW_UP_ALREADY_SENT",
  "AUCTION_NOT_PUBLISHED",
  "EMPTY_AUDIENCE",
  "EMPTY_BROADCAST_BODY",
  "MISSING_UNSUBSCRIBE_FOOTER",
  "SEGMENT_COUNT_MISSING",
  // Bidding
  "ITEM_IN_BUNDLE",
  // Request shape / assets
  "INVALID_REQUEST",
  "INVALID_SVG",
  "PAYLOAD_TOO_LARGE",
  // 415 from POST /assets for an SVG: a presigned PUT cannot be sanitized, so
  // SVG logos must go through POST /branding/upload-svg instead.
  "UNSUPPORTED_MEDIA_TYPE",
  "SERVICE_UNAVAILABLE",
  "ACTIVITY_FEED_DISABLED"
]);
var ApiErrorBodySchema = z.object({
  code: ApiErrorCodeSchema,
  message: z.string(),
  fieldErrors: z.record(z.string(), z.array(z.string())).optional(),
  currentMinimum: z.number().int().nonnegative().optional(),
  currentHighBid: z.number().int().nonnegative().optional(),
  requiredRole: z.string().optional(),
  currentRole: z.string().optional(),
  conflictingAuctionName: z.string().optional(),
  failures: z.array(z.object({ check: z.string(), message: z.string() })).optional(),
  // GONE on a magic-link exchange: which event the dead grant belonged to.
  // The `/si/<token>` link shape carries no slug, so this is the only thing
  // that lets the client offer a new link instead of a dead end.
  eventSlug: z.string().optional(),
  // *_IN_PAST on a closed auction's notification delay: the smallest value
  // (in that setting's own unit) whose send would still be ahead.
  earliestValue: z.number().int().positive().optional(),
  correlationId: z.string().optional()
}).passthrough();
var ApiSuccessSchema = (payload) => z.object({ success: z.literal(true), data: payload });
var ApiFailureSchema = z.object({
  success: z.literal(false),
  error: ApiErrorBodySchema
});
var ApiEnvelopeSchema = (payload) => z.discriminatedUnion("success", [ApiSuccessSchema(payload), ApiFailureSchema]);
var successEnvelope = (data) => ({
  success: true,
  data
});
var failureEnvelope = (error) => ({
  success: false,
  error
});
var ApiError = class _ApiError extends Error {
  code;
  status;
  body;
  correlationId;
  constructor(status, body) {
    super(body.message);
    this.name = "ApiError";
    this.code = body.code;
    this.status = status;
    this.body = body;
    this.correlationId = body.correlationId;
    Object.setPrototypeOf(this, _ApiError.prototype);
  }
  get fieldErrors() {
    return this.body.fieldErrors;
  }
  static fromEnvelope(status, raw) {
    const parsed = ApiFailureSchema.safeParse(raw);
    if (parsed.success) return new _ApiError(status, parsed.data.error);
    return new _ApiError(status, {
      code: "INTERNAL",
      message: "malformed error envelope"
    });
  }
};
function createApiClient(opts) {
  const instance = opts.axiosInstance ?? axios.create({
    baseURL: opts.baseUrl,
    timeout: opts.timeoutMs ?? 3e4,
    headers: { "content-type": "application/json" }
  });
  instance.interceptors.request.use(
    async (config) => {
      if (opts.getJwt) {
        const jwt = await opts.getJwt();
        if (jwt) config.headers.set("authorization", `Bearer ${jwt}`);
      }
      const meta = config.meta;
      if (meta?.idempotencyKey) {
        config.headers.set("idempotency-key", meta.idempotencyKey);
      } else if (meta?.generateIdempotencyKey && opts.idempotencyKey) {
        config.headers.set("idempotency-key", opts.idempotencyKey());
      }
      return config;
    }
  );
  instance.interceptors.response.use(
    (resp) => {
      const body = resp.data;
      if (body && typeof body === "object" && "success" in body) {
        if (body.success === true) {
          resp.data = body.data;
          return resp;
        }
        throw ApiError.fromEnvelope(resp.status, body);
      }
      return resp;
    },
    (err) => {
      if (err.response) {
        throw ApiError.fromEnvelope(err.response.status, err.response.data);
      }
      throw err;
    }
  );
  return instance;
}
var withIdempotency = (key, config = {}) => ({
  ...config,
  meta: { ...config.meta, idempotencyKey: key }
});

// src/clients/createWsClient.ts
var decodeData = (raw) => {
  if (typeof raw === "string") return raw;
  if (raw instanceof Uint8Array) return new TextDecoder().decode(raw);
  if (typeof ArrayBuffer !== "undefined" && raw instanceof ArrayBuffer) {
    return new TextDecoder().decode(new Uint8Array(raw));
  }
  const anyBlob = raw;
  if (typeof anyBlob.text === "function") {
    return "";
  }
  return "";
};
var resolveWsCtor = (override) => {
  if (override) return override;
  const g = globalThis.WebSocket;
  if (g) return g;
  throw new Error(
    "createWsClient: no WebSocket constructor available; pass options.WebSocketImpl or run in an env with global WebSocket"
  );
};
var isPingFrame = (frame) => frame.type === "ping";
function createWsClient(opts) {
  const reconnectOpts = {
    enabled: opts.reconnect?.enabled ?? true,
    initialDelayMs: opts.reconnect?.initialDelayMs ?? 500,
    maxDelayMs: opts.reconnect?.maxDelayMs ?? 3e4
  };
  const heartbeatOpts = {
    enabled: opts.heartbeat?.enabled ?? true,
    intervalMs: opts.heartbeat?.intervalMs ?? 25e3,
    stallTimeoutMs: opts.heartbeat?.stallTimeoutMs ?? 6e4
  };
  const handlers = /* @__PURE__ */ new Set();
  const channelSet = new Set(opts.initialChannels ?? []);
  let sock = null;
  let currentStatus = "idle";
  let userClosed = false;
  let connectGeneration = 0;
  let reconnectDelayMs = reconnectOpts.initialDelayMs;
  let reconnectTimer = null;
  let heartbeatTimer = null;
  let lastInboundAt = 0;
  const setStatus = (s) => {
    if (currentStatus === s) return;
    currentStatus = s;
    if (opts.onStatusChange) opts.onStatusChange(s);
  };
  const sendOp = (op) => {
    if (!sock || sock.readyState !== 1) return;
    try {
      sock.send(JSON.stringify(op));
    } catch {
    }
  };
  const flushSubscribe = () => {
    if (channelSet.size > 0) {
      sendOp({ op: "subscribe", channels: Array.from(channelSet) });
    }
  };
  const dispatch = (frame) => {
    for (const h of handlers) {
      try {
        h(frame);
      } catch {
      }
    }
  };
  const scheduleReconnect = () => {
    if (!reconnectOpts.enabled || userClosed) return;
    if (reconnectTimer) return;
    const backoffWindow = reconnectDelayMs;
    reconnectDelayMs = Math.min(reconnectDelayMs * 2, reconnectOpts.maxDelayMs);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void doConnect();
    }, Math.random() * backoffWindow);
  };
  const stopHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };
  const startHeartbeat = () => {
    if (!heartbeatOpts.enabled) return;
    stopHeartbeat();
    lastInboundAt = Date.now();
    heartbeatTimer = setInterval(() => {
      if (currentStatus !== "open" || !sock) return;
      if (Date.now() - lastInboundAt > heartbeatOpts.stallTimeoutMs) {
        try {
          sock.close(4e3, "heartbeat stall");
        } catch {
        }
        return;
      }
      sendOp({ op: "pong" });
    }, heartbeatOpts.intervalMs);
  };
  const doConnect = async () => {
    if (userClosed) return;
    if (currentStatus === "connecting" || currentStatus === "open") return;
    setStatus("connecting");
    const generation = ++connectGeneration;
    let jwt;
    try {
      jwt = await opts.getJwt();
    } catch {
      jwt = null;
    }
    if (userClosed || generation !== connectGeneration) return;
    const Ctor = resolveWsCtor(opts.WebSocketImpl);
    const protocols = jwt ? `bearer.${jwt}` : void 0;
    let next;
    try {
      next = new Ctor(opts.baseUrl, protocols);
    } catch (e) {
      setStatus("error");
      scheduleReconnect();
      throw e;
    }
    sock = next;
    next.onopen = () => {
      setStatus("open");
      reconnectDelayMs = reconnectOpts.initialDelayMs;
      flushSubscribe();
      startHeartbeat();
    };
    next.onmessage = (ev) => {
      lastInboundAt = Date.now();
      const text = decodeData(ev.data);
      if (!text) return;
      let frame = null;
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === "object" && "type" in parsed) {
          frame = parsed;
        }
      } catch {
        return;
      }
      if (!frame) return;
      if (isPingFrame(frame)) {
        sendOp({ op: "pong" });
        return;
      }
      dispatch(frame);
    };
    next.onerror = () => {
      setStatus("error");
    };
    next.onclose = () => {
      stopHeartbeat();
      setStatus("closed");
      sock = null;
      scheduleReconnect();
    };
  };
  return {
    async connect() {
      userClosed = false;
      await doConnect();
    },
    subscribe(channels2) {
      let added = false;
      for (const c of channels2) {
        if (!channelSet.has(c)) {
          channelSet.add(c);
          added = true;
        }
      }
      if (added && currentStatus === "open") {
        sendOp({ op: "subscribe", channels: channels2 });
      }
    },
    unsubscribe(channels2) {
      const drop = [];
      for (const c of channels2) {
        if (channelSet.delete(c)) drop.push(c);
      }
      if (drop.length > 0 && currentStatus === "open") {
        sendOp({ op: "unsubscribe", channels: drop });
      }
    },
    on(handler) {
      handlers.add(handler);
      return {
        unsubscribe() {
          handlers.delete(handler);
        }
      };
    },
    close() {
      userClosed = true;
      connectGeneration += 1;
      stopHeartbeat();
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (sock) {
        try {
          sock.close(1e3, "client closed");
        } catch {
        }
        sock = null;
      }
      setStatus("closed");
    },
    status() {
      return currentStatus;
    }
  };
}

// src/labels/statusLabels.ts
var AUCTION_ITEM_STATUS_LABELS = {
  draft: "Draft",
  published: "Published",
  open: "Open for bids",
  closed: "Awaiting payment",
  sold: "Paid",
  unsold: "Unsold",
  withdrawn: "Withdrawn"
};
function itemStatusLabel(status) {
  if (status === "cancelled") return AUCTION_ITEM_STATUS_LABELS.unsold;
  if (Object.prototype.hasOwnProperty.call(AUCTION_ITEM_STATUS_LABELS, status)) {
    return AUCTION_ITEM_STATUS_LABELS[status];
  }
  return status;
}
var INVOICE_PAYMENT_STATUS_LABELS = {
  pending: "Awaiting payment",
  processing: "Processing",
  paid: "Paid",
  failed: "Declined",
  refunded: "Refunded",
  partially_refunded: "Partially refunded",
  void: "Void"
};
var invoicePaymentStatusLabel = (status) => INVOICE_PAYMENT_STATUS_LABELS[status];
var DerivedInvoiceDisplayState = /* @__PURE__ */ ((DerivedInvoiceDisplayState2) => {
  DerivedInvoiceDisplayState2["NothingDue"] = "nothing_due";
  DerivedInvoiceDisplayState2["RefundSettling"] = "refund_settling";
  DerivedInvoiceDisplayState2["RefundFailed"] = "refund_failed";
  DerivedInvoiceDisplayState2["WaitingForTap"] = "waiting_for_tap";
  return DerivedInvoiceDisplayState2;
})(DerivedInvoiceDisplayState || {});
var PAID_DISPLAY = { label: "Paid", severity: "success" };
var ADMIN_DUE_DISPLAY = { label: "Awaiting payment", severity: "warn" };
var BIDDER_DUE_DISPLAY = { label: "Payment due", severity: "warn" };
var REFUNDED_DISPLAY = { label: "Refunded", severity: "secondary" };
var PARTIAL_REFUND_DISPLAY = { label: "Partially refunded", severity: "secondary" };
var CLEARING_DISPLAY = { ...PAID_DISPLAY, qualifier: "Clearing" };
var ADMIN_SETTLEMENT_STATUS_MAP = {
  paid_auto: PAID_DISPLAY,
  paid_online: PAID_DISPLAY,
  paid_offline: PAID_DISPLAY,
  awaiting: ADMIN_DUE_DISPLAY,
  no_card: ADMIN_DUE_DISPLAY,
  failed: { label: "Declined", severity: "danger" },
  processing: { label: "Processing", severity: "info" },
  clearing: CLEARING_DISPLAY,
  unconfirmed: { label: "Payment unconfirmed", severity: "danger" },
  partially_refunded: PARTIAL_REFUND_DISPLAY,
  refunded: REFUNDED_DISPLAY,
  void: { label: "Void", severity: "secondary" },
  ["nothing_due" /* NothingDue */]: { label: "Nothing due", severity: "secondary" },
  ["refund_settling" /* RefundSettling */]: { label: "Refund settling", severity: "info" },
  ["refund_failed" /* RefundFailed */]: { label: "Refund failed", severity: "danger" },
  ["waiting_for_tap" /* WaitingForTap */]: { label: "Waiting for tap", severity: "info" }
};
var BIDDER_SETTLEMENT_STATUS_MAP = {
  paid_auto: PAID_DISPLAY,
  paid_online: PAID_DISPLAY,
  paid_offline: PAID_DISPLAY,
  awaiting: BIDDER_DUE_DISPLAY,
  no_card: BIDDER_DUE_DISPLAY,
  failed: { label: "Payment declined", severity: "danger" },
  processing: { label: "Processing", severity: "info" },
  clearing: CLEARING_DISPLAY,
  unconfirmed: CLEARING_DISPLAY,
  partially_refunded: PARTIAL_REFUND_DISPLAY,
  refunded: REFUNDED_DISPLAY,
  void: { label: "No payment needed", severity: "secondary" },
  ["nothing_due" /* NothingDue */]: { label: "No payment needed", severity: "secondary" },
  ["refund_settling" /* RefundSettling */]: { label: "Refund on its way", severity: "info" },
  ["refund_failed" /* RefundFailed */]: { label: "Refund on its way", severity: "info" },
  ["waiting_for_tap" /* WaitingForTap */]: { label: "Waiting for tap", severity: "info" }
};
var StatusQualifier = /* @__PURE__ */ ((StatusQualifier2) => {
  StatusQualifier2["Clearing"] = "clearing";
  StatusQualifier2["Unconfirmed"] = "unconfirmed";
  StatusQualifier2["RefundSettling"] = "refund_settling";
  StatusQualifier2["OwedBack"] = "owed_back";
  StatusQualifier2["WaitingForTap"] = "waiting_for_tap";
  return StatusQualifier2;
})(StatusQualifier || {});
var STATUS_QUALIFIER_MAP = {
  ["clearing" /* Clearing */]: { label: "Clearing", severity: "info" },
  ["unconfirmed" /* Unconfirmed */]: { label: "Unconfirmed", severity: "danger" },
  ["refund_settling" /* RefundSettling */]: { label: "Refund settling", severity: "info" },
  ["owed_back" /* OwedBack */]: { label: "Owed back", severity: "danger" },
  ["waiting_for_tap" /* WaitingForTap */]: { label: "Waiting for tap", severity: "info" }
};
var ADMIN_REFUND_STATE_MAP = {
  none: { label: "No refund", severity: "secondary" },
  partial: PARTIAL_REFUND_DISPLAY,
  full: REFUNDED_DISPLAY,
  failed: ADMIN_SETTLEMENT_STATUS_MAP.refund_failed
};
var BIDDER_REFUND_STATE_MAP = {
  none: { label: "No refund", severity: "secondary" },
  partial: PARTIAL_REFUND_DISPLAY,
  full: REFUNDED_DISPLAY,
  failed: BIDDER_SETTLEMENT_STATUS_MAP.refund_failed
};
var ADMIN_SALE_STATE_MAP = {
  sold: { label: "Sold", severity: "success" },
  invoicing: { label: "Invoicing", severity: "info" },
  needs_buyer: { label: "Needs a buyer", severity: "warn" },
  offer_outstanding: { label: "Offer outstanding", severity: "info" },
  offer_ended: { label: "Needs a buyer", severity: "warn" },
  sale_canceled: { label: "Withdrawn", severity: "secondary" },
  unsold: { label: "Unsold", severity: "secondary" }
};
var BIDDER_SALE_STATE_MAP = {
  sold: { label: "Won", severity: "success" },
  invoicing: { label: "Invoice coming", severity: "info" },
  needs_buyer: { label: "No longer yours", severity: "secondary" },
  offer_outstanding: { label: "Offer waiting on you", severity: "info" },
  offer_ended: { label: "No longer yours", severity: "secondary" },
  sale_canceled: { label: "Didn't win", severity: "secondary" },
  unsold: { label: "Didn't win", severity: "secondary" }
};
var SALE_STATE_REASON_MAP = {
  declined: { label: "Offer declined", severity: "warn" },
  lapsed: { label: "Offer lapsed", severity: "warn" },
  cancelled: { label: "Offer canceled", severity: "secondary" }
};
var CRM_SYNC_DISPLAY_MAP = {
  pending: { label: "Pending", severity: "info" },
  constituent_synced: { label: "Account synced", severity: "success" },
  synced: { label: "Synced", severity: "success" },
  failed: { label: "Failed", severity: "danger" },
  permanently_failed: { label: "Permanently failed", severity: "danger" },
  invoice_sync_failed: { label: "Invoice sync failed", severity: "danger" },
  not_ready: { label: "Not ready", severity: "secondary" },
  not_applicable: { label: "Not applicable", severity: "secondary" }
};
var RECEIPT_CLEARING_NOTICE = "Your payment is still clearing. We'll let you know if it doesn't go through.";
var refundOnItsWayLabel = (formattedAmount) => `Refund of ${formattedAmount} on its way`;
var refundContactLabel = (organization) => `Contact ${organization} about your refund`;
var ADMIN_BID_STATUS_DISPLAY = {
  label: "Admin",
  severity: "info",
  icon: "pi-shield"
};
function adminBidStatusDisplay(adminName) {
  return { ...ADMIN_BID_STATUS_DISPLAY, tooltip: `Placed by ${adminName}` };
}
var BIDDER_STAFF_BID_NOTE = "Placed by auction staff";

// src/email/normalizeEmail.ts
function normalizeEmail(email) {
  return email.trim().toLowerCase();
}
function emailsMatch(first, second) {
  return normalizeEmail(first) === normalizeEmail(second);
}

export { ADMIN_BID_STATUS_DISPLAY, ADMIN_REFUND_STATE_MAP, ADMIN_SALE_STATE_MAP, ADMIN_SETTLEMENT_STATUS_MAP, ALLOWED_IMAGE_CONTENT_TYPES, ALLOWED_LOGO_CONTENT_TYPES, AUCTION_ITEM_STATUS_LABELS, AddPaymentMethodInputSchema, AdminBidAttributionFrameSchema, AdminBidEntrySchema, AdminBidPlacementInputSchema, AdminBidsResponseSchema, AdminMeSchema, AdminRoleSchema, AdminUserListItemSchema, AdminUserListResponseSchema, AffectedEventSchema, ApiEnvelopeSchema, ApiError, ApiErrorBodySchema, ApiErrorCodeSchema, ApiFailureSchema, ApiSuccessSchema, ApplePayProvisioningErrorCodeSchema, ApplePayProvisioningSchema, ApplePayProvisioningStateSchema, ArchivePreviewSchema, AssumeOrgRequestSchema, AssumeOrgResponseSchema, AuctionEventArchiveFilterSchema, AuctionEventBrandingSchema, AuctionEventCreateInputSchema, AuctionEventListQuerySchema, AuctionEventMerchantLockOnDetailSchema, AuctionEventSchema, AuctionEventStatusSchema, AuctionEventUpdateInputSchema, AuctionItemSchema, AuctionItemStatusSchema, AuctionsRoleSchema, AutoCheckoutModeSchema, BIDDER_CLIENT_FLAG_KEYS, BIDDER_REFUND_STATE_MAP, BIDDER_SALE_STATE_MAP, BIDDER_SETTLEMENT_STATUS_MAP, BIDDER_STAFF_BID_NOTE, BidCountsResponseSchema, BidMaxExceededFrameSchema, BidMaxRaisedFrameSchema, BidOutbidFrameSchema, BidPlacedBySchema, BidPlacedFrameSchema, BidPlacementInputSchema, BidPlacementResultSchema, BidSchema, BidStackEntrySchema, BidTypeSchema, BidVoidedFrameSchema, BidderActivatedFrameSchema, BidderActivityItemSchema, BidderActivitySchema, BidderChargeInputSchema, BidderClientFlagsSchema, BidderEntryPathSchema, BidderInvoicePaymentContextSchema, BidderInvoiceTotalsSchema, BidderItemSummarySchema, BidderOutstandingOfferSchema, BidderPaymentLineSchema, BidderPaymentSchema, BidderPaymentStateSchema, BidderRegisteredFrameSchema, BidderReleasedLotSchema, BidderReleasedRefundSchema, BidderSchema, BidderSegmentSchema, BidderUpdateInputSchema, BrandingPresignedSlotSchema, BroadcastAudienceResponseSchema, BroadcastComposerConfigSchema, BroadcastSendInputSchema, BroadcastSendResultSchema, BroadcastStatusSchema, BroadcastSummarySchema, BroadcastTestSendInputSchema, BroadcastTestSendResultSchema, BulkActionResponseSchema, BulkItemIdsInputSchema, BulkItemResultSchema, BulkWithdrawInputSchema, BundleComponentInputSchema, BundleComponentSchema, BundleCreateInputSchema, BundleLifecycleResultSchema, BundleMemberSchema, BundleSchema, BundleUpdateInputSchema, CATALOG_KIND_RANK, CATALOG_ORDER_MAX_LOTS, CATALOG_POSITION_BUCKET, CLIENT_FLAG_KEYS, CLIENT_OP_TYPES, CRM_INSTANCE_STATUS_SCHEMA_VERSION, CRM_ONBOARDING_MAX_SELECTIONS, CRM_ONBOARDING_MAX_USERS, CRM_SYNC_DISPLAY_MAP, CampaignCreateInputSchema, CampaignCreateResultSchema, CancelSaleRequestSchema, CancelSaleResultSchema, CatalogLotPositionSchema, CatalogOrderSaveInputSchema, CatalogOrderSaveResponseSchema, CategoryCreateInputSchema, CategoryOriginSchema, CategorySchema, CategoryUpdateInputSchema, CentsSchema, ChargeEligibilitySchema, ChargeInputSchema, ChargeResultSchema, ClientOpSchema, ConnectionLostFrameSchema, CreateOrganizationRequestSchema, CreateSupervisorRequestSchema, CrmConstituentSyncedFrameSchema, CrmEmailSchema, CrmEntryUrlSchema, CrmEnvironment, CrmEnvironmentSchema, CrmFailedFrameSchema, CrmGatewaySlotSchema, CrmIdentifierSchema, CrmInstanceEventType, CrmInstanceStatusEventSchema, CrmInstanceStatusSchema, CrmInstanceStatusValue, CrmInstanceStatusValueSchema, CrmInvoiceSyncFailedFrameSchema, CrmOnboardingExchangeRequestSchema, CrmOnboardingFailureCode, CrmOnboardingFailureCodeSchema, CrmOnboardingGrantSchema, CrmOnboardingSessionSchema, CrmOnboardingSessionState, CrmOnboardingSessionStateSchema, CrmOnboardingSnapshotSchema, CrmOnboardingStatus, CrmOnboardingStatusSchema, CrmOnboardingSubmitRequestSchema, CrmOnboardingUserEligibility, CrmOnboardingUserEligibilitySchema, CrmOnboardingUserSchema, CrmOnboardingUsersResponseSchema, CrmPaymentGatewaySchema, CrmPaymentGatewaysResponseSchema, CrmProfileImportStatus, CrmProfileImportStatusSchema, CrmRefundSyncDeferredFrameSchema, CrmSyncDisplaySchema, CrmSyncRecordSchema, CrmSyncRecordTypeSchema, CrmSyncStatusSchema, CrmSyncedFrameSchema, CustodyEventKindSchema, CustodyEventSchema, DEFAULT_BID_INCREMENT_PERCENT, DEFAULT_METRICS_TIMEZONE, DEFAULT_STARTING_BID_PERCENT, DcfCardBrandSchema, DcfMethodSchema, DeactivateSsoInstanceResponseSchema, DerivedInvoiceDisplayState, DonorCreateInputSchema, DonorMergeInputSchema, DonorSchema, DonorUpdateInputSchema, EXACTLY_ONE_LOT_MESSAGE, EmailDeliveryEventTypeSchema, EmailDeliveryStatusSchema, EmailDeliveryWireSchema, ErrorFrameSchema, EvaluatedClientFlagsSchema, EventArchivedFrameSchema, EventClosedFrameSchema, EventClosingSoonFrameSchema, EventExtendedFrameSchema, EventMoneySchema, EventOpenedFrameSchema, EventPausedFrameSchema, EventResumedFrameSchema, FeatureFlagChangedFrameSchema, FeatureFlagOrgOverrideSchema, FeatureFlagOverrideInputSchema, FeatureFlagScheduleInputSchema, FeatureFlagScheduledTransitionSchema, FeatureFlagSchema, FeatureFlagUpdateSchema, FeeCoverageInputSchema, FeeCoveragePreviewSchema, FeeCoverageQuoteSchema, GrantOrgMemberRoleRequestSchema, IMPORT_MAX_ROWS, INVOICE_PAYMENT_STATUS_LABELS, InvoiceCheckoutModeSchema, InvoiceCreatedFrameSchema, InvoiceDeclineInputSchema, InvoiceDetailSchema, InvoiceDonationInputSchema, InvoiceFailedFrameSchema, InvoiceLastFailureSchema, InvoiceLineItemSchema, InvoiceLineItemSummarySchema, InvoiceLineItemTypeSchema, InvoicePaidFrameSchema, InvoicePaymentStatusSchema, InvoiceProcessingFrameSchema, InvoiceReassignedFrameSchema, InvoiceReceiptReadyFrameSchema, InvoiceRefundFailedFrameSchema, InvoiceRefundedFrameSchema, InvoiceReleasedFrameSchema, InvoiceSchema, InvoiceSummaryListResponseSchema, InvoiceSummarySchema, InvoiceUpdatedFrameSchema, InvoiceVoidedFrameSchema, Iso8601Schema, IsoDateSchema, ItemCreateInputSchema, ItemCustodyHistorySchema, ItemDispositionSchema, ItemHighBidChangedFrameSchema, ItemImportInputSchema, ItemImportRowSchema, ItemListQuerySchema, ItemListResponseSchema, ItemStatusChangedFrameSchema, ItemUpdateInputSchema, JobClassSchema, KNOWN_PROCESSORS, LEGACY_STARTING_BID_PERCENT, LOG_ONLY_NOTIFICATION_TYPES, LiveDashboardItemSchema, LiveDashboardItemsResponseSchema, LiveDashboardStatsSchema, LotKindSchema, LotRefSchema, MAX_BID_AMOUNT_CENTS, METRICS_CSV_ROW_LIMIT, METRICS_LOOKUP_LIMIT, METRICS_MAX_RANGE_MONTHS, METRICS_PAGE_SIZE, MagicLinkExchangeResultSchema, MagicLinkExchangeSchema, MagicLinkGrantSchema, MagicLinkRequestResultSchema, MagicLinkRequestSchema, MerchantDetailsResponseSchema, MerchantLockSlotSchema, MerchantPreviewChangeInputSchema, MerchantPreviewChangeResponseSchema, MethodAvailabilityDetailSchema, MethodAvailabilityDetailsSchema, MethodAvailabilitySchema, MethodBlockedReasonSchema, MethodCapabilitySchema, MethodTypeSchema, MyBidsResponseSchema, MyBidsStatusSchema, NeonPayMerchantDetailSchema, NotificationBroadcastSummarySchema, NotificationChannelSchema, NotificationChannelSettingSchema, NotificationChannelSettingUpdateSchema, NotificationFailedFrameSchema, NotificationLogEntrySchema, NotificationLogSchema, NotificationLogStatusSchema, NotificationLogTypeSchema, NotificationSentFrameSchema, NotificationSettingsUpdateInputSchema, NotificationSettingsWireSchema, NotificationTemplateComposerSchema, NotificationTemplateSchema, NotificationTypeSchema, OPAQUE_STORED_METHOD_TYPES, OfferAcceptedFrameSchema, OfferCancelledFrameSchema, OfferCreatedFrameSchema, OfferDeclinedFrameSchema, OfferDetailSchema, OfferExpiredFrameSchema, OfferStatusSchema, OfflinePaymentInputSchema, OnlineAccessSchema, OrgAccessEntrySchema, OrgAccessGrantReason, OrgAccessGrantReasonSchema, OrgAccessGrantStatus, OrgAccessGrantStatusSchema, OrgAdoptRequestSchema, OrgAdoptResponseSchema, OrgAssetSchema, OrgEmailConfigSchema, OrgEnterRequestSchema, OrgEnterResponseSchema, OrgMemberListResponseSchema, OrgMemberSchema, OrgPaymentConfigSchema, OrgSelectRequestSchema, OrgSelectResponseSchema, OrgSelectionEntrySchema, OrgSelectionRequiredResponseSchema, OrgSettingsSchema, OrgSettingsUpdateSchema, OrganizationSchema, OutstandingOfferSchema, PAYMENT_METHOD_DECLINED_CODE, PAYMENT_METHOD_DECLINED_MESSAGE, PAYMENT_METHOD_TOKEN_FIELD, PICKUP_INSTRUCTIONS_MAX_LENGTH, PUBLIC_CATALOG_LISTED_STATUSES, PageInfoSchema, PaymentConfigSchema, PaymentConfigUpdateSchema, PaymentFailedFrameSchema, PaymentMethodRequirementSchema, PaymentMethodSchema, PaymentMethodWireSchema, PaymentProcessingFrameSchema, PaymentTransactionSchema, PaymentTransactionStatusSchema, PaymentTransactionTypeSchema, PhotoVariantSchema, PlacementCollectSchema, PlacementCollectionOutcomeSchema, PlacementCollectionSchema, PlacementOpenInvoiceSchema, PlacementPriceBasisSchema, PlacementRequestSchema, PlatformAvailabilitySchema, PongFrameSchema, PongOpSchema, PresenceChangedFrameSchema, PresignedSlotSchema, ProcessorCapabilitiesSchema, PublicBidEntrySchema, PublicEventSchema, PublicItemDetailSchema, PublicItemSummaryPageSchema, PublicItemSummarySchema, RECEIPT_CLEARING_NOTICE, ReapplyDefaultsInputSchema, ReapplyDefaultsResponseSchema, ReapplyDefaultsSkipSchema, ReassignItemRequestSchema, ReassignItemResultSchema, ReassignLotRequestSchema, ReassignModeSchema, ReassignTargetConsequenceSchema, ReassignTargetSchema, ReassignTargetsResponseSchema, ReceiptRegenStatusSchema, ReceiptRegenerationSchema, RedemptionSchema, RefundAndReleaseInputSchema, RefundAndReleaseResultSchema, RefundFailureSchema, RefundInputSchema, RefundObligationStatusSchema, RefundStateSchema, RegenerateReceiptResponseSchema, RegistrationInputSchema, RegistrationResultSchema, ReleasedLineSchema, ResolveStuckChargeRequestSchema, ResolveStuckRefundRequestSchema, ResumeAuctionRequestSchema, ResumeEndTimeSchema, RetryHoldReleaseResultSchema, SALE_STATE_REASON_MAP, STATUS_QUALIFIER_MAP, STORED_METHOD_TYPE_LABELS, SUPERVISOR_CALENDAR_MAX_WINDOW_DAYS, SaleStateReasonSchema, SaleStateSchema, SalesforceIdSchema, ScheduledJobSchema, ScheduledJobStatusSchema, SegmentCountSchema, SessionRefreshResponseSchema, SetDefaultPaymentMethodInputSchema, SettlementBidderSummarySchema, SettlementBundleSchema, SettlementInFlightChargeSchema, SettlementInvoiceSchema, SettlementLineItemSchema, SettlementPayloadSchema, SettlementStatusSchema, SlotMerchantSchema, SponsorInfoSchema, SsoInstanceSchema, SsoOrgDetailSchema, SsoOrgUserSchema, SsoUserLookupResponseSchema, StatusQualifier, StoredCardSchema, StoredMethodTypeSchema, StrandedHoldReleaseStatusSchema, StrandedLotReasonSchema, StrandedLotSchema, StrandedVerificationHoldSchema, StuckChargeReasonSchema, StuckChargeSchema, StuckRefundSchema, SubscribeOpSchema, SupervisorAccessSchema, SupervisorCalendarAuctionSchema, SupervisorCalendarQuerySchema, SupervisorCalendarResponseSchema, SupervisorListItemSchema, SupervisorMeSchema, SupervisorMetricsAttentionCountsSchema, SupervisorMetricsAttentionItemSchema, SupervisorMetricsBucketSchema, SupervisorMetricsCategorySchema, SupervisorMetricsChannelSchema, SupervisorMetricsClosedQuerySchema, SupervisorMetricsClosedResponseSchema, SupervisorMetricsDurationBucketSchema, SupervisorMetricsDurationFiguresSchema, SupervisorMetricsDurationQuerySchema, SupervisorMetricsDurationResponseSchema, SupervisorMetricsDurationRowSchema, SupervisorMetricsEventFiguresSchema, SupervisorMetricsEventIdentitySchema, SupervisorMetricsEventLookupQuerySchema, SupervisorMetricsEventLookupResponseSchema, SupervisorMetricsEventLookupRowSchema, SupervisorMetricsEventRowSchema, SupervisorMetricsEventSortSchema, SupervisorMetricsEventTotalsSchema, SupervisorMetricsEventsQuerySchema, SupervisorMetricsEventsResponseSchema, SupervisorMetricsGroupBySchema, SupervisorMetricsInclusionSchema, SupervisorMetricsInvoiceStatesSchema, SupervisorMetricsMerchantReasonSchema, SupervisorMetricsMoneyEnvironmentSchema, SupervisorMetricsMoneySchema, SupervisorMetricsNeedsAttentionQuerySchema, SupervisorMetricsNeedsAttentionResponseSchema, SupervisorMetricsOrgFiguresSchema, SupervisorMetricsOrgLookupQuerySchema, SupervisorMetricsOrgLookupResponseSchema, SupervisorMetricsOrgLookupRowSchema, SupervisorMetricsOrgMoneySchema, SupervisorMetricsOrgRowSchema, SupervisorMetricsOrgSortSchema, SupervisorMetricsOrgsQuerySchema, SupervisorMetricsOrgsResponseSchema, SupervisorMetricsOverrideParamsSchema, SupervisorMetricsOverrideRequestSchema, SupervisorMetricsOverrideResponseSchema, SupervisorMetricsOverrideSchema, SupervisorMetricsOverviewQuerySchema, SupervisorMetricsOverviewResponseSchema, SupervisorMetricsPageFilterSchema, SupervisorMetricsPaymentFiguresSchema, SupervisorMetricsPaymentFilterSchema, SupervisorMetricsPaymentGroupSchema, SupervisorMetricsPaymentRowSchema, SupervisorMetricsPaymentsQuerySchema, SupervisorMetricsPaymentsResponseSchema, SupervisorMetricsReasonSchema, SupervisorMetricsSelectionSchema, SupervisorMetricsSettingKeySchema, SupervisorMetricsSettingValueSchema, SupervisorMetricsSettingsQuerySchema, SupervisorMetricsSettingsResponseSchema, SupervisorMetricsStoredMethodSchema, SupervisorMetricsTabSchema, SupervisorMetricsTimezoneSchema, SupervisorMetricsUpcomingStatusSchema, SupervisorOrgDetailSchema, SupervisorOrganizationSchema, SupervisorPermissionSchema, TemplateEditTierSchema, TerminalCancelResultSchema, TerminalChargeInputSchema, TerminalListResponseSchema, TerminalSchema, TerminalStatusResultSchema, TerminalStatusSchema, UNKNOWN_PROCESSOR_CAPABILITIES, UNPOSITIONED_CATALOG_POSITION, UnarchiveInputSchema, UnbidLotSchema, UninvoicedLotSchema, UnsubscribeOpSchema, UpdateOrganizationRequestSchema, UpdateSupervisorAccessRequestSchema, UuidSchema, VoidBidRequestSchema, VoidBidResponseSchema, WS_FRAME_TYPES, WaffleAppSchema, WaffleAppsResponseSchema, WalkupRegistrationInputSchema, WalkupRegistrationResultSchema, WishlistIdsResponseSchema, WishlistToggleResponseSchema, WsFrameSchema, adminBidStatusDisplay, canonicalProcessor, catalogOrderKeyOf, catalogOrderRevisionOf, channels, compareCatalogLots, compareCatalogOrderKeys, createApiClient, createWsClient, dcfMethodForPaymentMethod, emailsMatch, failureEnvelope, invoicePaymentStatusLabel, isKnownProcessor, isListedInPublicCatalog, isOpaqueStoredMethodType, isSafeRelativePath, itemStatusLabel, lotKeyOf, lotRefOfLine, lotRefOfSummary, namesExactlyOneLot, normalizeEmail, normalizeSupervisorMetricsQuery, paymentMethodAvailabilityShape, paymentMethodDeclinedFieldErrors, percentOfValueCents, refundContactLabel, refundOnItsWayLabel, sameFeeContext, storedMethodTypeLabel, successEnvelope, supervisorHas, toWireCardBrand, withIdempotency };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map